/**
 * Translations for Kashf.ai Platform
 * Supported languages: English (en), French (fr), Arabic (ar)
 */

export const translations = {
  en: {
    // Navigation
    nav: {
      dashboard: 'Dashboard',
      contracts: 'Contracts',
      invoices: 'Invoices',
      vendors: 'Vendors',
      renewals: 'Renewals',
      analysis: 'Analysis',
      discrepancies: 'Discrepancies',
      integrations: 'Integrations',
      optimization: 'Optimization',
      audit: 'Audit Trail',
      settings: 'Settings',
      admin: 'Admin',
      platformAdmin: 'Platform Admin',
      adminDashboard: 'Dashboard',
      organizations: 'Organizations',
      allUsers: 'All Users',
      platformAnalytics: 'Analytics',
      plansProviders: 'Plans & AI',
      billingUsage: 'Billing & Usage'
    },

    // Common
    common: {
      search: 'Search contracts, invoices...',
      uploadDocument: 'Upload Document',
      viewAll: 'View All',
      cancel: 'Cancel',
      save: 'Save',
      saveChanges: 'Save Changes',
      close: 'Close',
      review: 'Review',
      takeAction: 'Take Action',
      configure: 'Configure',
      connect: 'Connect',
      all: 'All',
      days: 'days',
      month: 'month',
      year: 'yr',
      home: 'Home',
      pending: 'Pending',
      invoice: 'Invoice',
      contract: 'Contract',
      status: 'Status',
      type: 'Type',
      priority: 'Priority',
      retry: 'Retry',
      noData: 'No data available',
      regenerate: 'Regenerate',
      viewDetails: 'View Details',
      identified: 'Identified',
      view: 'View',
      more: 'More',
      any: 'Any',
      clear: 'Clear',
      apply: 'Apply',
      cardView: 'Card View',
      tableView: 'Table View',
      showing: 'Showing',
      of: 'of',
      items: 'items',
      actions: 'Actions',
      loading: 'Loading...',
      download: 'Download',
      edit: 'Edit',
      save: 'Save Changes',
      cancel: 'Cancel',
      editing: 'Editing',
      backToList: 'Back to List',
      total: 'total',
      issues: 'issues',
      noIssues: 'No issues',
      delete: 'Delete',
      export: 'Export',
      found: 'found',
      remove: 'Remove',
      saving: 'Saving...',
      creating: 'Creating...',
      unsavedChanges: 'You have unsaved changes. Are you sure you want to leave?',
      selected: 'selected',
      submit: 'Submit',
      system: 'System',
      unlink: 'Unlink',
      back: 'Back',
      activity: 'Activity',
      error: 'Error',
      invoices: 'Invoices',
      viewAll: 'View all',
      aiGenerated: 'AI Generated',
      create: 'Create',
      selectOption: 'Select...',
    },

    // Upload
    upload: {
      title: 'Upload Document',
      subtitle: 'Upload and process your contracts and invoices',
      tabInvoice: 'Invoice',
      tabContract: 'Contract',
      dragDrop: 'Drag & drop files here or',
      browse: 'Browse',
      hint: 'Supports PDF, PNG, JPG, CSV, XLSX (max 25MB)',
      process: 'Process Documents',
      supportedFormats: 'PDF, PNG, JPG, CSV, XLSX (max 25MB)',
      processing: 'Processing...',
      processDocuments: 'Process Documents',
      recentUploads: 'Recent Uploads',
      noRecentUploads: 'No recent uploads',
      finishReview: 'Finish Review',
      dropHere: 'Drop files here',
      or: 'or',
      acceptedFormats: 'Accepted formats',
      maxSize: 'Max',
      clearAll: 'Clear All',
      uploadAll: 'Upload All',
      complete: 'Complete',
      failed: 'Failed',
      description: 'Drag and drop files here or click to browse',
      errorInvalidType: 'Invalid file type',
      errorFileTooLarge: 'File too large',
      modal: {
        processBtn: 'Process Documents',
      },
      status: {
        uploading: 'Uploading...',
        extracting: 'Extracting data...',
        complete: 'Complete',
        error: 'Error',
      },
      tips: {
        title: 'Tips for Best Results',
        formats: 'Use PDF format for best text extraction',
        quality: 'Ensure documents are clear and readable',
        batch: 'You can upload multiple files at once',
        review: 'Review extracted data before saving',
      },
      success: {
        title: 'Upload Complete!',
        message: 'Your documents have been processed successfully.',
        uploadMore: 'Upload More',
        viewDocuments: 'View Documents',
      },
    },

    // Limit reached messages
    limits: {
      limitReached: 'Limit Reached',
      documents: 'documents',
      storage: 'storage',
      documentLimitMessage: 'You have reached your monthly document upload limit. Upgrade your plan or wait until next month to upload more documents.',
      storageLimitMessage: 'You have reached your storage limit. Upgrade your plan or delete some files to free up space.',
      currentUsage: 'Current Usage',
      options: 'Your Options',
      viewUsage: 'View Usage Details',
      viewUsageDesc: 'See your current usage and limits',
      upgradePlan: 'Upgrade Your Plan',
      upgradePlanDesc: 'Get more documents and storage',
      contactSupport: 'Contact Support',
      contactSupportDesc: 'Get help with your account',
    },

    // Toast notifications
    toast: {
      success: 'Success',
      error: 'Error',
      warning: 'Warning',
      info: 'Info',
    },

    // Search
    search: {
      noResults: 'No results found',
      contracts: 'Contracts',
      invoices: 'Invoices',
      vendors: 'Vendors',
      discrepancies: 'Discrepancies'
    },

    // Notifications (dropdown)
    notifications: {
      title: 'Notifications',
      markAllRead: 'Mark all read',
      noNotifications: 'No notifications',
      viewSettings: 'Notification Settings',
      fetchError: 'Failed to load notifications',
      types: {
        renewal_reminder: 'Renewal Reminder',
        discrepancy_alert: 'Discrepancy Alert',
        invoice_processed: 'Invoice Processed',
        optimization_opportunity: 'Savings Opportunity',
        system_alert: 'System Alert'
      }
    },

    // Dashboard
    dashboard: {
      title: 'Dashboard',
      activeContracts: 'Active Contracts',
      invoicesProcessed: 'Invoices Processed',
      openDiscrepancies: 'Open Discrepancies',
      savingsIdentified: 'Savings Identified',
      upcomingRenewals: 'Upcoming Renewals',
      recentDiscrepancies: 'Recent Discrepancies',
      recentActivity: 'Recent Activity',
      optimizationOpportunities: 'Optimization Opportunities',
      accuracyRate: 'Accuracy Rate',
      analysisSummary: 'Analysis Summary',
      contractsMatched: 'Contracts Matched',
      discrepanciesFound: 'Discrepancies Found',
      discrepancyTypes: 'Discrepancy Types',
      financialImpact: 'Financial Impact',
      getStarted: 'Get Started',
      newAnalysis: 'New Analysis',
      noRecentActivity: 'No recent activity',
      processingTime: 'Processing Time',
      recommendations: 'Recommendations',
      resolutionStatus: 'Resolution Status',
      severityDistribution: 'Severity Distribution',
      topVendors: 'Top Vendors',
      totalAnalyzed: 'Total Analyzed',
      welcomeMessage: 'Welcome to Kashf.ai',
      welcomeTitle: 'Welcome',
      subtitle: 'Your procurement intelligence dashboard',
    },

    // Contracts
    contracts: {
      title: 'Contracts',
      addContract: 'Add Contract',
      contractId: 'Contract ID',
      vendor: 'Vendor',
      contractTitle: 'Title',
      title: 'Title',
      type: 'Type',
      value: 'Value',
      endDate: 'End Date',
      status: 'Status',
      startDate: 'Start Date',
      autoRenewal: 'Auto-Renewal',
      optOutPeriod: 'Opt-Out Period',
      contractTerms: 'Contract Terms',
      pricingSchedule: 'Pricing Schedule',
      contractInfo: 'Contract Information',
      datesRenewal: 'Dates & Renewal',
      item: 'Item',
      unitPrice: 'Unit Price',
      quantity: 'Qty',
      yes: 'Yes',
      no: 'No',
      active: 'Active',
      expired: 'Expired',
      // Types
      softwareLicense: 'Software License',
      serviceAgreement: 'Service Agreement',
      infrastructure: 'Infrastructure',
      allTypes: 'All Types',
      // Contract modal
      untitled: 'Untitled Contract',
      noContracts: 'No contracts found',
      number: 'Contract #',
      expirationDate: 'Expires'
    },
    
    // Invoices
    invoices: {
      title: 'Invoices',
      uploadInvoice: 'Upload Invoice',
      uploadNew: 'Upload Invoice',
      invoiceId: 'Invoice ID',
      number: 'Invoice #',
      contract: 'Contract',
      date: 'Date',
      dueDate: 'Due Date',
      dateRange: 'Date Range',
      amount: 'Amount',
      total: 'Total',
      issues: 'Issues',
      issue: 'issue',
      status: 'Status',
      vendor: 'Vendor',
      subtotal: 'Subtotal',
      tax: 'Tax',
      discrepanciesFound: 'Discrepancies Found',
      noIssues: 'None',
      none: 'None',
      issuesPlural: 'issues',
      discrepancies: 'Discrepancies',
      noInvoices: 'No invoices found',
      noInvoicesDescription: 'Upload an invoice to get started',
      noDocument: 'No document file available for this invoice',
      overdue: 'Overdue',
      analyze: 'Analyze',
      analyzeSelected: 'Analyze Selected',
      // Filters
      pending: 'Pending',
      needsReview: 'Needs Review',
      approved: 'Approved',
      allVendors: 'All Vendors',
      withIssues: 'With Issues',
      noIssuesFilter: 'No Issues',
      // Contract linking
      linkContract: 'Link Contract',
      linkAnother: 'Link Another',
      linkedContracts: 'Linked Contracts',
      noLinkedContracts: 'No contracts linked to this invoice',
      contractLinked: 'Contract linked successfully',
      contractUnlinked: 'Contract unlinked successfully',
      linkError: 'Failed to link contract',
      unlinkError: 'Failed to unlink contract',
      confirmUnlink: 'Are you sure you want to unlink this contract?',
      confirmDelete: 'Are you sure you want to delete this invoice?',
      deleteSuccess: 'Invoice deleted successfully',
      deleteError: 'Failed to delete invoice'
    },

    // Renewals
    renewals: {
      title: 'Renewals',
      // Status
      urgent: 'Urgent',
      soon: 'Soon',
      upcoming: 'Upcoming',
      completed: 'Completed',
      // Time ranges
      within14Days: 'Within 14 days',
      days15to30: '15-30 days',
      days31to90: '31-90 days',
      overdue: 'Overdue',
      // Types
      autoRenews: 'Auto-renews',
      manual: 'Manual',
      // Stats
      totalRenewals: 'Total Renewals',
      urgentRenewals: 'Urgent',
      dueThisMonth: 'Due This Month',
      totalValue: 'Total Value',
      // Filters
      filterByStatus: 'Filter by Status',
      filterByType: 'Filter by Type',
      filterByPriority: 'Filter by Priority',
      allStatuses: 'All Statuses',
      allTypes: 'All Types',
      allPriorities: 'All Priorities',
      // Priority
      priority: 'Priority',
      high: 'High',
      medium: 'Medium',
      low: 'Low',
      critical: 'Critical',
      // Actions
      viewDetails: 'View Details',
      addNote: 'Add Note',
      setReminder: 'Set Reminder',
      markAsReviewed: 'Mark as Reviewed',
      initiateRenewal: 'Initiate Renewal',
      scheduleReview: 'Schedule Review',
      renewContract: 'Renew Contract',
      cancelRenewal: 'Cancel Renewal',
      // Timeline
      timeline: 'Timeline',
      renewalDate: 'Renewal Date',
      optOutDeadline: 'Opt-Out Deadline',
      reviewDue: 'Review Due',
      reminderSent: 'Reminder Sent',
      contractStart: 'Contract Start',
      contractEnd: 'Contract End',
      // Details
      daysUntilRenewal: 'Days Until Renewal',
      contractValue: 'Contract Value',
      renewalTerms: 'Renewal Terms',
      autoRenewalEnabled: 'Auto-Renewal Enabled',
      autoRenewalDisabled: 'Auto-Renewal Disabled',
      noticePeriod: 'Notice Period',
      // Notes
      notes: 'Notes',
      addNoteTitle: 'Add a Note',
      notePlaceholder: 'Enter your note...',
      noNotes: 'No notes yet',
      // Messages
      noRenewals: 'No renewals found',
      noUpcoming: 'No upcoming renewals',
      renewalSaved: 'Renewal updated successfully',
      noteSaved: 'Note added successfully',
      reminderSet: 'Reminder set successfully',
      // Notifications
      reminderEmailSent: 'Renewal reminder email sent',
      upcomingRenewalAlert: 'Contract renewal approaching'
    },

    // Renewal (singular - for renewal page module)
    renewal: {
      pageTitle: 'Contract Renewals',
      pageSubtitle: 'Track and manage upcoming contract renewals',
      days: 'days',
      // Stats
      stats: {
        critical: 'Critical',
        urgent: 'Urgent',
        attention: 'Attention Needed',
        valueAtRisk: 'Value at Risk',
        needsDecision: 'Needs decision in 30 days'
      },
      // Filters
      filter: {
        status: 'Status',
        priority: 'Priority',
        action: 'Action',
        timeframe: 'Timeframe',
        allStatuses: 'All Statuses',
        allPriorities: 'All Priorities',
        allActions: 'All Actions'
      },
      // Status
      status: {
        upcoming: 'Upcoming',
        in_review: 'In Review',
        action_taken: 'Action Taken',
        auto_renewed: 'Auto Renewed',
        terminated: 'Terminated',
        expired: 'Expired'
      },
      // Priority
      priority: {
        critical: 'Critical',
        high: 'High',
        medium: 'Medium',
        low: 'Low'
      },
      // Action
      action: {
        undecided: 'Undecided',
        renew: 'Renew',
        renegotiate: 'Renegotiate',
        terminate: 'Terminate',
        renewDesc: 'Continue the contract with current terms',
        renegotiateDesc: 'Request new terms or pricing',
        terminateDesc: 'Do not renew this contract'
      },
      selectAction: 'What would you like to do with this contract?',
      updateError: 'Failed to update renewal. Please try again.',
      updateSuccess: 'Renewal updated successfully',
      // Urgency
      urgency: {
        overdue: 'Overdue',
        critical: 'Critical',
        urgent: 'Urgent',
        attention: 'Attention',
        routine: 'Routine'
      },
      // View
      view: {
        calendar: 'Calendar View',
        list: 'List View'
      },
      // Card fields
      renewalDate: 'Renewal Date',
      daysLeft: 'Days Left',
      contractValue: 'Contract Value',
      plannedAction: 'Planned Action',
      optOutDeadline: 'Opt-out Deadline',
      assignedTo: 'Assigned to',
      notes: 'notes',
      viewDetails: 'View Details',
      takeAction: 'Take Action',
      // Empty state
      empty: {
        title: 'No Renewals Found',
        description: 'Upload contracts with expiration dates to see renewals here.'
      },
      // Sync
      syncFromContracts: 'Sync from Contracts',
      // Timeline
      timeline: {
        title: 'Timeline',
        today: 'Today',
        optOutDeadline: 'Opt-Out Deadline',
        optOutDescription: 'Last day to opt out of automatic renewal',
        renewalDate: 'Renewal Date',
        notificationSent: 'Notification Sent',
        beforeDeadline: 'before deadline',
        note: 'Note',
        ago: 'ago',
        in: 'in'
      }
    },

    // Analysis
    analysis: {
      title: 'Analysis',
      // Dashboard
      dashboardTitle: 'Procurement Analytics',
      dashboardSubtitle: 'Real-time insights into your procurement performance',
      totalSpendAnalyzed: 'Total Spend Analyzed',
      discrepanciesDetected: 'Discrepancies Detected',
      potentialOvercharges: 'Potential Overcharges',
      savingsRecovered: 'Savings Recovered',
      vsLastMonth: 'vs last month',
      ofSpend: 'of spend',
      recoveryRate: 'recovery rate',
      spendByCategory: 'Spend by Category',
      discrepancyInsights: 'Discrepancy Insights',
      total: 'total',
      topIssueTypes: 'Top Issue Types',
      vendorPerformance: 'Vendor Performance',
      noVendorData: 'No vendor data available yet',
      accuracy: 'accuracy',
      resolutionProgress: 'Resolution Progress',
      resolved: 'Resolved',
      reviewPending: 'Review',
      pendingItems: 'pending items',
      recentActivity: 'Recent Activity',
      noRecentActivity: 'No recent activity',
      poweredByAI: 'Powered by AI',
      runAnalysis: 'Run Analysis',
      category: {
        software: 'Software & Licenses',
        services: 'Professional Services',
        infrastructure: 'Infrastructure',
        supplies: 'Office Supplies',
        other: 'Other',
      },
      type: {
        pricing: 'Price Overcharge',
        quantity: 'Quantity Mismatch',
        terms: 'Contract Violation',
        unauthorized: 'Unauthorized Charge',
      },
      status: {
        pending: 'Pending Review',
        underReview: 'Under Review',
        resolved: 'Resolved',
        dismissed: 'Dismissed',
      },
      insight: {
        criticalAlert: 'Critical Issues Require Attention',
        criticalDesc: 'critical discrepancies need immediate review to prevent financial loss.',
        savingsOpportunity: 'Savings Opportunity Identified',
        savingsDesc: 'in potential overcharges detected. Take action to recover these funds.',
        improveRecovery: 'Improve Recovery Rate',
        recoveryDesc: 'Your current recovery rate is below average. Consider prioritizing high-impact discrepancies.',
        goodStanding: 'Looking Good!',
        goodDesc: 'No critical issues detected. Keep monitoring your procurement spend for optimal results.',
        reviewNow: 'Review Now',
        viewDetails: 'View Details',
        viewStrategy: 'View Strategy',
      },
      discrepancies: 'Discrepancies',
      optimizations: 'Optimizations',
      discrepancySummary: 'Discrepancy Summary',
      openIssues: 'Open Issues',
      totalImpact: 'Total Impact',
      optimizationPotential: 'Optimization Potential',
      viewOptimizations: 'View cost-saving opportunities',
      opportunities: 'Opportunities',
      potentialSavings: 'Potential Savings',
      review: 'Review',
      takeAction: 'Take Action',
      vendors: 'Vendors',
      // Types
      pricing: 'Pricing',
      quantity: 'Quantity',
      unauthorized: 'Unauthorized',
      consolidation: 'Consolidation',
      volume: 'Volume',
      renegotiation: 'Renegotiation',
      // Procurement Analysis - Tabs & Overview
      overview: 'Overview',
      aiInsights: 'AI Insights',
      topDiscrepancies: 'Top Discrepancies',
      topOptimizations: 'Top Optimizations',
      viewAll: 'View All',
      // KPIs
      variance: 'Variance',
      discrepancyCount: 'Discrepancies Found',
      financialImpact: 'Financial Impact',
      savingsOpportunity: 'Savings Opportunity',
      critical: 'Critical',
      criticalIssues: 'critical issues',
      potentialOvercharge: 'potential overcharge',
      potentialSavings: 'potential savings',
      // AI Insights Panel
      aiSummary: 'AI Summary',
      keyFindings: 'Key Findings',
      priorityActions: 'Priority Actions',
      negotiationTips: 'Negotiation Tips',
      marketInsights: 'Market Insights',
      // Discrepancy Panel
      noDiscrepancies: 'No Discrepancies Found',
      noDiscrepanciesMessage: 'Great news! No discrepancies were detected between this invoice and the contract.',
      lineItem: 'Line Item',
      expected: 'Expected',
      actual: 'Actual',
      variance: 'Variance',
      accept: 'Accept',
      dispute: 'Dispute',
      reject: 'Reject',
      acceptSuccess: 'Discrepancy accepted successfully',
      disputeSuccess: 'Discrepancy dispute initiated',
      rejectSuccess: 'Discrepancy rejected',
      // Optimization Panel
      optimizationOpportunities: 'Optimization Opportunities',
      noOptimizations: 'No Optimizations Found',
      noOptimizationsMessage: 'No optimization opportunities identified for this invoice.',
      totalPotentialSavings: 'Total Potential Savings',
      affectedItems: 'Affected Items',
      lineItems: 'line items',
      perInvoice: 'Per Invoice',
      monthly: 'Monthly',
      annual: 'Annual',
      oneTime: 'One-Time',
      confidenceLabel: 'confidence',
      // Optimization Types
      optimization: {
        volumeDiscount: 'Volume Discount',
        renegotiation: 'Renegotiation',
        consolidation: 'Consolidation',
        timing: 'Payment Timing',
        alternative: 'Alternative Option'
      },
      // Confidence Levels
      confidence: {
        high: 'High Confidence',
        medium: 'Medium Confidence',
        low: 'Low Confidence'
      },
      // Contract Selector Modal
      selectContract: 'Select Contract',
      linkingInvoice: 'Linking Invoice',
      bestMatch: 'Best Match',
      matchScore: 'Match Score',
      vendorMatch: 'Vendor Match',
      skuOverlap: 'SKU Overlap',
      dateValidity: 'Date Validity',
      valueAlignment: 'Value Alignment',
      noSuggestions: 'No Contract Suggestions',
      noSuggestionsMessage: 'No matching contracts found for this invoice.',
      analyzeWithoutContract: 'Analyze Without Contract',
      linkAndAnalyze: 'Link & Analyze',
      // Header
      invoiceAnalysis: 'Invoice Analysis',
      reanalyze: 'Re-analyze',
      export: 'Export',
      backToInvoice: 'Back to Invoice',
      linkedContract: 'Linked Contract',
      noLinkedContract: 'No Linked Contract',
      selectContractToLink: 'Select a contract to link',
      // Status
      status: {
        clean: 'Clean',
        has_discrepancies: 'Has Discrepancies',
        needs_review: 'Needs Review',
        no_contract: 'No Contract'
      },
      // Contract Analysis
      analyze: 'Analyze',
      analyzeAll: 'Analyze All',
      invoicesAnalyzed: 'Invoices Analyzed',
      totalInvoiceValue: 'Total Invoice Value',
      totalDiscrepancies: 'Total Discrepancies',
      totalVariance: 'Total Variance',
      vendorPerformance: 'Vendor Performance',
      totalInvoices: 'Total Invoices',
      invoicesWithIssues: 'Invoices with Issues',
      errorRate: 'Error Rate',
      totalOvercharges: 'Total Overcharges',
      commonIssues: 'Common Issues',
      keyRecommendations: 'Key Recommendations',
      discrepancyBreakdown: 'Discrepancy Breakdown',
      bySeverity: 'By Severity',
      byType: 'By Type',
      invoiceAnalyses: 'Invoice Analyses',
      invoices: 'Invoices',
      noInvoices: 'No Invoices',
      noInvoicesLinked: 'No invoices are linked to this contract.'
    },

    // Severity levels
    severity: {
      critical: 'Critical',
      high: 'High',
      medium: 'Medium',
      low: 'Low'
    },

    // Discrepancy types
    discrepancy_type: {
      price_mismatch: 'Price Mismatch',
      quantity_mismatch: 'Quantity Mismatch',
      unauthorized_item: 'Unauthorized Item',
      missing_discount: 'Missing Discount',
      duplicate_charge: 'Duplicate Charge',
      tax_error: 'Tax Error',
      other: 'Other'
    },

    // Upload Modal
    upload: {
      title: 'Upload Document',
      subtitle: 'Upload and process your invoices and contracts',
      tabInvoice: 'Invoice',
      tabContract: 'Contract',
      dragDrop: 'Drag & drop files here or',
      browse: 'browse',
      hint: 'Supports PDF, PNG, JPG, CSV, XLSX (max 25MB)',
      supportedFormats: 'Supports PDF, PNG, JPG, CSV, XLSX (max 25MB)',
      process: 'Process Documents',
      processDocuments: 'Process Documents',
      processing: 'Processing...',
      cancel: 'Cancel',
      noRecentUploads: 'No recent uploads',
      recentUploads: 'Recent Uploads',
      clearAll: 'Clear All',
      uploadAll: 'Upload All',
      dropHere: 'Drop files here',
      or: 'or',
      acceptedFormats: 'Accepted formats',
      maxSize: 'Max',
      description: 'Drag and drop files here or click to browse',
      complete: 'Complete',
      failed: 'Failed',
      errorInvalidType: 'Invalid file type',
      errorFileTooLarge: 'File too large',
      finishReview: 'Finish Review',
      modal: {
        processBtn: 'Process Documents'
      },
      status: {
        uploading: 'Uploading...',
        extracting: 'Extracting data...',
        complete: 'Complete',
        error: 'Error'
      },
      tips: {
        title: 'Tips',
        formats: 'Supports PDF, PNG, JPG, CSV, and XLSX formats',
        quality: 'Ensure documents are clear and readable',
        batch: 'You can upload multiple files at once',
        review: 'Review extracted data before saving'
      },
      success: {
        title: 'Upload Successful',
        message: 'Your documents have been processed successfully',
        uploadMore: 'Upload More',
        viewDocuments: 'View Documents'
      }
    },

    // Integrations
    integrations: {
      title: 'Integrations',
      connected: 'Connected',
      notConnected: 'Not Connected',
      disconnected: 'Disconnected',
      lastSync: 'Last sync',
      neverSynced: 'Never synced',
      documentsSynced: 'documents synced',
      connectToSync: 'Connect to sync documents automatically',
      configure: 'Configure',
      connect: 'Connect',
      disconnect: 'Disconnect',
      enable: 'Enable',
      disable: 'Disable',
      enabled: 'Enabled',
      disabled: 'Disabled',
      testing: 'Testing...',
      creating: 'Creating...',
      import: 'Import',
      // Types
      documentStorage: 'Document Storage',
      procurement: 'Procurement',
      eSignature: 'E-Signature',
      // Status
      status: {
        idle: 'Idle',
        running: 'Running',
        completed: 'Completed',
        failed: 'Failed',
        error: 'Error'
      },
      // Services
      services: {
        dropbox: 'Dropbox',
        box: 'Box',
        googleDrive: 'Google Drive',
        oneDrive: 'OneDrive',
        sharePoint: 'SharePoint',
        erpOracle: 'Oracle ERP',
        erpSap: 'SAP',
        docusign: 'DocuSign',
        adobeSign: 'Adobe Sign'
      }
    },
    
    // Settings
    settings: {
      title: 'Settings',
      subtitle: 'Manage your account and preferences',
      profile: 'Profile',
      preferences: 'Preferences',
      team: 'Team',
      security: 'Security',
      auditTrail: 'Audit Trail',
      saveSuccess: 'Settings saved successfully',
      saveError: 'Failed to save settings',
      nameHint: 'Name is managed by your identity provider',
      customizeExperience: 'Customize your experience',
      // Profile section
      profileInformation: 'Profile Information',
      profileInfo: 'Profile Information',
      profileDesc: 'Update your account details and preferences.',
      updateAccountDetails: 'Your account details',
      fullName: 'Full Name',
      email: 'Email',
      role: 'Role',
      timezone: 'Timezone',
      dateFormat: 'Date Format',
      defaultView: 'Default View',
      // Nested profile
      'profile.title': 'Profile',
      'profile.subtitle': 'Manage your account information and preferences',
      'profile.accountInfo': 'Account Information',
      'profile.accountInfoDesc': 'Your basic account details',
      'profile.displayName': 'Display Name',
      'profile.email': 'Email',
      'profile.role': 'Role',
      'profile.organization': 'Organization',
      'profile.memberSince': 'Member since',
      'profile.lastLogin': 'Last login',
      'profile.preferences': 'Preferences',
      'profile.preferencesDesc': 'Customize your experience',
      'profile.language': 'Language',
      'profile.timezone': 'Timezone',
      'profile.dateFormat': 'Date Format',
      'profile.currency': 'Currency',
      'profile.defaultView': 'Default View',
      'profile.fetchError': 'Failed to load profile',
      'profile.saveSuccess': 'Profile saved successfully',
      'profile.saveError': 'Failed to save profile',
      // Notification preferences
      notificationPreferences: 'Notification Preferences',
      notificationPrefs: 'Notification Preferences',
      notificationDesc: 'Choose how you want to be notified about important events.',
      chooseNotifications: 'Choose how you want to be notified',
      emailNotifications: 'Email Notifications',
      emailNotificationsDesc: 'Receive notifications via email',
      inAppNotifications: 'In-App Notifications',
      inAppNotificationsDesc: 'Show notifications in the app',
      renewalReminders: 'Renewal Reminders',
      renewalRemindersDesc: 'Get notified about upcoming contract renewals',
      discrepancyAlerts: 'Discrepancy Alerts',
      discrepancyAlertsDesc: 'Get notified about invoice discrepancies',
      weeklyDigest: 'Weekly Digest',
      weeklyDigestDesc: 'Receive a weekly summary of activity',
      // Nested notifications
      notifications: {
        title: 'Notifications',
        subtitle: 'Configure how you want to be notified',
        channels: 'Notification Channels',
        channelsDescription: 'Choose how you want to receive notifications',
        types: 'Notification Types',
        typesDescription: 'Select which notifications you want to receive',
        email: 'Email Notifications',
        emailDescription: 'Receive notifications via email',
        inApp: 'In-App Notifications',
        inAppDescription: 'Show notifications in the application',
        renewalReminders: 'Renewal Reminders',
        renewalRemindersDescription: 'Get notified about upcoming contract renewals',
        discrepancyAlerts: 'Discrepancy Alerts',
        discrepancyAlertsDescription: 'Get notified when invoice discrepancies are detected',
        weeklyDigest: 'Weekly Digest',
        weeklyDigestDescription: 'Receive a weekly summary of activity',
        alertDays: 'Alert Days',
        alertDaysDescription: 'Choose how many days before renewal to receive alerts',
        fetchError: 'Failed to load notification preferences',
        saveSuccess: 'Notification preferences saved successfully',
        saveError: 'Failed to save notification preferences'
      },
      // Language and currency
      language: 'Language',
      languageDesc: 'Choose your preferred language',
      currency: 'Currency',
      currencyDesc: 'Select the currency for displaying amounts',
      currencyOptions: {
        // Major World Currencies
        EUR: 'Euro (EUR)',
        USD: 'US Dollar (USD)',
        GBP: 'British Pound (GBP)',
        JPY: 'Japanese Yen (JPY)',
        CHF: 'Swiss Franc (CHF)',
        CAD: 'Canadian Dollar (CAD)',
        AUD: 'Australian Dollar (AUD)',
        NZD: 'New Zealand Dollar (NZD)',
        // Asian Currencies
        CNY: 'Chinese Yuan (CNY)',
        INR: 'Indian Rupee (INR)',
        SGD: 'Singapore Dollar (SGD)',
        HKD: 'Hong Kong Dollar (HKD)',
        KRW: 'South Korean Won (KRW)',
        THB: 'Thai Baht (THB)',
        MYR: 'Malaysian Ringgit (MYR)',
        IDR: 'Indonesian Rupiah (IDR)',
        PHP: 'Philippine Peso (PHP)',
        VND: 'Vietnamese Dong (VND)',
        TWD: 'Taiwan Dollar (TWD)',
        // Middle East & North Africa
        MAD: 'Moroccan Dirham (MAD)',
        SAR: 'Saudi Riyal (SAR)',
        AED: 'UAE Dirham (AED)',
        TND: 'Tunisian Dinar (TND)',
        EGP: 'Egyptian Pound (EGP)',
        QAR: 'Qatari Riyal (QAR)',
        KWD: 'Kuwaiti Dinar (KWD)',
        BHD: 'Bahraini Dinar (BHD)',
        OMR: 'Omani Rial (OMR)',
        JOD: 'Jordanian Dinar (JOD)',
        ILS: 'Israeli Shekel (ILS)',
        // European Currencies
        SEK: 'Swedish Krona (SEK)',
        NOK: 'Norwegian Krone (NOK)',
        DKK: 'Danish Krone (DKK)',
        PLN: 'Polish Zloty (PLN)',
        CZK: 'Czech Koruna (CZK)',
        HUF: 'Hungarian Forint (HUF)',
        RON: 'Romanian Leu (RON)',
        TRY: 'Turkish Lira (TRY)',
        RUB: 'Russian Ruble (RUB)',
        UAH: 'Ukrainian Hryvnia (UAH)',
        // Americas
        MXN: 'Mexican Peso (MXN)',
        BRL: 'Brazilian Real (BRL)',
        ARS: 'Argentine Peso (ARS)',
        CLP: 'Chilean Peso (CLP)',
        COP: 'Colombian Peso (COP)',
        PEN: 'Peruvian Sol (PEN)',
        // Africa
        ZAR: 'South African Rand (ZAR)',
        NGN: 'Nigerian Naira (NGN)',
        KES: 'Kenyan Shilling (KES)',
        GHS: 'Ghanaian Cedi (GHS)',
        XOF: 'West African CFA Franc (XOF)',
        XAF: 'Central African CFA Franc (XAF)'
      },
      // Roles
      procurementManager: 'Procurement Manager',
      complianceOfficer: 'Compliance Officer',
      admin: 'Organization Admin',
      orgAdmin: 'Organization Admin',
      viewer: 'Viewer',
      roles: {
        admin: 'Organization Admin',
        orgAdmin: 'Organization Admin',
        procurementManager: 'Procurement Manager',
        complianceOfficer: 'Compliance Officer',
        viewer: 'Viewer'
      },
      // Security section
      securityDesc: 'Manage your password and security settings',
      changePassword: 'Change Password',
      currentPassword: 'Current Password',
      newPassword: 'New Password',
      confirmPassword: 'Confirm New Password',
      enterCurrentPassword: 'Enter your current password',
      enterNewPassword: 'Enter your new password',
      confirmNewPassword: 'Confirm your new password',
      updatePassword: 'Update Password',
      updating: 'Updating...',
      passwordMinLength: 'At least 8 characters',
      passwordUppercase: 'One uppercase letter',
      passwordLowercase: 'One lowercase letter',
      passwordNumber: 'One number',
      passwordMismatch: 'Passwords do not match',
      currentPasswordRequired: 'Current password is required',
      newPasswordRequired: 'New password is required',
      passwordTooShort: 'Password must be at least 8 characters',
      passwordNeedsUppercase: 'Password must contain at least one uppercase letter',
      passwordNeedsLowercase: 'Password must contain at least one lowercase letter',
      passwordNeedsNumber: 'Password must contain at least one number',
      passwordRequirements: 'Password must be at least 8 characters',
      passwordUpdated: 'Password updated successfully',
      passwordUpdateFailed: 'Failed to update password',
      accountSecurity: 'Account Security',
      lastPasswordChange: 'Last Password Change',
      twoFactorAuth: 'Two-Factor Authentication',
      notAvailable: 'Information not available',
      comingSoon: 'Coming soon',
      // Administration section
      administration: 'Administration',
      users: {
        title: 'User Management',
        subtitle: 'Manage users in your organization',
        addUser: 'Add User',
        searchPlaceholder: 'Search users...',
        allStatuses: 'All Statuses',
        allRoles: 'All Roles',
        empty: 'No users found',
        emptyDesc: 'Add users to your organization to get started.',
        user: 'User',
        users: 'users',
        role: 'Role',
        created: 'Created',
        lastLogin: 'Last Login',
        deactivate: 'Deactivate',
        reactivate: 'Reactivate',
        changeRole: 'Change Role',
        email: 'Email',
        displayName: 'Display Name',
        password: 'Password',
        optionalPassword: 'Leave empty to require password setup',
        passwordHint: 'If provided, must be at least 8 characters',
        resetPassword: 'Reset Password',
        resetPasswordSuccess: 'Password reset email sent successfully',
        resetPasswordFailed: 'Failed to send password reset email',
        deactivateReason: 'Enter reason for deactivation:',
        confirmReactivate: 'Are you sure you want to reactivate this user?',
        confirmResetPassword: 'Send a password reset email to this user?'
      },
      invitations: {
        title: 'Invitations',
        subtitle: 'Invite users to join your organization',
        sendInvite: 'Send Invitation',
        sendInvitation: 'Send Invitation',
        pendingInvitations: 'Pending Invitations',
        noInvitations: 'No invitations',
        noInvitationsDesc: 'Send invitations to add users to your organization.',
        invitations: 'invitations',
        resend: 'Resend',
        cancel: 'Cancel',
        expired: 'Expired',
        accepted: 'Accepted',
        role: 'Role',
        invitedBy: 'Invited by',
        sentOn: 'Sent on',
        expires: 'Expires',
        message: 'Message',
        resentTimes: 'Resent',
        times: 'time(s)',
        email: 'Email Address',
        personalMessage: 'Personal Message',
        messagePlaceholder: 'Add a personal message (optional)',
        messageHint: 'Optional message to include in the invitation email',
        confirmResend: 'Resend this invitation?',
        confirmCancel: 'Cancel this invitation?'
      },
      registrations: {
        title: 'Registration Requests',
        subtitle: 'Approve or reject user registration requests',
        pendingApprovals: 'Pending Approvals',
        pendingRequests: 'pending requests',
        requests: 'requests',
        noRequests: 'No registration requests',
        noRequestsDesc: 'Registration requests will appear here when users sign up.',
        approve: 'Approve',
        reject: 'Reject',
        rejectionReason: 'Reason for rejection',
        reason: 'Reason',
        confirmApprove: 'Approve this registration request?',
        rejectReason: 'Enter reason for rejection:'
      },
      organization: {
        title: 'Organization Settings',
        subtitle: 'Manage your organization preferences',
        general: 'General',
        generalSettings: 'General Settings',
        generalDesc: 'Basic organization information',
        name: 'Organization Name',
        defaultCurrency: 'Default Currency',
        timezone: 'Timezone',
        onboarding: 'User Onboarding',
        onboardingSettings: 'User Onboarding Settings',
        onboardingDesc: 'Configure how users join your organization',
        subscription: 'Subscription',
        allowSelfRegistration: 'Allow Self-Registration',
        allowSelfRegistrationDesc: 'Allow users to request to join your organization',
        requireApproval: 'Require Admin Approval',
        requireApprovalDesc: 'New registrations require admin approval before activation',
        allowedDomains: 'Allowed Email Domains',
        allowedDomainsHint: 'Comma-separated list. Leave empty to allow all domains.',
        defaultRole: 'Default Role for New Users',
        defaultRoleHint: 'Role assigned to new users who join via invitation or self-registration',
        invitationExpiry: 'Invitation Expiry (Days)',
        invitationExpiryHint: 'Number of days before invitations expire (1-30)',
        subscriptionDetails: 'Subscription Details',
        subscriptionDesc: 'Your current plan and usage',
        currentPlan: 'Current Plan',
        maxContracts: 'Max Contracts',
        maxUsers: 'Max Users',
        maxStorage: 'Max Storage',
        upgradeMessage: 'Need more capacity? Contact us to upgrade your plan.',
        contactSales: 'Contact Sales',
        saveSuccess: 'Organization settings saved successfully',
        onboardingSaveSuccess: 'Onboarding settings saved successfully'
      },
      usage: {
        title: 'Usage & Billing',
        subtitle: 'Monitor your organization\'s resource consumption',
        overview: 'Overview',
        history: 'History',
        byUser: 'By User',
        daysRemaining: 'days remaining',
        overageEnabled: 'Overage billing is enabled for your organization. Usage beyond limits will incur additional charges.',
        aiTokens: 'AI Tokens',
        storage: 'Storage',
        documents: 'Documents',
        extractions: 'Extractions',
        tokens: 'Tokens',
        remaining: 'remaining',
        overage: 'overage',
        expectedOverageCharges: 'Expected Overage Charges',
        totalOverage: 'Total Expected Overage',
        period: 'Period',
        overageCost: 'Overage',
        user: 'User',
        share: 'Share',
        noHistory: 'No usage history available',
        noUserData: 'No user usage data available',
        status: {
          ok: 'OK',
          warning: 'Warning',
          blocked: 'Blocked',
          overage: 'Overage'
        }
      },
      billing: {
        title: 'Billing & Subscription',
        subtitle: 'Manage your subscription and payment details',
        currentPlan: 'Current Plan',
        planDetails: 'Plan Details',
        planIncludes: 'Plan Includes',
        billingPeriod: 'Billing Period',
        nextBilling: 'Next Billing Date',
        paymentMethod: 'Payment Method',
        usageSummary: 'Usage Summary',
        usageThisPeriod: 'Usage This Period',
        changePlan: 'Change Plan',
        manageBilling: 'Manage Billing',
        managePayments: 'Manage Payments',
        managePaymentsDesc: 'Update payment methods, view invoices, and manage your subscription',
        plans: 'Available Plans',
        selectPlan: 'Select Plan',
        monthly: 'Monthly',
        annual: 'Annual',
        annualSavings: 'Save with annual billing',
        currentPlanBadge: 'Current',
        recommended: 'Recommended',
        popular: 'Most Popular',
        upgrade: 'Upgrade',
        downgrade: 'Downgrade',
        perMonth: '/month',
        perYear: '/year',
        billedAnnually: 'billed annually',
        billedMonthly: 'billed monthly',
        confirmUpgrade: 'Confirm Upgrade',
        confirmDowngrade: 'Confirm Downgrade',
        upgradeSuccess: 'Successfully upgraded your plan',
        upgradeError: 'Failed to upgrade plan. Please try again.',
        portalError: 'Failed to open billing portal. Please try again.',
        loadError: 'Failed to load subscription details',
        free: 'Free',
        unlimited: 'Unlimited',
        cancelSubscription: 'Cancel Subscription',
        reactivateSubscription: 'Reactivate Subscription',
        subscriptionActive: 'Active',
        subscriptionCanceled: 'Canceled',
        subscriptionPastDue: 'Past Due',
        cancelsOn: 'Cancels on',
        viewDetails: 'View Details',
        managePayment: 'Manage Payment',
        choosePlan: 'Choose a Plan',
        save20: 'Save 20%',
        contactSales: 'Contact Sales',
        select: 'Select',
        upgradeCanceled: 'Plan upgrade was canceled.',
        cancelPending: 'Your subscription will be canceled at the end of the current billing period.',
        noSubscription: 'No active subscription',
        features: {
          contracts: 'Contracts',
          invoices: 'Invoices',
          users: 'Users',
          storage: 'Storage',
          aiExtractions: 'AI Extractions',
          support: 'Support',
          apiAccess: 'API Access',
          customIntegrations: 'Custom Integrations'
        }
      }
    },

    // Admin (Super Admin)
    admin: {
      superAdmin: 'Super Admin',
      dashboard: {
        title: 'Platform Administration'
      },
      stats: {
        totalOrganizations: 'Total Organizations',
        totalUsers: 'Total Users',
        activeOrgs: 'Active Organizations',
        suspendedOrgs: 'Suspended',
        pendingApprovals: 'Pending Approvals',
        acrossAllPlans: 'Across all plans',
        platformWide: 'Platform-wide',
        currentlyActive: 'Currently active',
        awaitingReview: 'Awaiting review',
        requiresAttention: 'Requires attention'
      },
      analytics: {
        title: 'Platform Analytics',
        subtitle: 'Overview of platform usage and metrics',
        orgsByPlan: 'Organizations by Plan',
        usersByRole: 'Users by Role'
      },
      recentOrganizations: 'Recent Organizations',
      noOrganizations: 'No organizations yet',
      created: 'Created',
      quickActions: 'Quick Actions',
      actions: {
        createOrg: 'Create Organization',
        createOrgDesc: 'Set up a new organization',
        manageUsers: 'Manage Users',
        manageUsersDesc: 'View all platform users',
        reviewPending: 'Review Pending',
        reviewPendingDesc: 'Approve pending requests',
        billing: 'Billing & Usage',
        billingDesc: 'View platform billing'
      },
      organizations: {
        title: 'Organizations',
        create: 'New Organization',
        searchPlaceholder: 'Search organizations...',
        allStatuses: 'All Statuses',
        empty: 'No organizations found',
        name: 'Organization',
        plan: 'Plan',
        status: 'Status',
        users: 'Users',
        created: 'Created',
        activate: 'Activate',
        suspend: 'Suspend',
        createTitle: 'Create Organization',
        details: 'Organization Details',
        changePlan: 'Change Plan',
        changePlanTitle: 'Change Subscription Plan',
        changingPlanFor: 'Changing plan for',
        // Extended fields
        editTitle: 'Edit Organization',
        basicInfo: 'Basic Info',
        domain: 'Domain',
        website: 'Website',
        phone: 'Phone',
        industry: 'Industry',
        taxId: 'Tax ID / VAT',
        description: 'Description',
        descriptionPlaceholder: 'Brief description of the organization...',
        address: 'Address',
        street: 'Street',
        city: 'City',
        state: 'State / Province',
        postalCode: 'Postal Code',
        country: 'Country',
        primaryContact: 'Primary Contact',
        contactName: 'Name',
        contactRole: 'Role / Title',
        contactEmail: 'Email',
        contactPhone: 'Phone',
        companyPhone: 'Company Phone',
        noContact: 'No contact information',
        subscription: 'Subscription',
        meta: 'Meta',
        updated: 'Updated',
        logo: 'Logo',
        uploadLogo: 'Upload Logo',
        removeLogo: 'Remove',
        logoHint: 'JPG, PNG, SVG or WebP. Max 2MB.',
        invalidFileType: 'Invalid file type. Please upload a JPG, PNG, SVG, or WebP image.',
        fileTooLarge: 'File is too large. Maximum size is 2MB.',
        selectIndustry: 'Select industry...'
      },
      users: {
        title: 'All Users',
        searchPlaceholder: 'Search by name or email...',
        allStatuses: 'All Statuses',
        allRoles: 'All Roles',
        empty: 'No users found',
        user: 'User',
        organization: 'Organization',
        role: 'Role',
        status: 'Status',
        lastLogin: 'Last Login',
        userDetails: 'User Details',
        profile: 'Profile',
        displayName: 'Display Name',
        email: 'Email',
        orgName: 'Name',
        orgId: 'ID',
        activity: 'Activity',
        created: 'Created',
        activate: 'Activate',
        suspend: 'Suspend',
        confirmActivate: 'Are you sure you want to activate this user?',
        activateFailed: 'Failed to activate user',
        suspendReason: 'Please provide a reason for suspending this user:',
        suspendFailed: 'Failed to suspend user',
        resetPassword: 'Reset Password',
        confirmResetPassword: 'Send a password reset email to this user?',
        resetPasswordSuccess: 'Password reset email sent successfully',
        resetPasswordFailed: 'Failed to send password reset email',
        editUser: 'Edit User',
        noOrganization: '-- No Organization (Pending) --',
        createUser: 'Create User',
        password: 'Password',
        passwordPlaceholder: 'Leave blank to send welcome email',
        requiredFields: 'Email and Display Name are required',
        createFailed: 'Failed to create user',
        superAdminNoOrg: 'Super Admins have platform-wide access and do not belong to a specific organization.'
      },
      confirmSuspend: 'Are you sure you want to suspend this organization?',
      plans: {
        title: 'Plans & AI Providers',
        free: 'Free',
        starter: 'Starter',
        professional: 'Professional',
        enterprise: 'Enterprise',
        contracts: 'contracts',
        users: 'users',
        new: 'New Plan',
        create: 'Create New Plan',
        edit: 'Edit Plan',
        empty: 'No plans created yet',
        createFirst: 'Create Your First Plan',
        confirmDelete: 'Are you sure you want to delete this plan?'
      },
      providers: {
        title: 'AI Providers',
        new: 'Add Provider',
        create: 'Add AI Provider',
        edit: 'Edit Provider',
        empty: 'No AI providers configured yet',
        createFirst: 'Add Your First Provider',
        confirmDelete: 'Are you sure you want to delete this provider?'
      },
      billing: {
        title: 'Billing & Usage',
        period: 'Period',
        overview: 'Overview',
        organizations: 'Organizations',
        subscriptionRevenue: 'Subscription Revenue',
        thisPeriod: 'This period',
        estimatedCosts: 'Estimated LLM Costs',
        aiProvider: 'AI provider costs',
        grossMargin: 'Gross Margin',
        margin: 'margin',
        totalTokens: 'Total Tokens Used',
        acrossAllOrgs: 'Across all organizations',
        usageSummary: 'Platform Usage Summary',
        totalDocuments: 'Documents Processed',
        totalExtractions: 'AI Extractions',
        totalStorage: 'Storage Used',
        activeOrgs: 'Active Organizations',
        overageOrgs: 'Organizations with Overage',
        noOverage: 'No organizations with overage',
        noOverageDesc: 'All organizations are within their plan limits',
        organization: 'Organization',
        overageTokens: 'Overage Tokens',
        overageCost: 'Overage Cost',
        noOrgs: 'No organizations found',
        noOrgsDesc: 'No usage data available for this period',
        allOrgs: 'All Organizations',
        export: 'Export',
        tokenUsage: 'Token Usage',
        documents: 'Documents',
        storage: 'Storage',
        overage: 'Overage',
        cost: 'Est. Cost',
        manage: 'Manage',
        billingSettings: 'Billing Settings',
        enableOverage: 'Enable Overage',
        enableOverageDesc: 'Allow organization to exceed plan limits with additional charges',
        customPricing: 'Custom Pricing',
        optional: 'optional',
        customPricingHint: 'Leave blank to use plan default rates',
        pricePerToken: 'Price per 1K Tokens',
        pricePerGb: 'Price per GB Storage',
        pricePerDoc: 'Price per Document',
        pricePerExtraction: 'Price per Extraction',
        billingNotes: 'Billing Notes',
        billingNotesPlaceholder: 'Internal notes about this organization...'
      },
      suspendReason: 'Please enter a reason for suspending this organization:'
    },

    // Upload
    upload: {
      title: 'Upload Documents',
      subtitle: 'Upload invoices and contracts for AI-powered extraction and analysis',
      invoice: 'Invoice',
      contract: 'Contract',
      dragDrop: 'Drag & drop files here or',
      browse: 'browse',
      supportedFormats: 'Supports PDF, PNG, JPG, CSV, XLSX (max 25MB)',
      processing: 'Processing...',
      processDocuments: 'Process Documents',
      // Batch upload
      batchTitle: 'Batch Upload',
      selectedFiles: 'Selected Files',
      uploadAll: 'Upload All',
      clearAll: 'Clear All',
      uploading: 'Uploading...',
      uploadComplete: 'Upload Complete',
      uploadFailed: 'Upload Failed',
      removeFile: 'Remove File',
      cancelUpload: 'Cancel Upload',
      // Recent uploads
      recentUploads: 'Recent Uploads',
      noRecentUploads: 'No recent uploads',
      // Tips
      tips: {
        title: 'Upload Tips',
        formats: 'Supported formats: PDF, PNG, JPG, CSV, XLSX',
        quality: 'For best results, use clear, high-resolution documents',
        batch: 'You can upload multiple files at once for batch processing',
        review: 'Review extracted data and make corrections before saving'
      },
      // Modal
      modal: {
        processBtn: 'Process Documents'
      },
      // Status
      status: {
        uploading: 'Uploading...',
        extracting: 'Extracting data...',
        complete: 'Complete',
        error: 'Error'
      },
      // Extraction preview
      extractionPreview: 'Extraction Preview',
      extractedFields: 'Extracted Fields',
      needsReview: 'Needs Review',
      highConfidence: 'High Confidence',
      lowConfidence: 'Low Confidence',
      mediumConfidence: 'Medium Confidence',
      overallConfidence: 'Overall Confidence',
      editField: 'Edit Field',
      saveCorrections: 'Save Corrections',
      noCorrections: 'No corrections to save',
      correctionsSaved: 'Corrections saved successfully',
      // Confidence
      confidence: 'Confidence',
      confidenceHigh: 'High',
      confidenceMedium: 'Medium',
      confidenceLow: 'Low',
      // Review flow
      finishReview: 'Finish Review',
      reviewingDocument: 'Reviewing Document',
      // Success
      success: {
        title: 'Upload Complete!',
        message: 'Your documents have been processed successfully.',
        uploadMore: 'Upload More',
        viewDocuments: 'View Documents'
      },
      // Errors
      errors: {
        invalidFormat: 'Invalid file format',
        fileTooLarge: 'File exceeds maximum size (25MB)',
        uploadFailed: 'Upload failed. Please try again.',
        extractionFailed: 'Extraction failed for this document'
      },
      // Status messages for modal
      status: {
        uploading: 'Uploading...',
        extracting: 'Extracting data...',
        complete: 'Complete',
        error: 'Error'
      },
      modal: {
        processBtn: 'Process Documents'
      }
    },
    
    // Activity types
    activity: {
      invoiceUploaded: 'Invoice uploaded',
      analysisCompleted: 'Analysis completed',
      discrepancyResolved: 'Discrepancy resolved',
      contractRenewed: 'Contract renewed',
      contractUploaded: 'Contract uploaded',
      discrepanciesFound: 'discrepancies found in batch upload'
    },
    
    // Status
    status: {
      active: 'Active',
      expired: 'Expired',
      pending: 'Pending',
      review: 'Review',
      approved: 'Approved',
      draft: 'Draft',
      terminated: 'Terminated',
      pendingReview: 'Pending Review',
      pendingApproval: 'Pending Approval',
      renewed: 'Renewed'
    },

    // Analysis (Procurement Analysis)
    analysis: {
      // Dashboard
      dashboardTitle: 'Procurement Analytics',
      dashboardSubtitle: 'Real-time insights into your procurement performance',
      totalSpendAnalyzed: 'Total Spend Analyzed',
      discrepanciesDetected: 'Discrepancies Detected',
      potentialOvercharges: 'Potential Overcharges',
      savingsRecovered: 'Savings Recovered',
      vsLastMonth: 'vs last month',
      ofSpend: 'of spend',
      recoveryRate: 'recovery rate',
      spendByCategory: 'Spend by Category',
      discrepancyInsights: 'Discrepancy Insights',
      total: 'total',
      topIssueTypes: 'Top Issue Types',
      vendorPerformance: 'Vendor Performance',
      noVendorData: 'No vendor data available yet',
      accuracy: 'accuracy',
      resolutionProgress: 'Resolution Progress',
      resolved: 'Resolved',
      reviewPending: 'Review',
      pendingItems: 'pending items',
      recentActivity: 'Recent Activity',
      noRecentActivity: 'No recent activity',
      poweredByAI: 'Powered by AI',
      runAnalysis: 'Run Analysis',
      category: {
        software: 'Software & Licenses',
        services: 'Professional Services',
        infrastructure: 'Infrastructure',
        supplies: 'Office Supplies',
        other: 'Other',
      },
      type: {
        pricing: 'Price Overcharge',
        quantity: 'Quantity Mismatch',
        terms: 'Contract Violation',
        unauthorized: 'Unauthorized Charge',
      },
      status: {
        pending: 'Pending Review',
        underReview: 'Under Review',
        resolved: 'Resolved',
        dismissed: 'Dismissed',
        clean: 'Clean',
        hasDiscrepancies: 'Has Discrepancies',
        needsReview: 'Needs Review',
        noContract: 'No Contract',
      },
      insight: {
        criticalAlert: 'Critical Issues Require Attention',
        criticalDesc: 'critical discrepancies need immediate review to prevent financial loss.',
        savingsOpportunity: 'Savings Opportunity Identified',
        savingsDesc: 'in potential overcharges detected. Take action to recover these funds.',
        improveRecovery: 'Improve Recovery Rate',
        recoveryDesc: 'Your current recovery rate is below average. Consider prioritizing high-impact discrepancies.',
        goodStanding: 'Looking Good!',
        goodDesc: 'No critical issues detected. Keep monitoring your procurement spend for optimal results.',
        reviewNow: 'Review Now',
        viewDetails: 'View Details',
        viewStrategy: 'View Strategy',
      },
      invoiceAnalysis: 'Invoice Analysis',
      contractAnalysis: 'Contract Analysis',
      overview: 'Overview',
      discrepancies: 'Discrepancies',
      optimizations: 'Optimizations',
      aiInsights: 'AI Insights',
      variance: 'Variance',
      impact: 'Impact',
      savings: 'Savings',
      overcharge: 'overcharge',
      undercharge: 'undercharge',
      noVariance: 'No variance',
      criticalHigh: 'critical/high',
      allLowMedium: 'All low/medium',
      clean: 'All clear',
      potentialOvercharge: 'potential overcharge',
      noImpact: 'No impact',
      opportunities: 'opportunities',
      noOptimizations: 'No optimizations',
      invoice: 'Invoice',
      contract: 'Contract',
      lineItems: 'Line Items',
      matched: 'Matched',
      reanalyze: 'Re-analyze',
      noContractLinked: 'No Contract Linked',
      noContractMessage: 'Link a contract to enable full analysis with line-by-line comparison.',
      selectContract: 'Select Contract',
      // Discrepancy types
      discrepancy: {
        priceOvercharge: 'Price Overcharge',
        quantityExcess: 'Quantity Excess',
        unauthorizedItem: 'Unauthorized Item',
        missingDiscount: 'Missing Discount',
        duplicateCharge: 'Duplicate Charge',
        incorrectTax: 'Incorrect Tax',
        contractExpired: 'Contract Expired'
      },
      // AI
      aiPowered: 'AI Powered',
      aiSummary: 'AI Summary',
      summary: 'Summary',
      keyFindings: 'Key Findings',
      priorityActions: 'Priority Actions',
      procurementInsights: 'Procurement Insights',
      negotiationTips: 'Negotiation Tips',
      suggestedScript: 'Suggested Script',
      confidenceLabel: 'confidence',
      confidence: {
        high: 'High Confidence',
        medium: 'Medium Confidence',
        low: 'Low Confidence',
      },
      // Values
      invoiced: 'Invoiced',
      expected: 'Expected',
      // Actions
      accept: 'Accept',
      dispute: 'Dispute',
      reject: 'Reject',
      acceptSuccess: 'Discrepancy accepted successfully',
      disputeSuccess: 'Discrepancy disputed successfully',
      rejectSuccess: 'Discrepancy rejected successfully',
      // Optimizations
      optimization: {
        volumeDiscount: 'Volume Discount',
        renegotiation: 'Renegotiation',
        consolidation: 'Consolidation',
        timing: 'Timing',
        alternative: 'Alternative'
      },
      optimizationOpportunities: 'Optimization Opportunities',
      potentialSavings: 'Potential Savings',
      totalPotentialSavings: 'Total Potential Savings',
      perInvoice: 'per invoice',
      monthly: 'monthly',
      annual: 'annual',
      oneTime: 'one-time',
      affectedItems: 'Affected Items',
      // Contract selector
      linkingInvoice: 'Linking Invoice',
      matchScore: 'Match Score',
      vendorMatch: 'Vendor Match',
      skuOverlap: 'SKU Overlap',
      dateValidity: 'Date Validity',
      valueAlignment: 'Value Alignment',
      bestMatch: 'Best Match',
      noSuggestions: 'No Suggestions',
      noSuggestionsMessage: 'No matching contracts found for this invoice.',
      analyzeWithoutContract: 'Analyze Without Contract',
      linkAndAnalyze: 'Link & Analyze',
      // Empty states
      noDiscrepancies: 'No Discrepancies Found',
      noDiscrepanciesMessage: 'This invoice matches the contract terms.',
      noOptimizationsMessage: 'No optimization opportunities identified.',
      noInsights: 'No additional insights available.',
      // Summary
      discrepanciesFound: 'Discrepancies Found',
      issues: 'issues',
      totalImpact: 'Total Impact',
      topDiscrepancies: 'Top Discrepancies',
      topOptimizations: 'Top Optimizations',
      viewAll: 'View All',
      // Contract Analysis
      analyze: 'Analyze',
      analyzeAll: 'Analyze All',
      invoicesAnalyzed: 'Invoices Analyzed',
      totalInvoiceValue: 'Total Invoice Value',
      totalDiscrepancies: 'Total Discrepancies',
      totalVariance: 'Total Variance',
      vendorPerformance: 'Vendor Performance',
      totalInvoices: 'Total Invoices',
      invoicesWithIssues: 'Invoices with Issues',
      errorRate: 'Error Rate',
      totalOvercharges: 'Total Overcharges',
      commonIssues: 'Common Issues',
      keyRecommendations: 'Key Recommendations',
      discrepancyBreakdown: 'Discrepancy Breakdown',
      bySeverity: 'By Severity',
      byType: 'By Type',
      invoiceAnalyses: 'Invoice Analyses',
      invoices: 'Invoices',
      noInvoices: 'No Invoices',
      noInvoicesLinked: 'No invoices are linked to this contract.'
    },

    // Contract (for ContractList and ContractDetail components)
    contract: {
      vendor: 'Vendor',
      value: 'Value',
      period: 'Period',
      title: 'Title',
      status: 'Status',
      expires: 'Expires',
      expired: 'Expired',
      expiresTomorrow: 'Expires tomorrow',
      daysLeft: 'days left',
      autoRenewal: 'Auto-renewal',
      manualRenewal: 'Manual renewal',
      oneTime: 'One-time',
      renew: 'Renew',
      expiringWithin: 'Expiring Within',
      noContracts: 'No contracts found',
      noContractsDescription: 'Upload a contract to get started',
      uploadContract: 'Upload Contract',
      uploadNew: 'Upload Contract',
      // Detail page translations
      number: 'Contract Number',
      type: 'Type',
      renewalType: 'Renewal Type',
      untitled: 'Untitled Contract',
      contractInfo: 'Contract Information',
      vendorInfo: 'Vendor Information',
      vendorName: 'Vendor Name',
      vendorId: 'Vendor ID',
      vendorContact: 'Contact',
      dates: 'Important Dates',
      effectiveDate: 'Effective Date',
      expirationDate: 'Expiration Date',
      daysRemaining: 'Days Remaining',
      financial: 'Financial Details',
      totalValue: 'Total Value',
      currency: 'Currency',
      paymentTerms: 'Payment Terms',
      pricingSchedule: 'Pricing Schedule',
      noPricingItems: 'No pricing items extracted',
      itemCode: 'Item Code',
      description: 'Description',
      unit: 'Unit',
      unitPrice: 'Unit Price',
      minQty: 'Min Qty',
      maxQty: 'Max Qty',
      keyTerms: 'Key Terms',
      linkedInvoices: 'Linked Invoices',
      noLinkedInvoices: 'No invoices linked to this contract yet',
      totalInvoiced: 'Total Invoiced',
      discrepancies: 'Issues & Discrepancies',
      noDiscrepancies: 'No issues found for this contract',
      recentDiscrepancies: 'Recent Discrepancies',
      recentIssues: 'Recent Issues',
      contractIssues: 'Contract Issues',
      invoiceDiscrepancies: 'Invoice Discrepancies',
      compareInvoices: 'Compare Invoices',
      expiredAlert: 'Contract Expired',
      expiredMessage: 'This contract has expired and should be renewed or terminated.',
      expiringCritical: 'Expiring Soon',
      expiringNotice: 'Expiration Notice',
      daysUntilExpiry: 'days until expiration',
      initiateRenewal: 'Initiate Renewal',
      notFound: 'Contract not found',
      loadError: 'Failed to load contract',
      // AI Summary translations
      aiSummary: 'AI Contract Summary',
      generatingSummary: 'Generating AI summary...',
      aiGenerated: 'AI Generated',
      // Edit form
      parties: 'Parties',
      buyer: 'Buyer',
      seller: 'Seller/Vendor',
      saveSuccess: 'Contract saved successfully!',
      saveError: 'Failed to save contract',
      // Additional detail fields
      signedDate: 'Signed Date',
      billingFrequency: 'Billing Frequency',
      // Renewal terms
      renewalTerms: 'Renewal Terms',
      noticePeriod: 'Notice Period',
      renewalTerm: 'Renewal Term',
      optOutDeadline: 'Opt-out Deadline',
      // Legal terms
      legalTerms: 'Legal Terms',
      governingLaw: 'Governing Law',
      disputeResolution: 'Dispute Resolution',
      liabilityLimit: 'Liability Limit',
      terminationClause: 'Termination Clause',
      terminationNotice: 'Termination Notice',
      insuranceRequirements: 'Insurance Requirements',
      confidentiality: 'Confidentiality',
      nonCompete: 'Non-Compete',
      customTerms: 'Additional Terms',
    },

    // Vendors
    vendors: {
      name: 'Name',
      category: 'Category',
      aliases: 'Also known as',
      totalValue: 'Total Value',
      activeContracts: 'Active Contracts',
      contracts: 'contracts',
      noContracts: 'No active contracts',
      email: 'Email',
      phone: 'Phone',
      address: 'Address',
      search: 'Search',
      searchPlaceholder: 'Search vendors...',
      noVendors: 'No vendors found',
      noVendorsDescription: 'Vendors are automatically created when you upload contracts or invoices',
      addVendor: 'Add Vendor',
      viewContracts: 'View Contracts',
      viewInvoices: 'View Invoices',
      deleteConfirm: 'Are you sure you want to delete this vendor? This will not delete associated contracts or invoices.',
      deleteSuccess: 'Vendor deleted successfully',
      deleteFailed: 'Failed to delete vendor. Please try again.',
      createHint: 'Vendors are automatically created when you upload contracts or invoices',
      namePlaceholder: 'Enter vendor name',
      emailPlaceholder: 'vendor@example.com',
      phonePlaceholder: '+1 (555) 123-4567',
      nameRequired: 'Vendor name is required',
      createSuccess: 'Vendor created successfully',
      createFailed: 'Failed to create vendor',
      editVendor: 'Edit Vendor',
      vendorInfo: 'Vendor Information',
      contactInfo: 'Contact Information',
      updateSuccess: 'Vendor updated successfully',
      updateFailed: 'Failed to update vendor',
      overview: 'Overview',
      totalInvoices: 'Total Invoices',
      vendorId: 'Vendor ID',
      primaryContact: 'Primary Contact',
      noContractsLinked: 'No contracts linked to this vendor',
      noInvoicesLinked: 'No invoices linked to this vendor',
      notFound: 'Vendor not found',
      loadError: 'Failed to load vendor details',
      categories: {
        technology: 'Technology',
        services: 'Services',
        supplies: 'Supplies',
        consulting: 'Consulting',
        other: 'Other',
      },
    },

    // Login
    login: {
      title: 'Sign In',
      subtitle: 'Access your procurement intelligence platform',
      continueWithGoogle: 'Continue with Google',
      continueWithMicrosoft: 'Continue with Microsoft',
      enterpriseSSO: 'Enterprise SSO',
      enterpriseDomain: 'Company Domain',
      enterpriseDomainPlaceholder: 'your-company.com',
      continueWithSSO: 'Continue with SSO',
      signInWithEmail: 'Sign in with Email',
      or: 'or',
      noAccount: "Don't have an account?",
      createAccount: 'Create one',
      forgotPassword: 'Forgot password?',
      error: {
        authFailed: 'Authentication failed. Please try again.',
        enterpriseDomainRequired: 'Please enter your company domain'
      }
    },

    // Auth
    auth: {
      // Login & Register
      signIn: 'Sign In',
      register: 'Create Account',
      email: 'Email',
      password: 'Password',
      confirmPassword: 'Confirm Password',
      newPassword: 'New Password',
      fullName: 'Full Name',
      companyName: 'Company Name',
      companyNamePlaceholder: 'Enter your company or organization name',
      forgotPassword: 'Forgot password?',
      noAccount: "Don't have an account?",
      hasAccount: 'Already have an account?',
      signUp: 'Sign up',
      orContinueWith: 'or continue with',
      continueWithGoogle: 'Continue with Google',
      continueWithMicrosoft: 'Continue with Microsoft',
      createAccountSubtitle: 'Create your account',
      // Email Verification
      verifyEmail: 'Verify Your Email',
      verificationCodeSent: 'We sent a 6-digit code to',
      enterCode: 'Enter verification code',
      verify: 'Verify',
      resendCode: 'Resend code',
      didntReceiveCode: "Didn't receive the code?",
      codeSent: 'Code sent successfully',
      // Password Reset
      resetPassword: 'Reset Password',
      sendResetCode: 'Send Reset Code',
      enterResetCode: 'Enter Reset Code',
      backToSignIn: 'Back to Sign in',
      passwordResetSuccess: 'Password updated successfully',
      // Processing
      callback: 'Signing in...',
      processing: 'Processing authentication...',
      // Errors
      invalidCredentials: 'Invalid email or password',
      emailExists: 'Email already registered',
      weakPassword: 'Password must be at least 8 characters',
      passwordMismatch: 'Passwords do not match',
      invalidEmail: 'Please enter a valid email',
      invalidOtp: 'Invalid or expired code',
      otpExpired: 'Code has expired. Please request a new one.',
      emailNotFound: 'No account found with this email',
      tooManyAttempts: 'Too many attempts. Please try again later.',
      sessionExpired: 'Your session has expired. Please sign in again.'
    },

    // Checkout
    checkout: {
      verifyingPayment: 'Verifying your payment...',
      paymentSuccess: 'Payment Successful!',
      completeRegistration: 'Complete your registration to get started',
      subscriptionReady: 'Your subscription is ready to activate',
      activateAccount: 'Activate Account',
    },

    // Errors
    errors: {
      pageNotFound: 'Page not found',
      somethingWentWrong: 'Something went wrong'
    },

    // Optimization
    optimization: {
      title: 'Cost Optimization',
      description: 'Discover and implement cost-saving opportunities across your procurement operations.',
      potentialSavings: 'Potential Savings',
      implementedSavings: 'Implemented Savings',
      totalOpportunities: 'Total Opportunities',
      implemented: 'Implemented',
      runAnalysis: 'Run Analysis',
      analyzing: 'Analyzing...',
      analysisComplete: 'Analysis complete',
      analysisError: 'Failed to run analysis',
      fetchError: 'Failed to load optimization opportunities',
      noOpportunities: 'No Opportunities Found',
      noOpportunitiesDesc: 'Run an analysis to discover cost-saving opportunities.',
      affectedVendors: 'Affected Vendors',
      affectedContracts: 'Affected Contracts',
      perYear: '/ year',
      reduction: 'reduction',
      accept: 'Accept',
      reject: 'Reject',
      acceptSuccess: 'Opportunity accepted successfully',
      acceptError: 'Failed to accept opportunity',
      rejectSuccess: 'Opportunity rejected',
      rejectError: 'Failed to reject opportunity',
      summary: 'Summary',
      recommendation: 'Recommendation',
      currentState: 'Current State',
      proposedState: 'Proposed State',
      totalSavings: 'Total Estimated Savings',
      monthlySpend: 'Monthly',
      annualSpend: 'Annual',
      confidence: 'Confidence',
      type: {
        volume_discount: 'Volume Discount',
        consolidation: 'Vendor Consolidation',
        renegotiation: 'Contract Renegotiation',
        elimination: 'Service Elimination',
        timing: 'Payment Timing',
        alternative: 'Alternative Vendor'
      },
      status: {
        identified: 'Identified',
        evaluating: 'Evaluating',
        under_review: 'Under Review',
        approved: 'Approved',
        in_progress: 'In Progress',
        implemented: 'Implemented',
        rejected: 'Rejected',
        deferred: 'Deferred'
      },
      priority: {
        critical: 'Critical',
        high: 'High',
        medium: 'Medium',
        low: 'Low'
      }
    },

    // Reports
    reports: {
      title: 'Reports',
      subtitle: 'Generate and manage compliance reports',
      generate: 'Generate Report',
      generateNew: 'Generate New Report',
      availableTypes: 'Available Report Types',
      generatedReports: 'Generated Reports',
      reportName: 'Report Name',
      reportType: 'Report Type',
      reportFormat: 'Format',
      reportTitle: 'Title (Optional)',
      titlePlaceholder: 'Enter a custom title for the report',
      description: 'Description',
      descriptionPlaceholder: 'Add a description for this report',
      startDate: 'Start Date',
      endDate: 'End Date',
      format: 'Format',
      status: 'Status',
      createdAt: 'Created',
      size: 'Size',
      download: 'Download',
      noReports: 'No reports generated yet',
      noTypesAvailable: 'No report types available',
      selectType: 'Select a report type',
      selectTypeError: 'Please select a report type',
      generating: 'Generating...',
      generateError: 'Failed to generate report',
      downloadError: 'Failed to download report',
      deleteError: 'Failed to delete report',
      confirmDelete: 'Are you sure you want to delete this report?'
    },

    // Audit
    audit: {
      title: 'Audit Trail',
      subtitle: 'Track all changes and actions in the system',
      noEntries: 'No audit entries found',
      filterByAction: 'Filter by Action',
      filterByResource: 'Filter by Resource Type',
      filterByActor: 'Filter by Actor',
      filterByDate: 'Filter by Date',
      allActions: 'All Actions',
      allResources: 'All Resources',
      success: 'Success',
      failed: 'Failed',
      actor: 'Actor',
      action: 'Action',
      resource: 'Resource',
      timestamp: 'Timestamp',
      details: 'Details',
      viewChanges: 'View Changes',
      status: 'Status'
    },

    // Individual discrepancy translations
    discrepancy: {
      addComment: 'Add Comment',
      aiAnalysis: 'AI Analysis',
      clause: 'Clause',
      commentPlaceholder: 'Enter your comment...',
      comparison: 'Comparison',
      confidence: 'Confidence',
      contract: 'Contract',
      contractValue: 'Contract Value',
      detected: 'Detected',
      detectedAt: 'Detected At',
      difference: 'Difference',
      dismiss: 'Dismiss',
      dismissed: 'Dismissed',
      escalate: 'Escalate',
      id: 'ID',
      impact: 'Impact',
      invoice: 'Invoice',
      invoiceValue: 'Invoice Value',
      lineItem: 'Line Item',
      linkedDocuments: 'Linked Documents',
      loadError: 'Failed to load discrepancy',
      markResolved: 'Mark as Resolved',
      metadata: 'Metadata',
      noDiscrepancies: 'No discrepancies found',
      noDiscrepanciesDescription: 'No discrepancies have been detected yet',
      notFound: 'Discrepancy not found',
      overcharge: 'Overcharge',
      overchargeDetected: 'Overcharge Detected',
      overchargeExplanation: 'The invoice amount exceeds the contract price',
      pending: 'Pending',
      reopen: 'Reopen',
      resolutionHistory: 'Resolution History',
      resolve: 'Resolve',
      resolved: 'Resolved',
      resolvedAt: 'Resolved At',
      severity: 'Severity',
      startReview: 'Start Review',
      status: 'Status',
      suggestedAction: 'Suggested Action',
      totalImpact: 'Total Impact',
      type: {
        pricing: 'Pricing',
        quantity: 'Quantity',
        terms: 'Terms',
        dates: 'Dates',
        missingItem: 'Missing Item',
        unauthorized: 'Unauthorized',
        duplicate: 'Duplicate',
        other: 'Other',
        // CamelCase variants (for UI components)
        missingDiscount: 'Missing Discount',
        incorrectTax: 'Incorrect Tax',
        priceOvercharge: 'Price Overcharge',
        quantityExcess: 'Quantity Excess',
        duplicateCharge: 'Duplicate Charge',
        unauthorizedItem: 'Unauthorized Item',
        contractExpired: 'Contract Expired',
        // Snake case variants (from API)
        missing_required_field: 'Missing Required Field',
        price_mismatch: 'Price Mismatch',
        quantity_mismatch: 'Quantity Mismatch',
        date_mismatch: 'Date Mismatch',
        unauthorized_item: 'Unauthorized Item',
        duplicate_charge: 'Duplicate Charge',
        missing_discount: 'Missing Discount',
        incorrect_tax: 'Incorrect Tax',
        contract_expired: 'Contract Expired',
        invalid_format: 'Invalid Format',
        missing_signature: 'Missing Signature',
        terms_violation: 'Terms Violation',
        compliance_issue: 'Compliance Issue',
        overcharge: 'Overcharge',
        undercharge: 'Undercharge',
        // Descriptions
        pricingDesc: 'Price differs from contract terms',
        quantityDesc: 'Quantity does not match order',
        termsDesc: 'Violates contract terms',
        datesDesc: 'Date outside valid period',
        missingItemDesc: 'Item missing from invoice',
        unauthorizedDesc: 'Item not in contract',
        duplicateDesc: 'Duplicate charge detected',
        otherDesc: 'Other discrepancy type'
      },
      underReview: 'Under Review',
      undercharge: 'Undercharge',
      underchargeDetected: 'Undercharge Detected',
      underchargeExplanation: 'The invoice amount is below the contract price'
    },

    // Individual invoice translations
    invoice: {
      amount: 'Amount',
      analyzeAgainstContract: 'Analyze Against Contract',
      confirmUnlink: 'Are you sure you want to unlink this contract?',
      contractLinked: 'Contract linked successfully',
      contractUnlinked: 'Contract unlinked',
      date: 'Date',
      description: 'Description',
      discrepancies: 'Discrepancies',
      dueDate: 'Due Date',
      financialInfo: 'Financial Information',
      invoiceInfo: 'Invoice Information',
      lineItems: 'Line Items',
      linkAnother: 'Link Another',
      linkContract: 'Link Contract',
      linkError: 'Failed to link contract',
      linkedContracts: 'Linked Contracts',
      loadError: 'Failed to load invoice',
      noDiscrepancies: 'No discrepancies found',
      noLineItems: 'No line items',
      noLinkedContracts: 'No contracts linked',
      notFound: 'Invoice not found',
      number: 'Invoice Number',
      poNumber: 'PO Number',
      quantity: 'Quantity',
      reanalyze: 'Re-analyze',
      subtotal: 'Subtotal',
      tax: 'Tax',
      total: 'Total',
      unitPrice: 'Unit Price',
      unlinkError: 'Failed to unlink contract',
      untitled: 'Untitled Invoice',
      vendorAddress: 'Vendor Address',
      vendorId: 'Vendor ID',
      vendorInfo: 'Vendor Information',
      vendorName: 'Vendor Name',
      suggestedContracts: 'Suggested Contracts',
      suggestionsIntro: 'Based on vendor, SKU matching, and contract dates:',
      recommended: 'Recommended',
      linkAndAnalyze: 'Link & Analyze',
      edit: 'Edit Invoice',
      basicInfo: 'Basic Information',
      dates: 'Dates',
      sku: 'SKU',
      currency: 'Currency',
      addLineItem: 'Add Item',
      saveSuccess: 'Invoice saved successfully',
      saveError: 'Failed to save invoice',
      recalculate: 'Recalculate from line items',
      receivedDate: 'Received Date',
      status: 'Status',
    },

    // Status translations
    status: {
      active: 'Active',
      analyzed: 'Analyzed',
      approved: 'Approved',
      disputed: 'Disputed',
      draft: 'Draft',
      expired: 'Expired',
      extracted: 'Extracted',
      paid: 'Paid',
      pending: 'Pending',
      pendingApproval: 'Pending Approval',
      pendingReview: 'Pending Review',
      rejected: 'Rejected',
      renewed: 'Renewed',
      terminated: 'Terminated'
    },

    // Time translations
    time: {
      daysAgo: 'days ago',
      hoursAgo: 'hours ago',
      justNow: 'Just now',
      minutesAgo: 'minutes ago'
    },

    // Copilot AI Assistant
    copilot: {
      title: 'Kashf AI',
      placeholder: 'Ask about your invoices, contracts...',
      send: 'Send',
      newChat: 'New Chat',
      history: 'History',
      typing: 'Thinking...',
      error: 'Something went wrong. Please try again.',
      noConversations: 'No conversations yet',
      deleteConfirm: 'Delete this conversation?',
      backToChat: 'Back to chat',
      suggestions: {
        title: 'Try asking:',
        invoice: 'What invoices are overdue?',
        contract: 'Which contracts expire this month?',
        vendor: 'Who are my top vendors by spend?',
        discrepancy: 'Show me unresolved discrepancies'
      }
    },

  },

  fr: {
    // Navigation
    nav: {
      dashboard: 'Tableau de bord',
      contracts: 'Contrats',
      invoices: 'Factures',
      vendors: 'Fournisseurs',
      renewals: 'Renouvellements',
      analysis: 'Analyse',
      discrepancies: 'Écarts',
      integrations: 'Intégrations',
      optimization: 'Optimisation',
      audit: 'Journal d\'audit',
      settings: 'Paramètres',
      admin: 'Admin',
      platformAdmin: 'Admin Plateforme',
      adminDashboard: 'Tableau de bord',
      organizations: 'Organisations',
      allUsers: 'Tous les utilisateurs',
      platformAnalytics: 'Analytique',
      plansProviders: 'Plans & IA',
      billingUsage: 'Facturation & Usage'
    },

    // Common
    common: {
      search: 'Rechercher contrats, factures...',
      uploadDocument: 'Télécharger un document',
      viewAll: 'Voir tout',
      cancel: 'Annuler',
      save: 'Enregistrer',
      saving: 'Enregistrement...',
      creating: 'Création...',
      unsavedChanges: 'Vous avez des modifications non enregistrées. Êtes-vous sûr de vouloir quitter ?',
      saveChanges: 'Enregistrer les modifications',
      close: 'Fermer',
      review: 'Examiner',
      takeAction: 'Agir',
      configure: 'Configurer',
      connect: 'Connecter',
      all: 'Tous',
      days: 'jours',
      month: 'mois',
      year: 'an',
      home: 'Accueil',
      pending: 'En attente',
      invoice: 'Facture',
      contract: 'Contrat',
      status: 'Statut',
      type: 'Type',
      priority: 'Priorité',
      retry: 'Réessayer',
      noData: 'Aucune donnée disponible',
      regenerate: 'Régénérer',
      viewDetails: 'Voir les détails',
      identified: 'Identifié',
      view: 'Voir',
      more: 'Plus',
      any: 'Tous',
      clear: 'Effacer',
      apply: 'Appliquer',
      cardView: 'Vue en cartes',
      tableView: 'Vue en tableau',
      showing: 'Affichage',
      of: 'sur',
      items: 'éléments',
      actions: 'Actions',
      loading: 'Chargement...',
      download: 'Télécharger',
      edit: 'Modifier',
      save: 'Enregistrer',
      cancel: 'Annuler',
      editing: 'Modification',
      backToList: 'Retour à la liste',
      total: 'total',
      issues: 'problèmes',
      noIssues: 'Aucun problème',
      viewAll: 'Voir tout',
      aiGenerated: 'Généré par IA',
      create: 'Créer',
      selectOption: 'Sélectionner...',
    },

    // Upload
    upload: {
      title: 'Télécharger un document',
      subtitle: 'Téléchargez et traitez vos contrats et factures',
      tabInvoice: 'Facture',
      tabContract: 'Contrat',
      dragDrop: 'Glissez-déposez les fichiers ici ou',
      browse: 'Parcourir',
      hint: 'Formats acceptés: PDF, PNG, JPG, CSV, XLSX (max 25Mo)',
      process: 'Traiter les documents',
      supportedFormats: 'PDF, PNG, JPG, CSV, XLSX (max 25Mo)',
      processing: 'Traitement en cours...',
      processDocuments: 'Traiter les documents',
      recentUploads: 'Téléchargements récents',
      noRecentUploads: 'Aucun téléchargement récent',
      finishReview: 'Terminer la révision',
      dropHere: 'Déposez les fichiers ici',
      or: 'ou',
      acceptedFormats: 'Formats acceptés',
      maxSize: 'Max',
      clearAll: 'Tout effacer',
      uploadAll: 'Tout télécharger',
      complete: 'Terminé',
      failed: 'Échec',
      description: 'Glissez-déposez les fichiers ici ou cliquez pour parcourir',
      errorInvalidType: 'Type de fichier invalide',
      errorFileTooLarge: 'Fichier trop volumineux',
      modal: {
        processBtn: 'Traiter les documents',
      },
      status: {
        uploading: 'Téléchargement...',
        extracting: 'Extraction des données...',
        complete: 'Terminé',
        error: 'Erreur',
      },
      tips: {
        title: 'Conseils pour de meilleurs résultats',
        formats: 'Utilisez le format PDF pour une meilleure extraction de texte',
        quality: 'Assurez-vous que les documents sont clairs et lisibles',
        batch: 'Vous pouvez télécharger plusieurs fichiers à la fois',
        review: 'Vérifiez les données extraites avant d\'enregistrer',
      },
      success: {
        title: 'Téléchargement terminé !',
        message: 'Vos documents ont été traités avec succès.',
        uploadMore: 'Télécharger plus',
        viewDocuments: 'Voir les documents',
      },
    },

    // Limit reached messages
    limits: {
      limitReached: 'Limite atteinte',
      documents: 'documents',
      storage: 'stockage',
      documentLimitMessage: 'Vous avez atteint votre limite mensuelle de téléchargement de documents. Mettez à niveau votre forfait ou attendez le mois prochain pour télécharger plus de documents.',
      storageLimitMessage: 'Vous avez atteint votre limite de stockage. Mettez à niveau votre forfait ou supprimez des fichiers pour libérer de l\'espace.',
      currentUsage: 'Utilisation actuelle',
      options: 'Vos options',
      viewUsage: 'Voir les détails d\'utilisation',
      viewUsageDesc: 'Consultez votre utilisation actuelle et vos limites',
      upgradePlan: 'Mettre à niveau votre forfait',
      upgradePlanDesc: 'Obtenez plus de documents et de stockage',
      contactSupport: 'Contacter le support',
      contactSupportDesc: 'Obtenez de l\'aide pour votre compte',
    },

    // Toast notifications
    toast: {
      success: 'Succès',
      error: 'Erreur',
      warning: 'Attention',
      info: 'Info',
    },

    // Search
    search: {
      noResults: 'Aucun résultat trouvé',
      contracts: 'Contrats',
      invoices: 'Factures',
      vendors: 'Fournisseurs',
      discrepancies: 'Écarts'
    },

    // Notifications (dropdown)
    notifications: {
      title: 'Notifications',
      markAllRead: 'Tout marquer comme lu',
      noNotifications: 'Aucune notification',
      viewSettings: 'Paramètres de notification',
      fetchError: 'Échec du chargement des notifications',
      types: {
        renewal_reminder: 'Rappel de renouvellement',
        discrepancy_alert: 'Alerte d\'écart',
        invoice_processed: 'Facture traitée',
        optimization_opportunity: 'Opportunité d\'économies',
        system_alert: 'Alerte système'
      }
    },

    // Dashboard
    dashboard: {
      title: 'Tableau de bord',
      activeContracts: 'Contrats actifs',
      invoicesProcessed: 'Factures traitées',
      openDiscrepancies: 'Écarts ouverts',
      savingsIdentified: 'Économies identifiées',
      upcomingRenewals: 'Renouvellements à venir',
      recentDiscrepancies: 'Écarts récents',
      recentActivity: 'Activité récente',
      optimizationOpportunities: 'Opportunités d\'optimisation',
      accuracyRate: 'Taux de précision',
      analysisSummary: 'Résumé de l\'analyse',
      contractsMatched: 'Contrats correspondants',
      discrepanciesFound: 'Écarts trouvés',
      discrepancyTypes: 'Types d\'écarts',
      financialImpact: 'Impact financier',
      getStarted: 'Commencer',
      newAnalysis: 'Nouvelle analyse',
      noRecentActivity: 'Aucune activité récente',
      processingTime: 'Temps de traitement',
      recommendations: 'Recommandations',
      resolutionStatus: 'Statut de résolution',
      severityDistribution: 'Répartition par sévérité',
      topVendors: 'Principaux fournisseurs',
      totalAnalyzed: 'Total analysé',
      welcomeMessage: 'Bienvenue sur Kashf.ai',
      welcomeTitle: 'Bienvenue',
      subtitle: 'Votre tableau de bord d\'intelligence des achats'
    },

    // Contracts
    contracts: {
      title: 'Contrats',
      addContract: 'Ajouter un contrat',
      contractId: 'ID Contrat',
      vendor: 'Fournisseur',
      contractTitle: 'Titre',
      type: 'Type',
      value: 'Valeur',
      endDate: 'Date de fin',
      status: 'Statut',
      startDate: 'Date de début',
      autoRenewal: 'Renouvellement auto',
      optOutPeriod: 'Période de résiliation',
      contractTerms: 'Conditions du contrat',
      pricingSchedule: 'Grille tarifaire',
      contractInfo: 'Informations du contrat',
      datesRenewal: 'Dates et renouvellement',
      item: 'Article',
      unitPrice: 'Prix unitaire',
      quantity: 'Qté',
      yes: 'Oui',
      no: 'Non',
      active: 'Actif',
      expired: 'Expiré',
      softwareLicense: 'Licence logicielle',
      serviceAgreement: 'Contrat de service',
      infrastructure: 'Infrastructure',
      allTypes: 'Tous les types'
    },
    
    // Invoices
    invoices: {
      title: 'Factures',
      uploadInvoice: 'Télécharger une facture',
      uploadNew: 'Télécharger une facture',
      invoiceId: 'ID Facture',
      number: 'Facture #',
      contract: 'Contrat',
      date: 'Date',
      dueDate: 'Date d\'échéance',
      dateRange: 'Période',
      amount: 'Montant',
      total: 'Total',
      issues: 'Problèmes',
      issue: 'problème',
      status: 'Statut',
      vendor: 'Fournisseur',
      subtotal: 'Sous-total',
      tax: 'Taxe',
      discrepanciesFound: 'Écarts détectés',
      noIssues: 'Aucun',
      none: 'Aucun',
      issuesPlural: 'problèmes',
      discrepancies: 'Écarts',
      noInvoices: 'Aucune facture trouvée',
      noInvoicesDescription: 'Téléchargez une facture pour commencer',
      noDocument: 'Aucun fichier document disponible pour cette facture',
      overdue: 'En retard',
      analyze: 'Analyser',
      analyzeSelected: 'Analyser la sélection',
      pending: 'En attente',
      needsReview: 'À examiner',
      approved: 'Approuvé',
      allVendors: 'Tous les fournisseurs',
      withIssues: 'Avec problèmes',
      noIssuesFilter: 'Sans problèmes',
      // Contract linking
      linkContract: 'Lier un contrat',
      linkAnother: 'Lier un autre',
      linkedContracts: 'Contrats liés',
      noLinkedContracts: 'Aucun contrat lié à cette facture',
      contractLinked: 'Contrat lié avec succès',
      contractUnlinked: 'Contrat délié avec succès',
      linkError: 'Échec de la liaison du contrat',
      unlinkError: 'Échec de la suppression du lien',
      confirmUnlink: 'Êtes-vous sûr de vouloir délier ce contrat ?',
      confirmDelete: 'Êtes-vous sûr de vouloir supprimer cette facture ?',
      deleteSuccess: 'Facture supprimée avec succès',
      deleteError: 'Échec de la suppression de la facture'
    },

    // Renewals
    renewals: {
      title: 'Renouvellements',
      // Status
      urgent: 'Urgent',
      soon: 'Bientôt',
      upcoming: 'À venir',
      completed: 'Terminé',
      // Time ranges
      within14Days: 'Dans 14 jours',
      days15to30: '15-30 jours',
      days31to90: '31-90 jours',
      overdue: 'En retard',
      // Types
      autoRenews: 'Renouvellement auto',
      manual: 'Manuel',
      // Stats
      totalRenewals: 'Total des renouvellements',
      urgentRenewals: 'Urgents',
      dueThisMonth: 'Ce mois-ci',
      totalValue: 'Valeur totale',
      // Filters
      filterByStatus: 'Filtrer par statut',
      filterByType: 'Filtrer par type',
      filterByPriority: 'Filtrer par priorité',
      allStatuses: 'Tous les statuts',
      allTypes: 'Tous les types',
      allPriorities: 'Toutes les priorités',
      // Priority
      priority: 'Priorité',
      high: 'Haute',
      medium: 'Moyenne',
      low: 'Basse',
      critical: 'Critique',
      // Actions
      viewDetails: 'Voir les détails',
      addNote: 'Ajouter une note',
      setReminder: 'Définir un rappel',
      markAsReviewed: 'Marquer comme examiné',
      initiateRenewal: 'Initier le renouvellement',
      scheduleReview: 'Planifier l\'examen',
      renewContract: 'Renouveler le contrat',
      cancelRenewal: 'Annuler le renouvellement',
      // Timeline
      timeline: 'Chronologie',
      renewalDate: 'Date de renouvellement',
      optOutDeadline: 'Date limite de résiliation',
      reviewDue: 'Examen dû',
      reminderSent: 'Rappel envoyé',
      contractStart: 'Début du contrat',
      contractEnd: 'Fin du contrat',
      // Details
      daysUntilRenewal: 'Jours avant renouvellement',
      contractValue: 'Valeur du contrat',
      renewalTerms: 'Conditions de renouvellement',
      autoRenewalEnabled: 'Renouvellement auto activé',
      autoRenewalDisabled: 'Renouvellement auto désactivé',
      noticePeriod: 'Période de préavis',
      // Notes
      notes: 'Notes',
      addNoteTitle: 'Ajouter une note',
      notePlaceholder: 'Entrez votre note...',
      noNotes: 'Pas encore de notes',
      // Messages
      noRenewals: 'Aucun renouvellement trouvé',
      noUpcoming: 'Aucun renouvellement à venir',
      renewalSaved: 'Renouvellement mis à jour',
      noteSaved: 'Note ajoutée avec succès',
      reminderSet: 'Rappel défini avec succès',
      // Notifications
      reminderEmailSent: 'Email de rappel envoyé',
      upcomingRenewalAlert: 'Renouvellement de contrat approchant'
    },

    // Renewal (singular - for renewal page module)
    renewal: {
      pageTitle: 'Renouvellements de Contrats',
      pageSubtitle: 'Suivre et gérer les renouvellements de contrats à venir',
      days: 'jours',
      // Stats
      stats: {
        critical: 'Critique',
        urgent: 'Urgent',
        attention: 'Attention requise',
        valueAtRisk: 'Valeur à risque',
        needsDecision: 'Décision requise dans 30 jours'
      },
      // Filters
      filter: {
        status: 'Statut',
        priority: 'Priorité',
        action: 'Action',
        timeframe: 'Période',
        allStatuses: 'Tous les statuts',
        allPriorities: 'Toutes les priorités',
        allActions: 'Toutes les actions'
      },
      // Status
      status: {
        upcoming: 'À venir',
        in_review: 'En cours d\'examen',
        action_taken: 'Action effectuée',
        auto_renewed: 'Renouvelé auto',
        terminated: 'Résilié',
        expired: 'Expiré'
      },
      // Priority
      priority: {
        critical: 'Critique',
        high: 'Haute',
        medium: 'Moyenne',
        low: 'Basse'
      },
      // Action
      action: {
        undecided: 'Indécis',
        renew: 'Renouveler',
        renegotiate: 'Renégocier',
        terminate: 'Résilier',
        renewDesc: 'Continuer le contrat avec les conditions actuelles',
        renegotiateDesc: 'Demander de nouvelles conditions ou tarifs',
        terminateDesc: 'Ne pas renouveler ce contrat'
      },
      selectAction: 'Que souhaitez-vous faire avec ce contrat ?',
      updateError: 'Échec de la mise à jour du renouvellement. Veuillez réessayer.',
      updateSuccess: 'Renouvellement mis à jour avec succès',
      // Urgency
      urgency: {
        overdue: 'En retard',
        critical: 'Critique',
        urgent: 'Urgent',
        attention: 'Attention',
        routine: 'Normal'
      },
      // View
      view: {
        calendar: 'Vue Calendrier',
        list: 'Vue Liste'
      },
      // Card fields
      renewalDate: 'Date de renouvellement',
      daysLeft: 'Jours restants',
      contractValue: 'Valeur du contrat',
      plannedAction: 'Action prévue',
      optOutDeadline: 'Date limite de résiliation',
      assignedTo: 'Assigné à',
      notes: 'notes',
      viewDetails: 'Voir les détails',
      takeAction: 'Agir',
      // Empty state
      empty: {
        title: 'Aucun renouvellement trouvé',
        description: 'Téléchargez des contrats avec des dates d\'expiration pour voir les renouvellements ici.'
      },
      // Sync
      syncFromContracts: 'Synchroniser depuis les contrats',
      // Timeline
      timeline: {
        title: 'Chronologie',
        today: 'Aujourd\'hui',
        optOutDeadline: 'Date limite de résiliation',
        optOutDescription: 'Dernier jour pour se désister du renouvellement automatique',
        renewalDate: 'Date de renouvellement',
        notificationSent: 'Notification envoyée',
        beforeDeadline: 'avant la date limite',
        note: 'Note',
        ago: 'passés',
        in: 'dans'
      }
    },

    // Analysis
    analysis: {
      title: 'Analyse',
      // Dashboard
      dashboardTitle: 'Analytique des Achats',
      dashboardSubtitle: 'Aperçu en temps réel de vos performances d\'approvisionnement',
      totalSpendAnalyzed: 'Dépenses Totales Analysées',
      discrepanciesDetected: 'Anomalies Détectées',
      potentialOvercharges: 'Surfacturations Potentielles',
      savingsRecovered: 'Économies Récupérées',
      vsLastMonth: 'vs mois dernier',
      ofSpend: 'des dépenses',
      recoveryRate: 'taux de récupération',
      spendByCategory: 'Dépenses par Catégorie',
      discrepancyInsights: 'Aperçu des Anomalies',
      total: 'total',
      topIssueTypes: 'Principaux Types de Problèmes',
      vendorPerformance: 'Performance des Fournisseurs',
      noVendorData: 'Aucune donnée fournisseur disponible',
      accuracy: 'précision',
      resolutionProgress: 'Progression de Résolution',
      resolved: 'Résolu',
      reviewPending: 'Examiner',
      pendingItems: 'éléments en attente',
      recentActivity: 'Activité Récente',
      noRecentActivity: 'Aucune activité récente',
      poweredByAI: 'Propulsé par l\'IA',
      runAnalysis: 'Lancer l\'Analyse',
      category: {
        software: 'Logiciels & Licences',
        services: 'Services Professionnels',
        infrastructure: 'Infrastructure',
        supplies: 'Fournitures de Bureau',
        other: 'Autre',
      },
      type: {
        pricing: 'Surfacturation',
        quantity: 'Écart de Quantité',
        terms: 'Violation de Contrat',
        unauthorized: 'Frais Non Autorisés',
      },
      status: {
        pending: 'En Attente de Révision',
        underReview: 'En Cours de Révision',
        resolved: 'Résolu',
        dismissed: 'Rejeté',
      },
      insight: {
        criticalAlert: 'Problèmes Critiques à Traiter',
        criticalDesc: 'anomalies critiques nécessitent une révision immédiate pour éviter des pertes financières.',
        savingsOpportunity: 'Opportunité d\'Économies Identifiée',
        savingsDesc: 'de surfacturations potentielles détectées. Agissez pour récupérer ces fonds.',
        improveRecovery: 'Améliorer le Taux de Récupération',
        recoveryDesc: 'Votre taux de récupération actuel est inférieur à la moyenne. Priorisez les anomalies à fort impact.',
        goodStanding: 'Tout va Bien!',
        goodDesc: 'Aucun problème critique détecté. Continuez à surveiller vos dépenses pour des résultats optimaux.',
        reviewNow: 'Examiner Maintenant',
        viewDetails: 'Voir les Détails',
        viewStrategy: 'Voir la Stratégie',
      },
      discrepancies: 'Écarts',
      optimizations: 'Optimisations',
      discrepancySummary: 'Résumé des écarts',
      openIssues: 'Problèmes ouverts',
      totalImpact: 'Impact total',
      optimizationPotential: 'Potentiel d\'optimisation',
      viewOptimizations: 'Voir les opportunités d\'économies',
      opportunities: 'Opportunités',
      potentialSavings: 'Économies potentielles',
      review: 'Examiner',
      takeAction: 'Agir',
      vendors: 'Fournisseurs',
      pricing: 'Tarification',
      quantity: 'Quantité',
      unauthorized: 'Non autorisé',
      consolidation: 'Consolidation',
      volume: 'Volume',
      renegotiation: 'Renégociation',
      // Procurement Analysis
      overview: 'Aperçu',
      aiInsights: 'Analyses IA',
      topDiscrepancies: 'Principaux écarts',
      topOptimizations: 'Principales optimisations',
      viewAll: 'Voir tout',
      variance: 'Écart',
      discrepancyCount: 'Écarts détectés',
      financialImpact: 'Impact financier',
      savingsOpportunity: 'Opportunité d\'économie',
      critical: 'Critique',
      criticalIssues: 'problèmes critiques',
      potentialOvercharge: 'surfacturation potentielle',
      aiSummary: 'Résumé IA',
      keyFindings: 'Conclusions clés',
      priorityActions: 'Actions prioritaires',
      negotiationTips: 'Conseils de négociation',
      marketInsights: 'Perspectives du marché',
      noDiscrepancies: 'Aucun écart détecté',
      noDiscrepanciesMessage: 'Bonne nouvelle ! Aucun écart n\'a été détecté entre cette facture et le contrat.',
      lineItem: 'Ligne',
      expected: 'Attendu',
      actual: 'Réel',
      accept: 'Accepter',
      dispute: 'Contester',
      reject: 'Rejeter',
      acceptSuccess: 'Écart accepté avec succès',
      disputeSuccess: 'Contestation de l\'écart initiée',
      rejectSuccess: 'Écart rejeté',
      optimizationOpportunities: 'Opportunités d\'optimisation',
      noOptimizations: 'Aucune optimisation trouvée',
      noOptimizationsMessage: 'Aucune opportunité d\'optimisation identifiée pour cette facture.',
      totalPotentialSavings: 'Économies potentielles totales',
      affectedItems: 'Éléments affectés',
      lineItems: 'lignes',
      perInvoice: 'Par facture',
      monthly: 'Mensuel',
      annual: 'Annuel',
      oneTime: 'Ponctuel',
      confidenceLabel: 'confiance',
      optimization: {
        volumeDiscount: 'Remise de volume',
        renegotiation: 'Renégociation',
        consolidation: 'Consolidation',
        timing: 'Timing de paiement',
        alternative: 'Option alternative'
      },
      selectContract: 'Sélectionner un contrat',
      linkingInvoice: 'Lier la facture',
      bestMatch: 'Meilleure correspondance',
      matchScore: 'Score de correspondance',
      vendorMatch: 'Correspondance fournisseur',
      skuOverlap: 'Chevauchement SKU',
      dateValidity: 'Validité des dates',
      valueAlignment: 'Alignement des valeurs',
      noSuggestions: 'Aucune suggestion de contrat',
      noSuggestionsMessage: 'Aucun contrat correspondant trouvé pour cette facture.',
      analyzeWithoutContract: 'Analyser sans contrat',
      linkAndAnalyze: 'Lier et analyser',
      invoiceAnalysis: 'Analyse de facture',
      reanalyze: 'Ré-analyser',
      export: 'Exporter',
      backToInvoice: 'Retour à la facture',
      linkedContract: 'Contrat lié',
      noLinkedContract: 'Aucun contrat lié',
      selectContractToLink: 'Sélectionner un contrat à lier',
      status: {
        clean: 'Conforme',
        has_discrepancies: 'Écarts détectés',
        needs_review: 'À examiner',
        no_contract: 'Sans contrat'
      },
      // Contract Analysis
      analyze: 'Analyser',
      analyzeAll: 'Tout analyser',
      invoicesAnalyzed: 'Factures analysées',
      totalInvoiceValue: 'Valeur totale des factures',
      totalDiscrepancies: 'Total des écarts',
      totalVariance: 'Écart total',
      vendorPerformance: 'Performance fournisseur',
      totalInvoices: 'Total des factures',
      invoicesWithIssues: 'Factures avec problèmes',
      errorRate: 'Taux d\'erreur',
      totalOvercharges: 'Total des surfacturations',
      commonIssues: 'Problèmes courants',
      keyRecommendations: 'Recommandations clés',
      discrepancyBreakdown: 'Répartition des écarts',
      bySeverity: 'Par sévérité',
      byType: 'Par type',
      invoiceAnalyses: 'Analyses des factures',
      invoices: 'Factures',
      noInvoices: 'Aucune facture',
      noInvoicesLinked: 'Aucune facture n\'est liée à ce contrat.'
    },

    // Severity levels
    severity: {
      critical: 'Critique',
      high: 'Élevé',
      medium: 'Moyen',
      low: 'Faible'
    },

    // Discrepancy types
    discrepancy_type: {
      price_mismatch: 'Écart de prix',
      quantity_mismatch: 'Écart de quantité',
      unauthorized_item: 'Article non autorisé',
      missing_discount: 'Remise manquante',
      duplicate_charge: 'Double facturation',
      tax_error: 'Erreur de taxe',
      other: 'Autre'
    },

    // Upload Modal
    upload: {
      title: 'Télécharger un document',
      subtitle: 'Téléchargez et traitez vos factures et contrats',
      tabInvoice: 'Facture',
      tabContract: 'Contrat',
      dragDrop: 'Glissez-déposez les fichiers ici ou',
      browse: 'parcourir',
      hint: 'Supporte PDF, PNG, JPG, CSV, XLSX (max 25Mo)',
      supportedFormats: 'Supporte PDF, PNG, JPG, CSV, XLSX (max 25Mo)',
      process: 'Traiter les documents',
      processDocuments: 'Traiter les documents',
      processing: 'Traitement...',
      cancel: 'Annuler',
      noRecentUploads: 'Aucun téléchargement récent',
      recentUploads: 'Téléchargements récents',
      clearAll: 'Tout effacer',
      uploadAll: 'Tout télécharger',
      dropHere: 'Déposez les fichiers ici',
      or: 'ou',
      acceptedFormats: 'Formats acceptés',
      maxSize: 'Max',
      description: 'Glissez-déposez les fichiers ici ou cliquez pour parcourir',
      complete: 'Terminé',
      failed: 'Échoué',
      errorInvalidType: 'Type de fichier invalide',
      errorFileTooLarge: 'Fichier trop volumineux',
      finishReview: 'Terminer la révision',
      modal: {
        processBtn: 'Traiter les documents'
      },
      status: {
        uploading: 'Téléchargement...',
        extracting: 'Extraction des données...',
        complete: 'Terminé',
        error: 'Erreur'
      },
      tips: {
        title: 'Conseils',
        formats: 'Supporte les formats PDF, PNG, JPG, CSV et XLSX',
        quality: 'Assurez-vous que les documents sont clairs et lisibles',
        batch: 'Vous pouvez télécharger plusieurs fichiers à la fois',
        review: 'Vérifiez les données extraites avant d\'enregistrer'
      },
      success: {
        title: 'Téléchargement réussi',
        message: 'Vos documents ont été traités avec succès',
        uploadMore: 'Télécharger plus',
        viewDocuments: 'Voir les documents'
      }
    },

    // Integrations
    integrations: {
      title: 'Intégrations',
      connected: 'Connecté',
      notConnected: 'Non connecté',
      disconnected: 'Déconnecté',
      lastSync: 'Dernière sync',
      neverSynced: 'Jamais synchronisé',
      documentsSynced: 'documents synchronisés',
      connectToSync: 'Connectez pour synchroniser automatiquement',
      configure: 'Configurer',
      connect: 'Connecter',
      disconnect: 'Déconnecter',
      enable: 'Activer',
      disable: 'Désactiver',
      enabled: 'Activé',
      disabled: 'Désactivé',
      testing: 'Test en cours...',
      creating: 'Création...',
      import: 'Importer',
      documentStorage: 'Stockage de documents',
      procurement: 'Approvisionnement',
      eSignature: 'Signature électronique',
      status: {
        idle: 'Inactif',
        running: 'En cours',
        completed: 'Terminé',
        failed: 'Échoué',
        error: 'Erreur'
      },
      services: {
        dropbox: 'Dropbox',
        box: 'Box',
        googleDrive: 'Google Drive',
        oneDrive: 'OneDrive',
        sharePoint: 'SharePoint',
        erpOracle: 'Oracle ERP',
        erpSap: 'SAP',
        docusign: 'DocuSign',
        adobeSign: 'Adobe Sign'
      }
    },

    // Settings
    settings: {
      title: 'Paramètres',
      subtitle: 'Gérez votre compte et vos préférences',
      profile: 'Profil',
      preferences: 'Préférences',
      team: 'Équipe',
      security: 'Sécurité',
      auditTrail: 'Journal d\'audit',
      saveSuccess: 'Paramètres enregistrés avec succès',
      saveError: 'Échec de l\'enregistrement des paramètres',
      nameHint: 'Le nom est géré par votre fournisseur d\'identité',
      customizeExperience: 'Personnalisez votre expérience',
      // Profile section
      profileInformation: 'Informations du profil',
      profileInfo: 'Informations du profil',
      profileDesc: 'Mettez à jour vos informations de compte.',
      updateAccountDetails: 'Vos informations de compte',
      fullName: 'Nom complet',
      email: 'E-mail',
      role: 'Rôle',
      timezone: 'Fuseau horaire',
      dateFormat: 'Format de date',
      defaultView: 'Vue par défaut',
      // Nested profile
      'profile.title': 'Profil',
      'profile.subtitle': 'Gérez vos informations de compte et préférences',
      'profile.accountInfo': 'Informations du compte',
      'profile.accountInfoDesc': 'Vos informations de compte de base',
      'profile.displayName': 'Nom d\'affichage',
      'profile.email': 'E-mail',
      'profile.role': 'Rôle',
      'profile.organization': 'Organisation',
      'profile.memberSince': 'Membre depuis',
      'profile.lastLogin': 'Dernière connexion',
      'profile.preferences': 'Préférences',
      'profile.preferencesDesc': 'Personnalisez votre expérience',
      'profile.language': 'Langue',
      'profile.timezone': 'Fuseau horaire',
      'profile.dateFormat': 'Format de date',
      'profile.currency': 'Devise',
      'profile.defaultView': 'Vue par défaut',
      'profile.fetchError': 'Échec du chargement du profil',
      'profile.saveSuccess': 'Profil enregistré avec succès',
      'profile.saveError': 'Échec de l\'enregistrement du profil',
      // Notification preferences
      notificationPreferences: 'Préférences de notification',
      notificationPrefs: 'Préférences de notification',
      notificationDesc: 'Choisissez comment vous souhaitez être notifié.',
      chooseNotifications: 'Choisissez comment vous souhaitez être notifié',
      emailNotifications: 'Notifications par e-mail',
      emailNotificationsDesc: 'Recevoir des notifications par e-mail',
      inAppNotifications: 'Notifications dans l\'application',
      inAppNotificationsDesc: 'Afficher les notifications dans l\'application',
      renewalReminders: 'Rappels de renouvellement',
      renewalRemindersDesc: 'Être notifié des renouvellements de contrats à venir',
      discrepancyAlerts: 'Alertes d\'écarts',
      discrepancyAlertsDesc: 'Être notifié des écarts sur les factures',
      weeklyDigest: 'Résumé hebdomadaire',
      weeklyDigestDesc: 'Recevoir un résumé hebdomadaire de l\'activité',
      // Nested notifications
      notifications: {
        title: 'Notifications',
        subtitle: 'Configurez comment vous souhaitez être notifié',
        channels: 'Canaux de notification',
        channelsDescription: 'Choisissez comment recevoir les notifications',
        types: 'Types de notification',
        typesDescription: 'Sélectionnez les notifications que vous souhaitez recevoir',
        email: 'Notifications par e-mail',
        emailDescription: 'Recevoir des notifications par e-mail',
        inApp: 'Notifications dans l\'application',
        inAppDescription: 'Afficher les notifications dans l\'application',
        renewalReminders: 'Rappels de renouvellement',
        renewalRemindersDescription: 'Être notifié des renouvellements de contrats à venir',
        discrepancyAlerts: 'Alertes d\'écarts',
        discrepancyAlertsDescription: 'Être notifié lors de la détection d\'écarts sur les factures',
        weeklyDigest: 'Résumé hebdomadaire',
        weeklyDigestDescription: 'Recevoir un résumé hebdomadaire de l\'activité',
        alertDays: 'Jours d\'alerte',
        alertDaysDescription: 'Choisissez combien de jours avant le renouvellement recevoir les alertes',
        fetchError: 'Échec du chargement des préférences de notification',
        saveSuccess: 'Préférences de notification enregistrées avec succès',
        saveError: 'Échec de l\'enregistrement des préférences de notification'
      },
      // Language and currency
      language: 'Langue',
      languageDesc: 'Choisissez votre langue préférée',
      currency: 'Devise',
      currencyDesc: 'Sélectionnez la devise pour l\'affichage des montants',
      currencyOptions: {
        // Major World Currencies
        EUR: 'Euro (EUR)',
        USD: 'Dollar américain (USD)',
        GBP: 'Livre sterling (GBP)',
        JPY: 'Yen japonais (JPY)',
        CHF: 'Franc suisse (CHF)',
        CAD: 'Dollar canadien (CAD)',
        AUD: 'Dollar australien (AUD)',
        NZD: 'Dollar néo-zélandais (NZD)',
        // Asian Currencies
        CNY: 'Yuan chinois (CNY)',
        INR: 'Roupie indienne (INR)',
        SGD: 'Dollar de Singapour (SGD)',
        HKD: 'Dollar de Hong Kong (HKD)',
        KRW: 'Won sud-coréen (KRW)',
        THB: 'Baht thaïlandais (THB)',
        MYR: 'Ringgit malaisien (MYR)',
        IDR: 'Roupie indonésienne (IDR)',
        PHP: 'Peso philippin (PHP)',
        VND: 'Dong vietnamien (VND)',
        TWD: 'Dollar taïwanais (TWD)',
        // Middle East & North Africa
        MAD: 'Dirham marocain (MAD)',
        SAR: 'Riyal saoudien (SAR)',
        AED: 'Dirham des EAU (AED)',
        TND: 'Dinar tunisien (TND)',
        EGP: 'Livre égyptienne (EGP)',
        QAR: 'Riyal qatari (QAR)',
        KWD: 'Dinar koweïtien (KWD)',
        BHD: 'Dinar bahreïni (BHD)',
        OMR: 'Rial omanais (OMR)',
        JOD: 'Dinar jordanien (JOD)',
        ILS: 'Shekel israélien (ILS)',
        // European Currencies
        SEK: 'Couronne suédoise (SEK)',
        NOK: 'Couronne norvégienne (NOK)',
        DKK: 'Couronne danoise (DKK)',
        PLN: 'Zloty polonais (PLN)',
        CZK: 'Couronne tchèque (CZK)',
        HUF: 'Forint hongrois (HUF)',
        RON: 'Leu roumain (RON)',
        TRY: 'Livre turque (TRY)',
        RUB: 'Rouble russe (RUB)',
        UAH: 'Hryvnia ukrainienne (UAH)',
        // Americas
        MXN: 'Peso mexicain (MXN)',
        BRL: 'Real brésilien (BRL)',
        ARS: 'Peso argentin (ARS)',
        CLP: 'Peso chilien (CLP)',
        COP: 'Peso colombien (COP)',
        PEN: 'Sol péruvien (PEN)',
        // Africa
        ZAR: 'Rand sud-africain (ZAR)',
        NGN: 'Naira nigérian (NGN)',
        KES: 'Shilling kényan (KES)',
        GHS: 'Cedi ghanéen (GHS)',
        XOF: 'Franc CFA ouest-africain (XOF)',
        XAF: 'Franc CFA d\'Afrique centrale (XAF)'
      },
      // Roles
      procurementManager: 'Responsable des achats',
      complianceOfficer: 'Responsable conformité',
      admin: 'Admin Organisation',
      orgAdmin: 'Admin Organisation',
      viewer: 'Lecteur',
      roles: {
        admin: 'Admin Organisation',
        orgAdmin: 'Admin Organisation',
        procurementManager: 'Responsable des achats',
        complianceOfficer: 'Responsable conformité',
        viewer: 'Lecteur'
      },
      // Security section
      securityDesc: 'Gérez votre mot de passe et vos paramètres de sécurité',
      changePassword: 'Changer le mot de passe',
      currentPassword: 'Mot de passe actuel',
      newPassword: 'Nouveau mot de passe',
      confirmPassword: 'Confirmer le nouveau mot de passe',
      enterCurrentPassword: 'Entrez votre mot de passe actuel',
      enterNewPassword: 'Entrez votre nouveau mot de passe',
      confirmNewPassword: 'Confirmez votre nouveau mot de passe',
      updatePassword: 'Mettre à jour le mot de passe',
      updating: 'Mise à jour...',
      passwordMinLength: 'Au moins 8 caractères',
      passwordUppercase: 'Une lettre majuscule',
      passwordLowercase: 'Une lettre minuscule',
      passwordNumber: 'Un chiffre',
      passwordMismatch: 'Les mots de passe ne correspondent pas',
      currentPasswordRequired: 'Le mot de passe actuel est requis',
      newPasswordRequired: 'Le nouveau mot de passe est requis',
      passwordTooShort: 'Le mot de passe doit contenir au moins 8 caractères',
      passwordNeedsUppercase: 'Le mot de passe doit contenir au moins une majuscule',
      passwordNeedsLowercase: 'Le mot de passe doit contenir au moins une minuscule',
      passwordNeedsNumber: 'Le mot de passe doit contenir au moins un chiffre',
      passwordRequirements: 'Le mot de passe doit contenir au moins 8 caractères',
      passwordUpdated: 'Mot de passe mis à jour avec succès',
      passwordUpdateFailed: 'Échec de la mise à jour du mot de passe',
      accountSecurity: 'Sécurité du compte',
      lastPasswordChange: 'Dernier changement de mot de passe',
      twoFactorAuth: 'Authentification à deux facteurs',
      notAvailable: 'Information non disponible',
      comingSoon: 'Bientôt disponible',
      // Administration section
      administration: 'Administration',
      users: {
        title: 'Gestion des utilisateurs',
        subtitle: 'Gérer les utilisateurs de votre organisation',
        addUser: 'Ajouter un utilisateur',
        searchPlaceholder: 'Rechercher des utilisateurs...',
        allStatuses: 'Tous les statuts',
        allRoles: 'Tous les rôles',
        empty: 'Aucun utilisateur trouvé',
        emptyDesc: 'Ajoutez des utilisateurs à votre organisation pour commencer.',
        user: 'Utilisateur',
        users: 'utilisateurs',
        role: 'Rôle',
        created: 'Créé',
        lastLogin: 'Dernière connexion',
        deactivate: 'Désactiver',
        reactivate: 'Réactiver',
        changeRole: 'Changer le rôle',
        email: 'E-mail',
        displayName: 'Nom d\'affichage',
        password: 'Mot de passe',
        optionalPassword: 'Laisser vide pour exiger la configuration du mot de passe',
        passwordHint: 'Si fourni, doit contenir au moins 8 caractères',
        resetPassword: 'Réinitialiser le mot de passe',
        resetPasswordSuccess: 'E-mail de réinitialisation du mot de passe envoyé avec succès',
        resetPasswordFailed: 'Échec de l\'envoi de l\'e-mail de réinitialisation',
        deactivateReason: 'Entrez la raison de la désactivation :',
        confirmReactivate: 'Êtes-vous sûr de vouloir réactiver cet utilisateur ?',
        confirmResetPassword: 'Envoyer un e-mail de réinitialisation du mot de passe à cet utilisateur ?'
      },
      invitations: {
        title: 'Invitations',
        subtitle: 'Invitez des utilisateurs à rejoindre votre organisation',
        sendInvite: 'Envoyer une invitation',
        sendInvitation: 'Envoyer une invitation',
        pendingInvitations: 'Invitations en attente',
        noInvitations: 'Aucune invitation',
        noInvitationsDesc: 'Envoyez des invitations pour ajouter des utilisateurs à votre organisation.',
        invitations: 'invitations',
        resend: 'Renvoyer',
        cancel: 'Annuler',
        expired: 'Expirée',
        accepted: 'Acceptée',
        role: 'Rôle',
        invitedBy: 'Invité par',
        sentOn: 'Envoyé le',
        expires: 'Expire',
        message: 'Message',
        resentTimes: 'Renvoyé',
        times: 'fois',
        email: 'Adresse e-mail',
        personalMessage: 'Message personnel',
        messagePlaceholder: 'Ajouter un message personnel (optionnel)',
        messageHint: 'Message optionnel à inclure dans l\'e-mail d\'invitation',
        confirmResend: 'Renvoyer cette invitation ?',
        confirmCancel: 'Annuler cette invitation ?'
      },
      registrations: {
        title: 'Demandes d\'inscription',
        subtitle: 'Approuver ou rejeter les demandes d\'inscription des utilisateurs',
        pendingApprovals: 'Approbations en attente',
        pendingRequests: 'demandes en attente',
        requests: 'demandes',
        noRequests: 'Aucune demande d\'inscription',
        noRequestsDesc: 'Les demandes d\'inscription apparaîtront ici lorsque des utilisateurs s\'inscriront.',
        approve: 'Approuver',
        reject: 'Rejeter',
        rejectionReason: 'Raison du rejet',
        reason: 'Raison',
        confirmApprove: 'Approuver cette demande d\'inscription ?',
        rejectReason: 'Entrez la raison du rejet :'
      },
      organization: {
        title: 'Paramètres de l\'organisation',
        subtitle: 'Gérer les préférences de votre organisation',
        general: 'Général',
        generalSettings: 'Paramètres généraux',
        generalDesc: 'Informations de base de l\'organisation',
        name: 'Nom de l\'organisation',
        defaultCurrency: 'Devise par défaut',
        timezone: 'Fuseau horaire',
        onboarding: 'Intégration des utilisateurs',
        onboardingSettings: 'Paramètres d\'intégration des utilisateurs',
        onboardingDesc: 'Configurer comment les utilisateurs rejoignent votre organisation',
        subscription: 'Abonnement',
        allowSelfRegistration: 'Autoriser l\'auto-inscription',
        allowSelfRegistrationDesc: 'Permettre aux utilisateurs de demander à rejoindre votre organisation',
        requireApproval: 'Exiger l\'approbation de l\'administrateur',
        requireApprovalDesc: 'Les nouvelles inscriptions nécessitent l\'approbation de l\'administrateur avant activation',
        allowedDomains: 'Domaines de messagerie autorisés',
        allowedDomainsHint: 'Liste séparée par des virgules. Laissez vide pour autoriser tous les domaines.',
        defaultRole: 'Rôle par défaut pour les nouveaux utilisateurs',
        defaultRoleHint: 'Rôle attribué aux nouveaux utilisateurs qui rejoignent via invitation ou auto-inscription',
        invitationExpiry: 'Expiration des invitations (Jours)',
        invitationExpiryHint: 'Nombre de jours avant l\'expiration des invitations (1-30)',
        subscriptionDetails: 'Détails de l\'abonnement',
        subscriptionDesc: 'Votre plan actuel et son utilisation',
        currentPlan: 'Plan actuel',
        maxContracts: 'Contrats max',
        maxUsers: 'Utilisateurs max',
        maxStorage: 'Stockage max',
        upgradeMessage: 'Besoin de plus de capacité ? Contactez-nous pour mettre à niveau votre plan.',
        contactSales: 'Contacter les ventes',
        saveSuccess: 'Paramètres de l\'organisation enregistrés avec succès',
        onboardingSaveSuccess: 'Paramètres d\'intégration enregistrés avec succès'
      },
      usage: {
        title: 'Utilisation & Facturation',
        subtitle: 'Surveillez la consommation de ressources de votre organisation',
        overview: 'Aperçu',
        history: 'Historique',
        byUser: 'Par utilisateur',
        daysRemaining: 'jours restants',
        overageEnabled: 'La facturation des dépassements est activée pour votre organisation. L\'utilisation au-delà des limites entraînera des frais supplémentaires.',
        aiTokens: 'Jetons IA',
        storage: 'Stockage',
        documents: 'Documents',
        extractions: 'Extractions',
        tokens: 'Jetons',
        remaining: 'restant',
        overage: 'dépassement',
        expectedOverageCharges: 'Frais de dépassement prévus',
        totalOverage: 'Total des dépassements prévus',
        period: 'Période',
        overageCost: 'Dépassement',
        user: 'Utilisateur',
        share: 'Part',
        noHistory: 'Aucun historique d\'utilisation disponible',
        noUserData: 'Aucune donnée d\'utilisation par utilisateur disponible',
        status: {
          ok: 'OK',
          warning: 'Attention',
          blocked: 'Bloqué',
          overage: 'Dépassement'
        }
      },
      billing: {
        title: 'Facturation et Abonnement',
        subtitle: 'Gérez votre abonnement et vos détails de paiement',
        currentPlan: 'Plan Actuel',
        planDetails: 'Détails du Plan',
        planIncludes: 'Le Plan Inclut',
        billingPeriod: 'Période de Facturation',
        nextBilling: 'Prochaine Facturation',
        paymentMethod: 'Moyen de Paiement',
        usageSummary: 'Résumé d\'Utilisation',
        usageThisPeriod: 'Utilisation Cette Période',
        changePlan: 'Changer de Plan',
        manageBilling: 'Gérer la Facturation',
        managePayments: 'Gérer les Paiements',
        managePaymentsDesc: 'Mettez à jour les moyens de paiement, consultez les factures et gérez votre abonnement',
        plans: 'Plans Disponibles',
        selectPlan: 'Sélectionner le Plan',
        monthly: 'Mensuel',
        annual: 'Annuel',
        annualSavings: 'Économisez avec la facturation annuelle',
        currentPlanBadge: 'Actuel',
        recommended: 'Recommandé',
        popular: 'Plus Populaire',
        upgrade: 'Mettre à Niveau',
        downgrade: 'Rétrograder',
        perMonth: '/mois',
        perYear: '/an',
        billedAnnually: 'facturé annuellement',
        billedMonthly: 'facturé mensuellement',
        confirmUpgrade: 'Confirmer la Mise à Niveau',
        confirmDowngrade: 'Confirmer la Rétrogradation',
        upgradeSuccess: 'Plan mis à niveau avec succès',
        upgradeError: 'Échec de la mise à niveau. Veuillez réessayer.',
        portalError: 'Échec de l\'ouverture du portail de facturation. Veuillez réessayer.',
        loadError: 'Échec du chargement des détails de l\'abonnement',
        free: 'Gratuit',
        unlimited: 'Illimité',
        cancelSubscription: 'Annuler l\'Abonnement',
        reactivateSubscription: 'Réactiver l\'Abonnement',
        subscriptionActive: 'Actif',
        subscriptionCanceled: 'Annulé',
        subscriptionPastDue: 'En Retard',
        cancelsOn: 'Annulation le',
        viewDetails: 'Voir les détails',
        managePayment: 'Gérer le paiement',
        choosePlan: 'Choisir un plan',
        save20: 'Économisez 20%',
        contactSales: 'Contacter les ventes',
        select: 'Sélectionner',
        upgradeCanceled: 'La mise à niveau a été annulée.',
        cancelPending: 'Votre abonnement sera annulé à la fin de la période de facturation en cours.',
        noSubscription: 'Aucun abonnement actif',
        features: {
          contracts: 'Contrats',
          invoices: 'Factures',
          users: 'Utilisateurs',
          storage: 'Stockage',
          aiExtractions: 'Extractions IA',
          support: 'Support',
          apiAccess: 'Accès API',
          customIntegrations: 'Intégrations Personnalisées'
        }
      }
    },

    // Admin (Super Admin)
    admin: {
      superAdmin: 'Super Admin',
      dashboard: {
        title: 'Administration de la plateforme'
      },
      stats: {
        totalOrganizations: 'Total des organisations',
        totalUsers: 'Total des utilisateurs',
        activeOrgs: 'Organisations actives',
        suspendedOrgs: 'Suspendues',
        pendingApprovals: 'Approbations en attente',
        acrossAllPlans: 'Tous plans confondus',
        platformWide: 'À l\'échelle de la plateforme',
        currentlyActive: 'Actuellement actives',
        awaitingReview: 'En attente de révision',
        requiresAttention: 'Nécessite une attention'
      },
      analytics: {
        title: 'Analytique de la plateforme',
        subtitle: 'Aperçu de l\'utilisation et des métriques de la plateforme',
        orgsByPlan: 'Organisations par plan',
        usersByRole: 'Utilisateurs par rôle'
      },
      recentOrganizations: 'Organisations récentes',
      noOrganizations: 'Aucune organisation',
      created: 'Créé',
      quickActions: 'Actions rapides',
      actions: {
        createOrg: 'Créer une organisation',
        createOrgDesc: 'Configurer une nouvelle organisation',
        manageUsers: 'Gérer les utilisateurs',
        manageUsersDesc: 'Voir tous les utilisateurs',
        reviewPending: 'Examiner les demandes',
        reviewPendingDesc: 'Approuver les demandes en attente',
        billing: 'Facturation & Usage',
        billingDesc: 'Voir la facturation de la plateforme'
      },
      organizations: {
        title: 'Organisations',
        create: 'Nouvelle organisation',
        searchPlaceholder: 'Rechercher des organisations...',
        allStatuses: 'Tous les statuts',
        empty: 'Aucune organisation trouvée',
        name: 'Organisation',
        plan: 'Plan',
        status: 'Statut',
        users: 'Utilisateurs',
        created: 'Créé',
        activate: 'Activer',
        suspend: 'Suspendre',
        createTitle: 'Créer une organisation',
        details: 'Détails de l\'organisation',
        changePlan: 'Changer de plan',
        changePlanTitle: 'Changer le plan d\'abonnement',
        changingPlanFor: 'Changement de plan pour',
        editTitle: 'Modifier l\'organisation',
        basicInfo: 'Informations de base',
        domain: 'Domaine',
        website: 'Site web',
        phone: 'Téléphone',
        industry: 'Secteur',
        taxId: 'N° TVA',
        description: 'Description',
        descriptionPlaceholder: 'Brève description de l\'organisation...',
        address: 'Adresse',
        street: 'Rue',
        city: 'Ville',
        state: 'État / Province',
        postalCode: 'Code postal',
        country: 'Pays',
        primaryContact: 'Contact principal',
        contactName: 'Nom',
        contactRole: 'Rôle / Titre',
        contactEmail: 'Email',
        contactPhone: 'Téléphone',
        companyPhone: 'Téléphone de l\'entreprise',
        noContact: 'Aucune information de contact',
        subscription: 'Abonnement',
        meta: 'Méta',
        updated: 'Mis à jour',
        logo: 'Logo',
        uploadLogo: 'Télécharger le logo',
        removeLogo: 'Supprimer',
        logoHint: 'JPG, PNG, SVG ou WebP. Max 2 Mo.',
        invalidFileType: 'Type de fichier invalide. Veuillez télécharger une image JPG, PNG, SVG ou WebP.',
        fileTooLarge: 'Le fichier est trop volumineux. Taille maximale : 2 Mo.',
        selectIndustry: 'Sélectionner un secteur...'
      },
      users: {
        title: 'Tous les utilisateurs',
        searchPlaceholder: 'Rechercher par nom ou e-mail...',
        allStatuses: 'Tous les statuts',
        allRoles: 'Tous les rôles',
        empty: 'Aucun utilisateur trouvé',
        user: 'Utilisateur',
        organization: 'Organisation',
        role: 'Rôle',
        status: 'Statut',
        lastLogin: 'Dernière connexion',
        userDetails: 'Détails de l\'utilisateur',
        profile: 'Profil',
        displayName: 'Nom d\'affichage',
        email: 'E-mail',
        orgName: 'Nom',
        orgId: 'ID',
        activity: 'Activité',
        created: 'Créé',
        activate: 'Activer',
        suspend: 'Suspendre',
        confirmActivate: 'Êtes-vous sûr de vouloir activer cet utilisateur ?',
        activateFailed: 'Échec de l\'activation de l\'utilisateur',
        suspendReason: 'Veuillez fournir une raison pour suspendre cet utilisateur :',
        suspendFailed: 'Échec de la suspension de l\'utilisateur',
        resetPassword: 'Réinitialiser le mot de passe',
        confirmResetPassword: 'Envoyer un e-mail de réinitialisation de mot de passe à cet utilisateur ?',
        resetPasswordSuccess: 'E-mail de réinitialisation de mot de passe envoyé avec succès',
        resetPasswordFailed: 'Échec de l\'envoi de l\'e-mail de réinitialisation de mot de passe',
        editUser: 'Modifier l\'utilisateur',
        noOrganization: '-- Aucune organisation (En attente) --',
        createUser: 'Créer un utilisateur',
        password: 'Mot de passe',
        passwordPlaceholder: 'Laisser vide pour envoyer un e-mail de bienvenue',
        requiredFields: 'L\'e-mail et le nom d\'affichage sont requis',
        createFailed: 'Échec de la création de l\'utilisateur',
        superAdminNoOrg: 'Les super administrateurs ont un accès à toute la plateforme et n\'appartiennent pas à une organisation spécifique.'
      },
      confirmSuspend: 'Êtes-vous sûr de vouloir suspendre cette organisation ?',
      plans: {
        title: 'Plans & Fournisseurs IA',
        free: 'Gratuit',
        starter: 'Débutant',
        professional: 'Professionnel',
        enterprise: 'Entreprise',
        contracts: 'contrats',
        users: 'utilisateurs',
        new: 'Nouveau plan',
        create: 'Créer un nouveau plan',
        edit: 'Modifier le plan',
        empty: 'Aucun plan créé',
        createFirst: 'Créez votre premier plan',
        confirmDelete: 'Êtes-vous sûr de vouloir supprimer ce plan ?'
      },
      providers: {
        title: 'Fournisseurs IA',
        new: 'Ajouter un fournisseur',
        create: 'Ajouter un fournisseur IA',
        edit: 'Modifier le fournisseur',
        empty: 'Aucun fournisseur IA configuré',
        createFirst: 'Ajoutez votre premier fournisseur',
        confirmDelete: 'Êtes-vous sûr de vouloir supprimer ce fournisseur ?'
      },
      billing: {
        title: 'Facturation & Usage',
        period: 'Période',
        overview: 'Aperçu',
        organizations: 'Organisations',
        subscriptionRevenue: 'Revenus d\'abonnement',
        thisPeriod: 'Cette période',
        estimatedCosts: 'Coûts IA estimés',
        aiProvider: 'Coûts fournisseur IA',
        grossMargin: 'Marge brute',
        margin: 'marge',
        totalTokens: 'Total des tokens utilisés',
        acrossAllOrgs: 'Toutes organisations confondues',
        usageSummary: 'Résumé d\'utilisation de la plateforme',
        totalDocuments: 'Documents traités',
        totalExtractions: 'Extractions IA',
        totalStorage: 'Stockage utilisé',
        activeOrgs: 'Organisations actives',
        overageOrgs: 'Organisations en dépassement',
        noOverage: 'Aucune organisation en dépassement',
        noOverageDesc: 'Toutes les organisations sont dans leurs limites',
        organization: 'Organisation',
        overageTokens: 'Tokens en dépassement',
        overageCost: 'Coût du dépassement',
        noOrgs: 'Aucune organisation trouvée',
        noOrgsDesc: 'Aucune donnée d\'utilisation pour cette période',
        allOrgs: 'Toutes les organisations',
        export: 'Exporter',
        tokenUsage: 'Utilisation des tokens',
        documents: 'Documents',
        storage: 'Stockage',
        overage: 'Dépassement',
        cost: 'Coût est.',
        manage: 'Gérer',
        billingSettings: 'Paramètres de facturation',
        enableOverage: 'Activer le dépassement',
        enableOverageDesc: 'Permettre à l\'organisation de dépasser les limites du plan avec des frais supplémentaires',
        customPricing: 'Tarification personnalisée',
        optional: 'optionnel',
        customPricingHint: 'Laisser vide pour utiliser les tarifs par défaut',
        pricePerToken: 'Prix par 1K tokens',
        pricePerGb: 'Prix par Go de stockage',
        pricePerDoc: 'Prix par document',
        pricePerExtraction: 'Prix par extraction',
        billingNotes: 'Notes de facturation',
        billingNotesPlaceholder: 'Notes internes sur cette organisation...'
      },
      suspendReason: 'Veuillez entrer une raison pour suspendre cette organisation :'
    },

    // Upload
    upload: {
      title: 'Télécharger des documents',
      subtitle: 'Téléchargez des factures et contrats pour extraction et analyse par IA',
      invoice: 'Facture',
      contract: 'Contrat',
      dragDrop: 'Glissez-déposez les fichiers ici ou',
      browse: 'parcourir',
      supportedFormats: 'PDF, PNG, JPG, CSV, XLSX (max 25 Mo)',
      processing: 'Traitement...',
      processDocuments: 'Traiter les documents',
      // Batch upload
      batchTitle: 'Téléchargement en lot',
      selectedFiles: 'Fichiers sélectionnés',
      uploadAll: 'Tout télécharger',
      clearAll: 'Tout effacer',
      uploading: 'Téléchargement...',
      uploadComplete: 'Téléchargement terminé',
      uploadFailed: 'Échec du téléchargement',
      removeFile: 'Supprimer le fichier',
      cancelUpload: 'Annuler le téléchargement',
      // Recent uploads
      recentUploads: 'Téléchargements récents',
      noRecentUploads: 'Aucun téléchargement récent',
      // Tips
      tips: {
        title: 'Conseils de téléchargement',
        formats: 'Formats supportés: PDF, PNG, JPG, CSV, XLSX',
        quality: 'Pour de meilleurs résultats, utilisez des documents clairs et haute résolution',
        batch: 'Vous pouvez télécharger plusieurs fichiers à la fois pour un traitement en lot',
        review: 'Vérifiez les données extraites et faites des corrections avant de sauvegarder'
      },
      // Modal
      modal: {
        processBtn: 'Traiter les documents'
      },
      // Status
      status: {
        uploading: 'Téléchargement...',
        extracting: 'Extraction des données...',
        complete: 'Terminé',
        error: 'Erreur'
      },
      // Extraction preview
      extractionPreview: 'Aperçu de l\'extraction',
      extractedFields: 'Champs extraits',
      needsReview: 'À vérifier',
      highConfidence: 'Haute confiance',
      lowConfidence: 'Faible confiance',
      mediumConfidence: 'Confiance moyenne',
      overallConfidence: 'Confiance globale',
      editField: 'Modifier le champ',
      saveCorrections: 'Enregistrer les corrections',
      noCorrections: 'Aucune correction à enregistrer',
      correctionsSaved: 'Corrections enregistrées avec succès',
      // Confidence
      confidence: 'Confiance',
      confidenceHigh: 'Haute',
      confidenceMedium: 'Moyenne',
      confidenceLow: 'Basse',
      // Review flow
      finishReview: 'Terminer la vérification',
      reviewingDocument: 'Vérification du document',
      // Success
      success: {
        title: 'Téléchargement terminé !',
        message: 'Vos documents ont été traités avec succès.',
        uploadMore: 'Télécharger plus',
        viewDocuments: 'Voir les documents'
      },
      // Errors
      errors: {
        invalidFormat: 'Format de fichier invalide',
        fileTooLarge: 'Le fichier dépasse la taille maximale (25 Mo)',
        uploadFailed: 'Échec du téléchargement. Veuillez réessayer.',
        extractionFailed: 'Échec de l\'extraction pour ce document'
      },
      // Status messages for modal
      status: {
        uploading: 'Téléchargement...',
        extracting: 'Extraction des données...',
        complete: 'Terminé',
        error: 'Erreur'
      },
      modal: {
        processBtn: 'Traiter les documents'
      }
    },

    // Activity
    activity: {
      invoiceUploaded: 'Facture téléchargée',
      analysisCompleted: 'Analyse terminée',
      discrepancyResolved: 'Écart résolu',
      contractRenewed: 'Contrat renouvelé',
      contractUploaded: 'Contrat téléchargé',
      discrepanciesFound: 'écarts trouvés dans le lot'
    },
    
    // Status
    status: {
      active: 'Actif',
      expired: 'Expiré',
      pending: 'En attente',
      review: 'À examiner',
      approved: 'Approuvé',
      draft: 'Brouillon',
      terminated: 'Résilié',
      pendingReview: 'En attente de révision',
      pendingApproval: 'En attente d\'approbation',
      renewed: 'Renouvelé'
    },

    // Analysis (Procurement Analysis)
    analysis: {
      // Dashboard
      dashboardTitle: 'Analytique des Achats',
      dashboardSubtitle: 'Aperçu en temps réel de vos performances d\'approvisionnement',
      totalSpendAnalyzed: 'Dépenses Totales Analysées',
      discrepanciesDetected: 'Anomalies Détectées',
      potentialOvercharges: 'Surfacturations Potentielles',
      savingsRecovered: 'Économies Récupérées',
      vsLastMonth: 'vs mois dernier',
      ofSpend: 'des dépenses',
      recoveryRate: 'taux de récupération',
      spendByCategory: 'Dépenses par Catégorie',
      discrepancyInsights: 'Aperçu des Anomalies',
      total: 'total',
      topIssueTypes: 'Principaux Types de Problèmes',
      vendorPerformance: 'Performance des Fournisseurs',
      noVendorData: 'Aucune donnée fournisseur disponible',
      accuracy: 'précision',
      resolutionProgress: 'Progression de Résolution',
      resolved: 'Résolu',
      reviewPending: 'Examiner',
      pendingItems: 'éléments en attente',
      recentActivity: 'Activité Récente',
      noRecentActivity: 'Aucune activité récente',
      poweredByAI: 'Propulsé par l\'IA',
      runAnalysis: 'Lancer l\'Analyse',
      category: {
        software: 'Logiciels & Licences',
        services: 'Services Professionnels',
        infrastructure: 'Infrastructure',
        supplies: 'Fournitures de Bureau',
        other: 'Autre',
      },
      type: {
        pricing: 'Surfacturation',
        quantity: 'Écart de Quantité',
        terms: 'Violation de Contrat',
        unauthorized: 'Frais Non Autorisés',
      },
      status: {
        pending: 'En Attente de Révision',
        underReview: 'En Cours de Révision',
        resolved: 'Résolu',
        dismissed: 'Rejeté',
        clean: 'Conforme',
        hasDiscrepancies: 'Avec Écarts',
        needsReview: 'À Réviser',
        noContract: 'Sans Contrat',
      },
      insight: {
        criticalAlert: 'Problèmes Critiques à Traiter',
        criticalDesc: 'anomalies critiques nécessitent une révision immédiate pour éviter des pertes financières.',
        savingsOpportunity: 'Opportunité d\'Économies Identifiée',
        savingsDesc: 'de surfacturations potentielles détectées. Agissez pour récupérer ces fonds.',
        improveRecovery: 'Améliorer le Taux de Récupération',
        recoveryDesc: 'Votre taux de récupération actuel est inférieur à la moyenne. Priorisez les anomalies à fort impact.',
        goodStanding: 'Tout va Bien!',
        goodDesc: 'Aucun problème critique détecté. Continuez à surveiller vos dépenses pour des résultats optimaux.',
        reviewNow: 'Examiner Maintenant',
        viewDetails: 'Voir les Détails',
        viewStrategy: 'Voir la Stratégie',
      },
      invoiceAnalysis: 'Analyse de Facture',
      contractAnalysis: 'Analyse de Contrat',
      overview: 'Aperçu',
      discrepancies: 'Écarts',
      optimizations: 'Optimisations',
      aiInsights: 'Insights IA',
      variance: 'Variance',
      impact: 'Impact',
      savings: 'Économies',
      overcharge: 'surfacturation',
      undercharge: 'sous-facturation',
      noVariance: 'Aucune variance',
      criticalHigh: 'critique/élevé',
      allLowMedium: 'Tous faible/moyen',
      clean: 'Tout est correct',
      potentialOvercharge: 'surfacturation potentielle',
      noImpact: 'Aucun impact',
      opportunities: 'opportunités',
      noOptimizations: 'Aucune optimisation',
      invoice: 'Facture',
      contract: 'Contrat',
      lineItems: 'Lignes',
      matched: 'Lié',
      reanalyze: 'Ré-analyser',
      noContractLinked: 'Aucun Contrat Lié',
      noContractMessage: 'Liez un contrat pour activer l\'analyse complète ligne par ligne.',
      selectContract: 'Sélectionner un Contrat',
      discrepancy: {
        priceOvercharge: 'Surfacturation',
        quantityExcess: 'Quantité Excessive',
        unauthorizedItem: 'Article Non Autorisé',
        missingDiscount: 'Remise Manquante',
        duplicateCharge: 'Double Facturation',
        incorrectTax: 'Taxe Incorrecte',
        contractExpired: 'Contrat Expiré'
      },
      aiPowered: 'Propulsé par IA',
      summary: 'Résumé',
      keyFindings: 'Conclusions Clés',
      priorityActions: 'Actions Prioritaires',
      procurementInsights: 'Insights Achats',
      negotiationTips: 'Conseils de Négociation',
      suggestedScript: 'Script Suggéré',
      confidenceLabel: 'confiance',
      confidence: {
        high: 'Confiance Élevée',
        medium: 'Confiance Moyenne',
        low: 'Confiance Faible',
      },
      invoiced: 'Facturé',
      expected: 'Attendu',
      accept: 'Accepter',
      dispute: 'Contester',
      reject: 'Rejeter',
      acceptSuccess: 'Écart accepté avec succès',
      disputeSuccess: 'Écart contesté avec succès',
      rejectSuccess: 'Écart rejeté avec succès',
      optimization: {
        volumeDiscount: 'Remise Volume',
        renegotiation: 'Renégociation',
        consolidation: 'Consolidation',
        timing: 'Timing',
        alternative: 'Alternative'
      },
      optimizationOpportunities: 'Opportunités d\'Optimisation',
      potentialSavings: 'Économies Potentielles',
      totalPotentialSavings: 'Économies Potentielles Totales',
      perInvoice: 'par facture',
      monthly: 'mensuel',
      annual: 'annuel',
      oneTime: 'unique',
      affectedItems: 'Éléments Affectés',
      linkingInvoice: 'Liaison de Facture',
      matchScore: 'Score de Correspondance',
      vendorMatch: 'Correspondance Fournisseur',
      skuOverlap: 'Chevauchement SKU',
      dateValidity: 'Validité des Dates',
      valueAlignment: 'Alignement des Valeurs',
      bestMatch: 'Meilleure Correspondance',
      noSuggestions: 'Aucune Suggestion',
      noSuggestionsMessage: 'Aucun contrat correspondant trouvé pour cette facture.',
      analyzeWithoutContract: 'Analyser Sans Contrat',
      linkAndAnalyze: 'Lier & Analyser',
      noDiscrepancies: 'Aucun Écart Trouvé',
      noDiscrepanciesMessage: 'Cette facture correspond aux termes du contrat.',
      noOptimizationsMessage: 'Aucune opportunité d\'optimisation identifiée.',
      noInsights: 'Aucun insight supplémentaire disponible.',
      discrepanciesFound: 'Écarts Trouvés',
      issues: 'problèmes',
      totalImpact: 'Impact Total',
      topDiscrepancies: 'Principaux Écarts',
      topOptimizations: 'Principales Optimisations',
      viewAll: 'Voir Tout'
    },

    // Contract (for ContractList and ContractDetail components)
    contract: {
      vendor: 'Fournisseur',
      value: 'Valeur',
      period: 'Période',
      title: 'Titre',
      status: 'Statut',
      expires: 'Expire',
      expired: 'Expiré',
      expiresTomorrow: 'Expire demain',
      daysLeft: 'jours restants',
      autoRenewal: 'Renouvellement auto',
      manualRenewal: 'Renouvellement manuel',
      oneTime: 'Unique',
      renew: 'Renouveler',
      expiringWithin: 'Expire dans',
      noContracts: 'Aucun contrat trouvé',
      noContractsDescription: 'Téléchargez un contrat pour commencer',
      uploadContract: 'Télécharger un contrat',
      uploadNew: 'Télécharger un contrat',
      // Detail page translations
      number: 'Numéro de contrat',
      type: 'Type',
      renewalType: 'Type de renouvellement',
      untitled: 'Contrat sans titre',
      contractInfo: 'Informations du contrat',
      vendorInfo: 'Informations fournisseur',
      vendorName: 'Nom du fournisseur',
      vendorId: 'ID fournisseur',
      vendorContact: 'Contact',
      dates: 'Dates importantes',
      effectiveDate: 'Date d\'effet',
      expirationDate: 'Date d\'expiration',
      daysRemaining: 'Jours restants',
      financial: 'Détails financiers',
      totalValue: 'Valeur totale',
      currency: 'Devise',
      paymentTerms: 'Conditions de paiement',
      pricingSchedule: 'Grille tarifaire',
      noPricingItems: 'Aucun élément tarifaire extrait',
      itemCode: 'Code article',
      description: 'Description',
      unit: 'Unité',
      unitPrice: 'Prix unitaire',
      minQty: 'Qté min',
      maxQty: 'Qté max',
      keyTerms: 'Termes clés',
      linkedInvoices: 'Factures liées',
      noLinkedInvoices: 'Aucune facture liée à ce contrat',
      totalInvoiced: 'Total facturé',
      discrepancies: 'Problèmes & Écarts',
      noDiscrepancies: 'Aucun problème trouvé pour ce contrat',
      recentDiscrepancies: 'Écarts récents',
      recentIssues: 'Problèmes récents',
      contractIssues: 'Problèmes du contrat',
      invoiceDiscrepancies: 'Écarts de factures',
      compareInvoices: 'Comparer les factures',
      expiredAlert: 'Contrat expiré',
      expiredMessage: 'Ce contrat a expiré et doit être renouvelé ou résilié.',
      expiringCritical: 'Expire bientôt',
      expiringNotice: 'Avis d\'expiration',
      daysUntilExpiry: 'jours avant expiration',
      initiateRenewal: 'Initier le renouvellement',
      notFound: 'Contrat non trouvé',
      loadError: 'Échec du chargement du contrat',
      // AI Summary translations
      aiSummary: 'Résumé IA du contrat',
      generatingSummary: 'Génération du résumé IA...',
      aiGenerated: 'Généré par IA',
      // Edit form
      parties: 'Parties',
      buyer: 'Acheteur',
      seller: 'Vendeur/Fournisseur',
      saveSuccess: 'Contrat enregistré avec succès !',
      saveError: "Échec de l'enregistrement du contrat",
      // Additional detail fields
      signedDate: 'Date de signature',
      billingFrequency: 'Fréquence de facturation',
      // Renewal terms
      renewalTerms: 'Conditions de renouvellement',
      noticePeriod: 'Préavis',
      renewalTerm: 'Durée de renouvellement',
      optOutDeadline: 'Date limite de résiliation',
      // Legal terms
      legalTerms: 'Termes juridiques',
      governingLaw: 'Loi applicable',
      disputeResolution: 'Résolution des litiges',
      liabilityLimit: 'Limite de responsabilité',
      terminationClause: 'Clause de résiliation',
      terminationNotice: 'Préavis de résiliation',
      insuranceRequirements: 'Exigences d\'assurance',
      confidentiality: 'Confidentialité',
      nonCompete: 'Non-concurrence',
      customTerms: 'Termes supplémentaires',
    },

    // Vendors
    vendors: {
      name: 'Nom',
      category: 'Catégorie',
      aliases: 'Aussi connu comme',
      totalValue: 'Valeur Totale',
      activeContracts: 'Contrats Actifs',
      contracts: 'contrats',
      noContracts: 'Aucun contrat actif',
      email: 'E-mail',
      phone: 'Téléphone',
      address: 'Adresse',
      search: 'Rechercher',
      searchPlaceholder: 'Rechercher des fournisseurs...',
      noVendors: 'Aucun fournisseur trouvé',
      noVendorsDescription: 'Les fournisseurs sont automatiquement créés lors du téléchargement de contrats ou factures',
      addVendor: 'Ajouter Fournisseur',
      viewContracts: 'Voir Contrats',
      viewInvoices: 'Voir Factures',
      deleteConfirm: 'Êtes-vous sûr de vouloir supprimer ce fournisseur ? Cela ne supprimera pas les contrats ou factures associés.',
      deleteSuccess: 'Fournisseur supprimé avec succès',
      deleteFailed: 'Échec de la suppression du fournisseur. Veuillez réessayer.',
      createHint: 'Les fournisseurs sont automatiquement créés lors du téléchargement de contrats ou factures',
      namePlaceholder: 'Entrez le nom du fournisseur',
      emailPlaceholder: 'fournisseur@exemple.com',
      phonePlaceholder: '+33 1 23 45 67 89',
      nameRequired: 'Le nom du fournisseur est requis',
      createSuccess: 'Fournisseur créé avec succès',
      createFailed: 'Échec de la création du fournisseur',
      editVendor: 'Modifier le fournisseur',
      vendorInfo: 'Informations du fournisseur',
      contactInfo: 'Coordonnées',
      updateSuccess: 'Fournisseur mis à jour avec succès',
      updateFailed: 'Échec de la mise à jour du fournisseur',
      overview: 'Aperçu',
      totalInvoices: 'Total des factures',
      vendorId: 'ID Fournisseur',
      primaryContact: 'Contact principal',
      noContractsLinked: 'Aucun contrat lié à ce fournisseur',
      noInvoicesLinked: 'Aucune facture liée à ce fournisseur',
      notFound: 'Fournisseur non trouvé',
      loadError: 'Impossible de charger les détails du fournisseur',
      categories: {
        technology: 'Technologie',
        services: 'Services',
        supplies: 'Fournitures',
        consulting: 'Conseil',
        other: 'Autre',
      },
    },

    // Login
    login: {
      title: 'Connexion',
      subtitle: 'Accédez à votre plateforme d\'intelligence achats',
      continueWithGoogle: 'Continuer avec Google',
      continueWithMicrosoft: 'Continuer avec Microsoft',
      enterpriseSSO: 'SSO Entreprise',
      enterpriseDomain: 'Domaine de l\'entreprise',
      enterpriseDomainPlaceholder: 'votre-entreprise.com',
      continueWithSSO: 'Continuer avec SSO',
      signInWithEmail: 'Se connecter par e-mail',
      or: 'ou',
      noAccount: 'Pas de compte ?',
      createAccount: 'Créer un compte',
      forgotPassword: 'Mot de passe oublié ?',
      error: {
        authFailed: 'Échec de l\'authentification. Veuillez réessayer.',
        enterpriseDomainRequired: 'Veuillez entrer le domaine de votre entreprise'
      }
    },

    // Auth
    auth: {
      // Login & Register
      signIn: 'Connexion',
      register: 'Créer un compte',
      email: 'E-mail',
      password: 'Mot de passe',
      confirmPassword: 'Confirmer le mot de passe',
      newPassword: 'Nouveau mot de passe',
      fullName: 'Nom complet',
      companyName: 'Nom de l\'entreprise',
      companyNamePlaceholder: 'Entrez le nom de votre entreprise ou organisation',
      forgotPassword: 'Mot de passe oublié ?',
      noAccount: 'Pas de compte ?',
      hasAccount: 'Déjà un compte ?',
      signUp: 'S\'inscrire',
      orContinueWith: 'ou continuer avec',
      continueWithGoogle: 'Continuer avec Google',
      continueWithMicrosoft: 'Continuer avec Microsoft',
      createAccountSubtitle: 'Créez votre compte',
      // Email Verification
      verifyEmail: 'Vérifiez votre e-mail',
      verificationCodeSent: 'Nous avons envoyé un code à 6 chiffres à',
      enterCode: 'Entrez le code de vérification',
      verify: 'Vérifier',
      resendCode: 'Renvoyer le code',
      didntReceiveCode: 'Vous n\'avez pas reçu le code ?',
      codeSent: 'Code envoyé avec succès',
      // Password Reset
      resetPassword: 'Réinitialiser le mot de passe',
      sendResetCode: 'Envoyer le code',
      enterResetCode: 'Entrez le code',
      backToSignIn: 'Retour à la connexion',
      passwordResetSuccess: 'Mot de passe mis à jour avec succès',
      // Processing
      callback: 'Connexion en cours...',
      processing: 'Traitement de l\'authentification...',
      // Errors
      invalidCredentials: 'E-mail ou mot de passe invalide',
      emailExists: 'E-mail déjà enregistré',
      weakPassword: 'Le mot de passe doit contenir au moins 8 caractères',
      passwordMismatch: 'Les mots de passe ne correspondent pas',
      invalidEmail: 'Veuillez entrer un e-mail valide',
      invalidOtp: 'Code invalide ou expiré',
      otpExpired: 'Le code a expiré. Veuillez en demander un nouveau.',
      emailNotFound: 'Aucun compte trouvé avec cet e-mail',
      tooManyAttempts: 'Trop de tentatives. Veuillez réessayer plus tard.',
      sessionExpired: 'Votre session a expiré. Veuillez vous reconnecter.'
    },

    // Checkout
    checkout: {
      verifyingPayment: 'Vérification de votre paiement...',
      paymentSuccess: 'Paiement réussi !',
      completeRegistration: 'Complétez votre inscription pour commencer',
      subscriptionReady: 'Votre abonnement est prêt à être activé',
      activateAccount: 'Activer le compte',
    },

    // Errors
    errors: {
      pageNotFound: 'Page non trouvée',
      somethingWentWrong: 'Une erreur s\'est produite'
    },

    // Optimization
    optimization: {
      title: 'Optimisation des Coûts',
      description: 'Découvrez et mettez en œuvre des opportunités d\'économies dans vos opérations d\'achat.',
      potentialSavings: 'Économies Potentielles',
      implementedSavings: 'Économies Réalisées',
      totalOpportunities: 'Total des Opportunités',
      implemented: 'Implémentées',
      runAnalysis: 'Lancer l\'Analyse',
      analyzing: 'Analyse en cours...',
      analysisComplete: 'Analyse terminée',
      analysisError: 'Échec de l\'analyse',
      fetchError: 'Échec du chargement des opportunités',
      noOpportunities: 'Aucune Opportunité Trouvée',
      noOpportunitiesDesc: 'Lancez une analyse pour découvrir des opportunités d\'économies.',
      affectedVendors: 'Fournisseurs Concernés',
      affectedContracts: 'Contrats Concernés',
      perYear: '/ an',
      reduction: 'réduction',
      accept: 'Accepter',
      reject: 'Rejeter',
      acceptSuccess: 'Opportunité acceptée avec succès',
      acceptError: 'Échec de l\'acceptation',
      rejectSuccess: 'Opportunité rejetée',
      rejectError: 'Échec du rejet',
      summary: 'Résumé',
      recommendation: 'Recommandation',
      currentState: 'État Actuel',
      proposedState: 'État Proposé',
      totalSavings: 'Économies Totales Estimées',
      monthlySpend: 'Mensuel',
      annualSpend: 'Annuel',
      confidence: 'Confiance',
      type: {
        volume_discount: 'Remise sur Volume',
        consolidation: 'Consolidation Fournisseurs',
        renegotiation: 'Renégociation de Contrat',
        elimination: 'Élimination de Service',
        timing: 'Calendrier de Paiement',
        alternative: 'Fournisseur Alternatif'
      },
      status: {
        identified: 'Identifiée',
        evaluating: 'En Évaluation',
        under_review: 'En Révision',
        approved: 'Approuvée',
        in_progress: 'En Cours',
        implemented: 'Implémentée',
        rejected: 'Rejetée',
        deferred: 'Reportée'
      },
      priority: {
        critical: 'Critique',
        high: 'Élevée',
        medium: 'Moyenne',
        low: 'Basse'
      }
    },

    // Reports
    reports: {
      title: 'Rapports',
      subtitle: 'Générer et gérer les rapports de conformité',
      generate: 'Générer un Rapport',
      generateNew: 'Générer un Nouveau Rapport',
      availableTypes: 'Types de Rapports Disponibles',
      generatedReports: 'Rapports Générés',
      reportName: 'Nom du Rapport',
      reportType: 'Type de Rapport',
      reportFormat: 'Format',
      reportTitle: 'Titre (Optionnel)',
      titlePlaceholder: 'Entrez un titre personnalisé pour le rapport',
      description: 'Description',
      descriptionPlaceholder: 'Ajoutez une description pour ce rapport',
      startDate: 'Date de Début',
      endDate: 'Date de Fin',
      format: 'Format',
      status: 'Statut',
      createdAt: 'Créé le',
      size: 'Taille',
      download: 'Télécharger',
      noReports: 'Aucun rapport généré',
      noTypesAvailable: 'Aucun type de rapport disponible',
      selectType: 'Sélectionnez un type de rapport',
      selectTypeError: 'Veuillez sélectionner un type de rapport',
      generating: 'Génération...',
      generateError: 'Échec de la génération du rapport',
      downloadError: 'Échec du téléchargement du rapport',
      deleteError: 'Échec de la suppression du rapport',
      confirmDelete: 'Êtes-vous sûr de vouloir supprimer ce rapport ?'
    },

    // Audit
    audit: {
      title: 'Journal d\'Audit',
      subtitle: 'Suivre tous les changements et actions dans le système',
      noEntries: 'Aucune entrée d\'audit trouvée',
      filterByAction: 'Filtrer par Action',
      filterByResource: 'Filtrer par Type de Ressource',
      filterByActor: 'Filtrer par Acteur',
      filterByDate: 'Filtrer par Date',
      allActions: 'Toutes les Actions',
      allResources: 'Toutes les Ressources',
      success: 'Succès',
      failed: 'Échec',
      actor: 'Acteur',
      action: 'Action',
      resource: 'Ressource',
      timestamp: 'Horodatage',
      details: 'Détails',
      viewChanges: 'Voir les Changements',
      status: 'Statut'
    },

    // Individual discrepancy translations
    discrepancy: {
      addComment: 'Ajouter un commentaire',
      aiAnalysis: 'Analyse IA',
      clause: 'Clause',
      commentPlaceholder: 'Entrez votre commentaire...',
      comparison: 'Comparaison',
      confidence: 'Confiance',
      contract: 'Contrat',
      contractValue: 'Valeur du contrat',
      detected: 'Détecté',
      detectedAt: 'Détecté le',
      difference: 'Différence',
      dismiss: 'Rejeter',
      dismissed: 'Rejeté',
      escalate: 'Escalader',
      id: 'ID',
      impact: 'Impact',
      invoice: 'Facture',
      invoiceValue: 'Valeur de la facture',
      lineItem: 'Ligne',
      linkedDocuments: 'Documents liés',
      loadError: 'Échec du chargement',
      markResolved: 'Marquer comme résolu',
      metadata: 'Métadonnées',
      noDiscrepancies: 'Aucune anomalie trouvée',
      noDiscrepanciesDescription: 'Aucune anomalie n\'a été détectée',
      notFound: 'Anomalie non trouvée',
      overcharge: 'Surfacturation',
      overchargeDetected: 'Surfacturation détectée',
      overchargeExplanation: 'Le montant de la facture dépasse le prix du contrat',
      pending: 'En attente',
      reopen: 'Rouvrir',
      resolutionHistory: 'Historique de résolution',
      resolve: 'Résoudre',
      resolved: 'Résolu',
      resolvedAt: 'Résolu le',
      severity: 'Sévérité',
      startReview: 'Commencer la revue',
      status: 'Statut',
      suggestedAction: 'Action suggérée',
      totalImpact: 'Impact total',
      type: {
        pricing: 'Tarification',
        quantity: 'Quantité',
        terms: 'Conditions',
        dates: 'Dates',
        missingItem: 'Élément manquant',
        unauthorized: 'Non autorisé',
        duplicate: 'Doublon',
        other: 'Autre',
        // CamelCase variants (for UI components)
        missingDiscount: 'Remise manquante',
        incorrectTax: 'Taxe incorrecte',
        priceOvercharge: 'Surfacturation',
        quantityExcess: 'Excès de quantité',
        duplicateCharge: 'Facturation en double',
        unauthorizedItem: 'Article non autorisé',
        contractExpired: 'Contrat expiré',
        // Snake case variants (from API)
        missing_required_field: 'Champ requis manquant',
        price_mismatch: 'Écart de prix',
        quantity_mismatch: 'Écart de quantité',
        date_mismatch: 'Écart de date',
        unauthorized_item: 'Article non autorisé',
        duplicate_charge: 'Facturation en double',
        missing_discount: 'Remise manquante',
        incorrect_tax: 'Taxe incorrecte',
        contract_expired: 'Contrat expiré',
        invalid_format: 'Format invalide',
        missing_signature: 'Signature manquante',
        terms_violation: 'Violation des conditions',
        compliance_issue: 'Problème de conformité',
        overcharge: 'Surfacturation',
        undercharge: 'Sous-facturation',
        // Descriptions
        pricingDesc: 'Le prix diffère des conditions du contrat',
        quantityDesc: 'La quantité ne correspond pas à la commande',
        termsDesc: 'Viole les conditions du contrat',
        datesDesc: 'Date hors période valide',
        missingItemDesc: 'Élément manquant sur la facture',
        unauthorizedDesc: 'Élément non prévu au contrat',
        duplicateDesc: 'Facturation en double détectée',
        otherDesc: 'Autre type d\'anomalie'
      },
      underReview: 'En cours de revue',
      undercharge: 'Sous-facturation',
      underchargeDetected: 'Sous-facturation détectée',
      underchargeExplanation: 'Le montant de la facture est inférieur au prix du contrat'
    },

    // Individual invoice translations
    invoice: {
      amount: 'Montant',
      analyzeAgainstContract: 'Analyser contre le contrat',
      confirmUnlink: 'Êtes-vous sûr de vouloir dissocier ce contrat ?',
      contractLinked: 'Contrat lié avec succès',
      contractUnlinked: 'Contrat dissocié',
      date: 'Date',
      description: 'Description',
      discrepancies: 'Anomalies',
      dueDate: 'Date d\'échéance',
      financialInfo: 'Informations financières',
      invoiceInfo: 'Informations de la facture',
      lineItems: 'Lignes de facture',
      linkAnother: 'Lier un autre',
      linkContract: 'Lier un contrat',
      linkError: 'Échec de liaison du contrat',
      linkedContracts: 'Contrats liés',
      loadError: 'Échec du chargement de la facture',
      noDiscrepancies: 'Aucune anomalie trouvée',
      noLineItems: 'Aucune ligne',
      noLinkedContracts: 'Aucun contrat lié',
      notFound: 'Facture non trouvée',
      number: 'Numéro de facture',
      poNumber: 'Numéro de bon de commande',
      quantity: 'Quantité',
      reanalyze: 'Ré-analyser',
      subtotal: 'Sous-total',
      tax: 'Taxe',
      total: 'Total',
      unitPrice: 'Prix unitaire',
      unlinkError: 'Échec de dissociation du contrat',
      untitled: 'Facture sans titre',
      vendorAddress: 'Adresse du fournisseur',
      vendorId: 'ID du fournisseur',
      vendorInfo: 'Informations du fournisseur',
      vendorName: 'Nom du fournisseur',
      suggestedContracts: 'Contrats suggérés',
      suggestionsIntro: 'Basé sur le fournisseur, la correspondance SKU et les dates de contrat:',
      recommended: 'Recommandé',
      linkAndAnalyze: 'Lier et Analyser',
      edit: 'Modifier la facture',
      basicInfo: 'Informations de base',
      dates: 'Dates',
      sku: 'SKU',
      currency: 'Devise',
      addLineItem: 'Ajouter un article',
      saveSuccess: 'Facture enregistrée avec succès',
      saveError: 'Échec de l\'enregistrement de la facture',
      recalculate: 'Recalculer à partir des lignes',
      receivedDate: 'Date de réception',
      status: 'Statut',
    },

    // Status translations
    status: {
      active: 'Actif',
      analyzed: 'Analysé',
      approved: 'Approuvé',
      disputed: 'Contesté',
      draft: 'Brouillon',
      expired: 'Expiré',
      extracted: 'Extrait',
      paid: 'Payé',
      pending: 'En attente',
      pendingApproval: 'En attente d\'approbation',
      pendingReview: 'En attente de revue',
      rejected: 'Rejeté',
      renewed: 'Renouvelé',
      terminated: 'Résilié'
    },

    // Time translations
    time: {
      daysAgo: 'jours',
      hoursAgo: 'heures',
      justNow: 'À l\'instant',
      minutesAgo: 'minutes'
    },

    // Copilot AI Assistant
    copilot: {
      title: 'Kashf AI',
      placeholder: 'Posez des questions sur vos factures, contrats...',
      send: 'Envoyer',
      newChat: 'Nouvelle conversation',
      history: 'Historique',
      typing: 'Réflexion...',
      error: 'Une erreur s\'est produite. Veuillez réessayer.',
      noConversations: 'Aucune conversation',
      deleteConfirm: 'Supprimer cette conversation ?',
      backToChat: 'Retour au chat',
      suggestions: {
        title: 'Essayez de demander :',
        invoice: 'Quelles factures sont en retard ?',
        contract: 'Quels contrats expirent ce mois-ci ?',
        vendor: 'Quels sont mes principaux fournisseurs ?',
        discrepancy: 'Montrez-moi les écarts non résolus'
      }
    },

  },

  ar: {
    // Navigation
    nav: {
      dashboard: 'لوحة التحكم',
      contracts: 'العقود',
      invoices: 'الفواتير',
      vendors: 'الموردون',
      renewals: 'التجديدات',
      analysis: 'التحليل',
      discrepancies: 'التناقضات',
      integrations: 'التكاملات',
      optimization: 'التحسين',
      audit: 'سجل التدقيق',
      settings: 'الإعدادات',
      admin: 'الإدارة',
      platformAdmin: 'إدارة المنصة',
      adminDashboard: 'لوحة التحكم',
      organizations: 'المؤسسات',
      allUsers: 'جميع المستخدمين',
      platformAnalytics: 'التحليلات',
      plansProviders: 'الخطط والذكاء الاصطناعي',
      billingUsage: 'الفوترة والاستخدام'
    },

    // Common
    common: {
      search: 'البحث في العقود والفواتير...',
      uploadDocument: 'رفع مستند',
      viewAll: 'عرض الكل',
      cancel: 'إلغاء',
      save: 'حفظ',
      saving: 'جاري الحفظ...',
      creating: 'جاري الإنشاء...',
      unsavedChanges: 'لديك تغييرات غير محفوظة. هل أنت متأكد من المغادرة؟',
      saveChanges: 'حفظ التغييرات',
      close: 'إغلاق',
      review: 'مراجعة',
      takeAction: 'اتخاذ إجراء',
      configure: 'تكوين',
      connect: 'اتصال',
      all: 'الكل',
      days: 'أيام',
      month: 'شهر',
      year: 'سنة',
      home: 'الرئيسية',
      pending: 'قيد الانتظار',
      invoice: 'فاتورة',
      contract: 'عقد',
      status: 'الحالة',
      type: 'النوع',
      priority: 'الأولوية',
      retry: 'إعادة المحاولة',
      noData: 'لا تتوفر بيانات',
      regenerate: 'إعادة التوليد',
      viewDetails: 'عرض التفاصيل',
      identified: 'محدد',
      view: 'عرض',
      more: 'المزيد',
      any: 'الكل',
      clear: 'مسح',
      apply: 'تطبيق',
      cardView: 'عرض البطاقات',
      tableView: 'عرض الجدول',
      showing: 'عرض',
      of: 'من',
      items: 'عناصر',
      actions: 'إجراءات',
      loading: 'جاري التحميل...',
      download: 'تحميل',
      edit: 'تعديل',
      editing: 'تعديل',
      backToList: 'العودة للقائمة',
      total: 'المجموع',
      issues: 'مشاكل',
      noIssues: 'لا توجد مشاكل',
      viewAll: 'عرض الكل',
      aiGenerated: 'تم التوليد بالذكاء الاصطناعي',
      create: 'إنشاء',
      selectOption: 'اختر...',
    },

    // Upload
    upload: {
      title: 'رفع مستند',
      subtitle: 'قم برفع ومعالجة عقودك وفواتيرك',
      tabInvoice: 'فاتورة',
      tabContract: 'عقد',
      dragDrop: 'اسحب وأفلت الملفات هنا أو',
      browse: 'تصفح',
      hint: 'الصيغ المدعومة: PDF, PNG, JPG, CSV, XLSX (الحد الأقصى 25 ميجابايت)',
      process: 'معالجة المستندات',
      supportedFormats: 'PDF, PNG, JPG, CSV, XLSX (الحد الأقصى 25 ميجابايت)',
      processing: 'جاري المعالجة...',
      processDocuments: 'معالجة المستندات',
      recentUploads: 'الرفعات الأخيرة',
      noRecentUploads: 'لا توجد رفعات حديثة',
      finishReview: 'إنهاء المراجعة',
      dropHere: 'أفلت الملفات هنا',
      or: 'أو',
      acceptedFormats: 'الصيغ المقبولة',
      maxSize: 'الحد الأقصى',
      clearAll: 'مسح الكل',
      uploadAll: 'رفع الكل',
      complete: 'مكتمل',
      failed: 'فشل',
      description: 'اسحب وأفلت الملفات هنا أو انقر للتصفح',
      errorInvalidType: 'نوع ملف غير صالح',
      errorFileTooLarge: 'الملف كبير جداً',
      modal: {
        processBtn: 'معالجة المستندات',
      },
      status: {
        uploading: 'جاري الرفع...',
        extracting: 'استخراج البيانات...',
        complete: 'مكتمل',
        error: 'خطأ',
      },
      tips: {
        title: 'نصائح للحصول على أفضل النتائج',
        formats: 'استخدم صيغة PDF لأفضل استخراج للنص',
        quality: 'تأكد من أن المستندات واضحة وقابلة للقراءة',
        batch: 'يمكنك رفع عدة ملفات في وقت واحد',
        review: 'راجع البيانات المستخرجة قبل الحفظ',
      },
      success: {
        title: 'اكتمل الرفع!',
        message: 'تمت معالجة مستنداتك بنجاح.',
        uploadMore: 'رفع المزيد',
        viewDocuments: 'عرض المستندات',
      },
    },

    // Limits
    limits: {
      limitReached: 'تم الوصول للحد الأقصى',
      documents: 'مستندات',
      storage: 'التخزين',
      documentLimitMessage: 'لقد وصلت إلى الحد الأقصى الشهري لرفع المستندات. قم بترقية خطتك أو انتظر حتى الشهر القادم لرفع المزيد من المستندات.',
      storageLimitMessage: 'لقد وصلت إلى الحد الأقصى للتخزين. قم بترقية خطتك أو احذف بعض الملفات لتحرير مساحة.',
      currentUsage: 'الاستخدام الحالي',
      options: 'خياراتك',
      viewUsage: 'عرض تفاصيل الاستخدام',
      viewUsageDesc: 'اطلع على استخدامك الحالي وحدودك',
      upgradePlan: 'ترقية خطتك',
      upgradePlanDesc: 'احصل على المزيد من المستندات والتخزين',
      contactSupport: 'تواصل مع الدعم',
      contactSupportDesc: 'احصل على مساعدة لحسابك',
    },

    // Toast notifications
    toast: {
      success: 'نجاح',
      error: 'خطأ',
      warning: 'تحذير',
      info: 'معلومات',
    },

    // Search
    search: {
      noResults: 'لم يتم العثور على نتائج',
      contracts: 'العقود',
      invoices: 'الفواتير',
      vendors: 'الموردون',
      discrepancies: 'التناقضات'
    },

    // Notifications (dropdown)
    notifications: {
      title: 'الإشعارات',
      markAllRead: 'تحديد الكل كمقروء',
      noNotifications: 'لا توجد إشعارات',
      viewSettings: 'إعدادات الإشعارات',
      fetchError: 'فشل في تحميل الإشعارات',
      types: {
        renewal_reminder: 'تذكير بالتجديد',
        discrepancy_alert: 'تنبيه تناقض',
        invoice_processed: 'تمت معالجة الفاتورة',
        optimization_opportunity: 'فرصة توفير',
        system_alert: 'تنبيه النظام'
      }
    },

    // Dashboard
    dashboard: {
      title: 'لوحة التحكم',
      activeContracts: 'العقود النشطة',
      invoicesProcessed: 'الفواتير المعالجة',
      openDiscrepancies: 'التناقضات المفتوحة',
      savingsIdentified: 'الوفورات المحددة',
      upcomingRenewals: 'التجديدات القادمة',
      recentDiscrepancies: 'التناقضات الأخيرة',
      recentActivity: 'النشاط الأخير',
      optimizationOpportunities: 'فرص التحسين',
      accuracyRate: 'معدل الدقة',
      analysisSummary: 'ملخص التحليل',
      contractsMatched: 'العقود المطابقة',
      discrepanciesFound: 'التناقضات المكتشفة',
      discrepancyTypes: 'أنواع التناقضات',
      financialImpact: 'التأثير المالي',
      getStarted: 'ابدأ الآن',
      newAnalysis: 'تحليل جديد',
      noRecentActivity: 'لا يوجد نشاط حديث',
      processingTime: 'وقت المعالجة',
      recommendations: 'التوصيات',
      resolutionStatus: 'حالة الحل',
      severityDistribution: 'توزيع الخطورة',
      topVendors: 'أهم الموردين',
      totalAnalyzed: 'إجمالي التحليلات',
      welcomeMessage: 'مرحباً بك في كشف',
      welcomeTitle: 'مرحباً',
      subtitle: 'لوحة ذكاء المشتريات الخاصة بك'
    },

    // Contracts
    contracts: {
      title: 'العقود',
      addContract: 'إضافة عقد',
      contractId: 'رقم العقد',
      vendor: 'المورد',
      contractTitle: 'العنوان',
      type: 'النوع',
      value: 'القيمة',
      endDate: 'تاريخ الانتهاء',
      status: 'الحالة',
      startDate: 'تاريخ البدء',
      autoRenewal: 'تجديد تلقائي',
      optOutPeriod: 'فترة الإلغاء',
      contractTerms: 'شروط العقد',
      pricingSchedule: 'جدول الأسعار',
      contractInfo: 'معلومات العقد',
      datesRenewal: 'التواريخ والتجديد',
      item: 'البند',
      unitPrice: 'سعر الوحدة',
      quantity: 'الكمية',
      yes: 'نعم',
      no: 'لا',
      active: 'نشط',
      expired: 'منتهي',
      softwareLicense: 'ترخيص برمجي',
      serviceAgreement: 'اتفاقية خدمة',
      infrastructure: 'البنية التحتية',
      allTypes: 'جميع الأنواع'
    },
    
    // Invoices
    invoices: {
      title: 'الفواتير',
      uploadInvoice: 'رفع فاتورة',
      uploadNew: 'رفع فاتورة',
      invoiceId: 'رقم الفاتورة',
      number: 'فاتورة #',
      contract: 'العقد',
      date: 'التاريخ',
      dueDate: 'تاريخ الاستحقاق',
      dateRange: 'نطاق التاريخ',
      amount: 'المبلغ',
      total: 'الإجمالي',
      issues: 'المشاكل',
      issue: 'مشكلة',
      status: 'الحالة',
      vendor: 'المورد',
      subtotal: 'المجموع الفرعي',
      tax: 'الضريبة',
      discrepanciesFound: 'تم العثور على تناقضات',
      noIssues: 'لا يوجد',
      none: 'لا يوجد',
      issuesPlural: 'مشاكل',
      discrepancies: 'التناقضات',
      noInvoices: 'لم يتم العثور على فواتير',
      noInvoicesDescription: 'قم برفع فاتورة للبدء',
      noDocument: 'لا يوجد ملف مستند متاح لهذه الفاتورة',
      overdue: 'متأخر',
      analyze: 'تحليل',
      analyzeSelected: 'تحليل المحدد',
      pending: 'قيد الانتظار',
      needsReview: 'يحتاج مراجعة',
      approved: 'موافق عليه',
      allVendors: 'جميع الموردين',
      withIssues: 'مع مشاكل',
      noIssuesFilter: 'بدون مشاكل',
      // Contract linking
      linkContract: 'ربط عقد',
      linkAnother: 'ربط آخر',
      linkedContracts: 'العقود المرتبطة',
      noLinkedContracts: 'لا توجد عقود مرتبطة بهذه الفاتورة',
      contractLinked: 'تم ربط العقد بنجاح',
      contractUnlinked: 'تم إلغاء ربط العقد بنجاح',
      linkError: 'فشل في ربط العقد',
      unlinkError: 'فشل في إلغاء ربط العقد',
      confirmUnlink: 'هل أنت متأكد من إلغاء ربط هذا العقد؟',
      confirmDelete: 'هل أنت متأكد من حذف هذه الفاتورة؟',
      deleteSuccess: 'تم حذف الفاتورة بنجاح',
      deleteError: 'فشل في حذف الفاتورة'
    },

    // Renewals
    renewals: {
      title: 'التجديدات',
      // Status
      urgent: 'عاجل',
      soon: 'قريباً',
      upcoming: 'قادم',
      completed: 'مكتمل',
      // Time ranges
      within14Days: 'خلال 14 يوم',
      days15to30: '15-30 يوم',
      days31to90: '31-90 يوم',
      overdue: 'متأخر',
      // Types
      autoRenews: 'تجديد تلقائي',
      manual: 'يدوي',
      // Stats
      totalRenewals: 'إجمالي التجديدات',
      urgentRenewals: 'عاجلة',
      dueThisMonth: 'هذا الشهر',
      totalValue: 'القيمة الإجمالية',
      // Filters
      filterByStatus: 'تصفية حسب الحالة',
      filterByType: 'تصفية حسب النوع',
      filterByPriority: 'تصفية حسب الأولوية',
      allStatuses: 'جميع الحالات',
      allTypes: 'جميع الأنواع',
      allPriorities: 'جميع الأولويات',
      // Priority
      priority: 'الأولوية',
      high: 'عالية',
      medium: 'متوسطة',
      low: 'منخفضة',
      critical: 'حرجة',
      // Actions
      viewDetails: 'عرض التفاصيل',
      addNote: 'إضافة ملاحظة',
      setReminder: 'تعيين تذكير',
      markAsReviewed: 'تحديد كمراجع',
      initiateRenewal: 'بدء التجديد',
      scheduleReview: 'جدولة المراجعة',
      renewContract: 'تجديد العقد',
      cancelRenewal: 'إلغاء التجديد',
      // Timeline
      timeline: 'الجدول الزمني',
      renewalDate: 'تاريخ التجديد',
      optOutDeadline: 'آخر موعد للإلغاء',
      reviewDue: 'موعد المراجعة',
      reminderSent: 'تم إرسال التذكير',
      contractStart: 'بداية العقد',
      contractEnd: 'نهاية العقد',
      // Details
      daysUntilRenewal: 'أيام حتى التجديد',
      contractValue: 'قيمة العقد',
      renewalTerms: 'شروط التجديد',
      autoRenewalEnabled: 'التجديد التلقائي مفعل',
      autoRenewalDisabled: 'التجديد التلقائي معطل',
      noticePeriod: 'فترة الإشعار',
      // Notes
      notes: 'الملاحظات',
      addNoteTitle: 'إضافة ملاحظة',
      notePlaceholder: 'أدخل ملاحظتك...',
      noNotes: 'لا توجد ملاحظات بعد',
      // Messages
      noRenewals: 'لم يتم العثور على تجديدات',
      noUpcoming: 'لا توجد تجديدات قادمة',
      renewalSaved: 'تم تحديث التجديد بنجاح',
      noteSaved: 'تمت إضافة الملاحظة بنجاح',
      reminderSet: 'تم تعيين التذكير بنجاح',
      // Notifications
      reminderEmailSent: 'تم إرسال بريد التذكير',
      upcomingRenewalAlert: 'اقتراب موعد تجديد العقد'
    },

    // Renewal (singular - for renewal page module)
    renewal: {
      pageTitle: 'تجديدات العقود',
      pageSubtitle: 'تتبع وإدارة تجديدات العقود القادمة',
      days: 'أيام',
      // Stats
      stats: {
        critical: 'حرج',
        urgent: 'عاجل',
        attention: 'يحتاج انتباه',
        valueAtRisk: 'القيمة المعرضة للخطر',
        needsDecision: 'يحتاج قرار خلال 30 يوم'
      },
      // Filters
      filter: {
        status: 'الحالة',
        priority: 'الأولوية',
        action: 'الإجراء',
        timeframe: 'الفترة الزمنية',
        allStatuses: 'جميع الحالات',
        allPriorities: 'جميع الأولويات',
        allActions: 'جميع الإجراءات'
      },
      // Status
      status: {
        upcoming: 'قادم',
        in_review: 'قيد المراجعة',
        action_taken: 'تم اتخاذ إجراء',
        auto_renewed: 'تجديد تلقائي',
        terminated: 'منتهي',
        expired: 'منتهي الصلاحية'
      },
      // Priority
      priority: {
        critical: 'حرج',
        high: 'عالية',
        medium: 'متوسطة',
        low: 'منخفضة'
      },
      // Action
      action: {
        undecided: 'غير محدد',
        renew: 'تجديد',
        renegotiate: 'إعادة التفاوض',
        terminate: 'إنهاء',
        renewDesc: 'الاستمرار في العقد بالشروط الحالية',
        renegotiateDesc: 'طلب شروط أو أسعار جديدة',
        terminateDesc: 'عدم تجديد هذا العقد'
      },
      selectAction: 'ماذا تريد أن تفعل بهذا العقد؟',
      updateError: 'فشل في تحديث التجديد. يرجى المحاولة مرة أخرى.',
      updateSuccess: 'تم تحديث التجديد بنجاح',
      // Urgency
      urgency: {
        overdue: 'متأخر',
        critical: 'حرج',
        urgent: 'عاجل',
        attention: 'انتباه',
        routine: 'عادي'
      },
      // View
      view: {
        calendar: 'عرض التقويم',
        list: 'عرض القائمة'
      },
      // Card fields
      renewalDate: 'تاريخ التجديد',
      daysLeft: 'الأيام المتبقية',
      contractValue: 'قيمة العقد',
      plannedAction: 'الإجراء المخطط',
      optOutDeadline: 'آخر موعد للإلغاء',
      assignedTo: 'مُعيَّن إلى',
      notes: 'ملاحظات',
      viewDetails: 'عرض التفاصيل',
      takeAction: 'اتخاذ إجراء',
      // Empty state
      empty: {
        title: 'لم يتم العثور على تجديدات',
        description: 'قم بتحميل العقود مع تواريخ انتهاء الصلاحية لرؤية التجديدات هنا.'
      },
      // Sync
      syncFromContracts: 'مزامنة من العقود',
      // Timeline
      timeline: {
        title: 'الجدول الزمني',
        today: 'اليوم',
        optOutDeadline: 'آخر موعد للإلغاء',
        optOutDescription: 'آخر يوم للانسحاب من التجديد التلقائي',
        renewalDate: 'تاريخ التجديد',
        notificationSent: 'تم إرسال الإشعار',
        beforeDeadline: 'قبل الموعد النهائي',
        note: 'ملاحظة',
        ago: 'مضت',
        in: 'خلال'
      }
    },

    // Analysis
    analysis: {
      title: 'التحليل',
      // Dashboard
      dashboardTitle: 'تحليلات المشتريات',
      dashboardSubtitle: 'رؤى فورية حول أداء المشتريات الخاصة بك',
      totalSpendAnalyzed: 'إجمالي الإنفاق المحلل',
      discrepanciesDetected: 'التناقضات المكتشفة',
      potentialOvercharges: 'الرسوم الزائدة المحتملة',
      savingsRecovered: 'الوفورات المستردة',
      vsLastMonth: 'مقارنة بالشهر الماضي',
      ofSpend: 'من الإنفاق',
      recoveryRate: 'معدل الاسترداد',
      spendByCategory: 'الإنفاق حسب الفئة',
      discrepancyInsights: 'رؤى التناقضات',
      total: 'الإجمالي',
      topIssueTypes: 'أهم أنواع المشاكل',
      vendorPerformance: 'أداء الموردين',
      noVendorData: 'لا تتوفر بيانات الموردين حتى الآن',
      accuracy: 'الدقة',
      resolutionProgress: 'تقدم الحل',
      resolved: 'تم الحل',
      reviewPending: 'مراجعة',
      pendingItems: 'عناصر معلقة',
      recentActivity: 'النشاط الأخير',
      noRecentActivity: 'لا يوجد نشاط حديث',
      poweredByAI: 'مدعوم بالذكاء الاصطناعي',
      runAnalysis: 'بدء التحليل',
      category: {
        software: 'البرمجيات والتراخيص',
        services: 'الخدمات المهنية',
        infrastructure: 'البنية التحتية',
        supplies: 'اللوازم المكتبية',
        other: 'أخرى',
      },
      type: {
        pricing: 'رسوم زائدة',
        quantity: 'عدم تطابق الكمية',
        terms: 'مخالفة العقد',
        unauthorized: 'رسوم غير مصرح بها',
      },
      status: {
        pending: 'في انتظار المراجعة',
        underReview: 'قيد المراجعة',
        resolved: 'تم الحل',
        dismissed: 'مرفوض',
        clean: 'متوافق',
        hasDiscrepancies: 'يوجد تناقضات',
        needsReview: 'يحتاج مراجعة',
        noContract: 'بدون عقد',
      },
      insight: {
        criticalAlert: 'مشاكل حرجة تتطلب الانتباه',
        criticalDesc: 'تناقضات حرجة تحتاج مراجعة فورية لتجنب الخسائر المالية.',
        savingsOpportunity: 'فرصة توفير محددة',
        savingsDesc: 'من الرسوم الزائدة المحتملة المكتشفة. اتخذ إجراءً لاسترداد هذه الأموال.',
        improveRecovery: 'تحسين معدل الاسترداد',
        recoveryDesc: 'معدل الاسترداد الحالي أقل من المتوسط. فكر في إعطاء الأولوية للتناقضات ذات التأثير العالي.',
        goodStanding: 'كل شيء على ما يرام!',
        goodDesc: 'لم يتم اكتشاف مشاكل حرجة. استمر في مراقبة إنفاق المشتريات للحصول على نتائج مثلى.',
        reviewNow: 'مراجعة الآن',
        viewDetails: 'عرض التفاصيل',
        viewStrategy: 'عرض الاستراتيجية',
      },
      discrepancies: 'التناقضات',
      optimizations: 'التحسينات',
      discrepancySummary: 'ملخص التناقضات',
      openIssues: 'المشاكل المفتوحة',
      totalImpact: 'إجمالي التأثير',
      optimizationPotential: 'إمكانية التحسين',
      viewOptimizations: 'عرض فرص توفير التكاليف',
      opportunities: 'الفرص',
      potentialSavings: 'الوفورات المحتملة',
      review: 'مراجعة',
      takeAction: 'اتخاذ إجراء',
      vendors: 'الموردون',
      pricing: 'التسعير',
      quantity: 'الكمية',
      unauthorized: 'غير مصرح',
      consolidation: 'التوحيد',
      volume: 'الحجم',
      renegotiation: 'إعادة التفاوض',
      // Procurement Analysis
      overview: 'نظرة عامة',
      aiInsights: 'تحليلات الذكاء الاصطناعي',
      topDiscrepancies: 'أهم التناقضات',
      topOptimizations: 'أهم التحسينات',
      viewAll: 'عرض الكل',
      variance: 'الفرق',
      discrepancyCount: 'التناقضات المكتشفة',
      financialImpact: 'التأثير المالي',
      savingsOpportunity: 'فرصة التوفير',
      critical: 'حرج',
      criticalIssues: 'مشاكل حرجة',
      potentialOvercharge: 'زيادة محتملة في السعر',
      aiSummary: 'ملخص الذكاء الاصطناعي',
      keyFindings: 'النتائج الرئيسية',
      priorityActions: 'الإجراءات ذات الأولوية',
      negotiationTips: 'نصائح التفاوض',
      marketInsights: 'رؤى السوق',
      noDiscrepancies: 'لم يتم العثور على تناقضات',
      noDiscrepanciesMessage: 'خبر سار! لم يتم اكتشاف أي تناقضات بين هذه الفاتورة والعقد.',
      lineItem: 'البند',
      expected: 'المتوقع',
      actual: 'الفعلي',
      accept: 'قبول',
      dispute: 'اعتراض',
      reject: 'رفض',
      acceptSuccess: 'تم قبول التناقض بنجاح',
      disputeSuccess: 'تم بدء الاعتراض على التناقض',
      rejectSuccess: 'تم رفض التناقض',
      optimizationOpportunities: 'فرص التحسين',
      noOptimizations: 'لم يتم العثور على تحسينات',
      noOptimizationsMessage: 'لم يتم تحديد أي فرص تحسين لهذه الفاتورة.',
      totalPotentialSavings: 'إجمالي الوفورات المحتملة',
      affectedItems: 'العناصر المتأثرة',
      lineItems: 'بنود',
      perInvoice: 'لكل فاتورة',
      monthly: 'شهري',
      annual: 'سنوي',
      oneTime: 'مرة واحدة',
      confidenceLabel: 'الثقة',
      optimization: {
        volumeDiscount: 'خصم الكمية',
        renegotiation: 'إعادة التفاوض',
        consolidation: 'التوحيد',
        timing: 'توقيت الدفع',
        alternative: 'خيار بديل'
      },
      selectContract: 'اختر عقداً',
      linkingInvoice: 'ربط الفاتورة',
      bestMatch: 'أفضل تطابق',
      matchScore: 'درجة التطابق',
      vendorMatch: 'تطابق المورد',
      skuOverlap: 'تداخل SKU',
      dateValidity: 'صلاحية التاريخ',
      valueAlignment: 'توافق القيمة',
      noSuggestions: 'لا توجد اقتراحات للعقود',
      noSuggestionsMessage: 'لم يتم العثور على عقود مطابقة لهذه الفاتورة.',
      analyzeWithoutContract: 'تحليل بدون عقد',
      linkAndAnalyze: 'ربط وتحليل',
      invoiceAnalysis: 'تحليل الفاتورة',
      reanalyze: 'إعادة التحليل',
      export: 'تصدير',
      backToInvoice: 'العودة للفاتورة',
      linkedContract: 'العقد المرتبط',
      noLinkedContract: 'لا يوجد عقد مرتبط',
      selectContractToLink: 'اختر عقداً للربط',
      status: {
        clean: 'مطابق',
        has_discrepancies: 'يوجد تناقضات',
        needs_review: 'يحتاج مراجعة',
        no_contract: 'بدون عقد'
      },
      // Contract Analysis
      analyze: 'تحليل',
      analyzeAll: 'تحليل الكل',
      invoicesAnalyzed: 'الفواتير المحللة',
      totalInvoiceValue: 'إجمالي قيمة الفواتير',
      totalDiscrepancies: 'إجمالي التناقضات',
      totalVariance: 'إجمالي الفرق',
      vendorPerformance: 'أداء المورد',
      totalInvoices: 'إجمالي الفواتير',
      invoicesWithIssues: 'الفواتير ذات المشاكل',
      errorRate: 'معدل الخطأ',
      totalOvercharges: 'إجمالي الزيادات',
      commonIssues: 'المشاكل الشائعة',
      keyRecommendations: 'التوصيات الرئيسية',
      discrepancyBreakdown: 'تفصيل التناقضات',
      bySeverity: 'حسب الشدة',
      byType: 'حسب النوع',
      invoiceAnalyses: 'تحليلات الفواتير',
      invoices: 'الفواتير',
      noInvoices: 'لا توجد فواتير',
      noInvoicesLinked: 'لا توجد فواتير مرتبطة بهذا العقد.'
    },

    // Severity levels
    severity: {
      critical: 'حرج',
      high: 'عالي',
      medium: 'متوسط',
      low: 'منخفض'
    },

    // Discrepancy types
    discrepancy_type: {
      price_mismatch: 'اختلاف السعر',
      quantity_mismatch: 'اختلاف الكمية',
      unauthorized_item: 'عنصر غير مصرح',
      missing_discount: 'خصم مفقود',
      duplicate_charge: 'رسوم مكررة',
      tax_error: 'خطأ ضريبي',
      other: 'أخرى'
    },

    // Upload Modal
    upload: {
      title: 'تحميل مستند',
      subtitle: 'قم بتحميل ومعالجة فواتيرك وعقودك',
      tabInvoice: 'فاتورة',
      tabContract: 'عقد',
      dragDrop: 'اسحب وأفلت الملفات هنا أو',
      browse: 'تصفح',
      hint: 'يدعم PDF، PNG، JPG، CSV، XLSX (الحد الأقصى 25 ميجابايت)',
      supportedFormats: 'يدعم PDF، PNG، JPG، CSV، XLSX (الحد الأقصى 25 ميجابايت)',
      process: 'معالجة المستندات',
      processDocuments: 'معالجة المستندات',
      processing: 'جاري المعالجة...',
      cancel: 'إلغاء',
      noRecentUploads: 'لا توجد تحميلات حديثة',
      recentUploads: 'التحميلات الأخيرة',
      clearAll: 'مسح الكل',
      uploadAll: 'تحميل الكل',
      dropHere: 'أفلت الملفات هنا',
      or: 'أو',
      acceptedFormats: 'الصيغ المقبولة',
      maxSize: 'الحد الأقصى',
      description: 'اسحب وأفلت الملفات هنا أو انقر للتصفح',
      complete: 'مكتمل',
      failed: 'فشل',
      errorInvalidType: 'نوع ملف غير صالح',
      errorFileTooLarge: 'الملف كبير جداً',
      finishReview: 'إنهاء المراجعة',
      modal: {
        processBtn: 'معالجة المستندات'
      },
      status: {
        uploading: 'جاري التحميل...',
        extracting: 'جاري استخراج البيانات...',
        complete: 'مكتمل',
        error: 'خطأ'
      },
      tips: {
        title: 'نصائح',
        formats: 'يدعم صيغ PDF، PNG، JPG، CSV و XLSX',
        quality: 'تأكد من أن المستندات واضحة وقابلة للقراءة',
        batch: 'يمكنك تحميل عدة ملفات في وقت واحد',
        review: 'راجع البيانات المستخرجة قبل الحفظ'
      },
      success: {
        title: 'تم التحميل بنجاح',
        message: 'تمت معالجة مستنداتك بنجاح',
        uploadMore: 'تحميل المزيد',
        viewDocuments: 'عرض المستندات'
      }
    },

    // Integrations
    integrations: {
      title: 'التكاملات',
      connected: 'متصل',
      notConnected: 'غير متصل',
      disconnected: 'منفصل',
      lastSync: 'آخر مزامنة',
      neverSynced: 'لم تتم المزامنة',
      documentsSynced: 'مستندات تمت مزامنتها',
      connectToSync: 'اتصل للمزامنة التلقائية',
      configure: 'تكوين',
      connect: 'اتصال',
      disconnect: 'قطع الاتصال',
      enable: 'تفعيل',
      disable: 'تعطيل',
      enabled: 'مفعّل',
      disabled: 'معطّل',
      testing: 'جاري الاختبار...',
      creating: 'جاري الإنشاء...',
      import: 'استيراد',
      documentStorage: 'تخزين المستندات',
      procurement: 'المشتريات',
      eSignature: 'التوقيع الإلكتروني',
      status: {
        idle: 'خامل',
        running: 'قيد التشغيل',
        completed: 'مكتمل',
        failed: 'فشل',
        error: 'خطأ'
      },
      services: {
        dropbox: 'دروبوكس',
        box: 'بوكس',
        googleDrive: 'جوجل درايف',
        oneDrive: 'ون درايف',
        sharePoint: 'شيربوينت',
        erpOracle: 'أوراكل ERP',
        erpSap: 'ساب',
        docusign: 'دوكيوساين',
        adobeSign: 'أدوبي ساين'
      }
    },

    // Settings
    settings: {
      title: 'الإعدادات',
      subtitle: 'إدارة حسابك وتفضيلاتك',
      profile: 'الملف الشخصي',
      notifications: 'الإشعارات',
      preferences: 'التفضيلات',
      team: 'الفريق',
      security: 'الأمان',
      auditTrail: 'سجل التدقيق',
      saveSuccess: 'تم حفظ الإعدادات بنجاح',
      saveError: 'فشل في حفظ الإعدادات',
      nameHint: 'يتم إدارة الاسم من قبل مزود الهوية الخاص بك',
      customizeExperience: 'خصص تجربتك',
      // Profile section
      profileInformation: 'معلومات الملف الشخصي',
      profileInfo: 'معلومات الملف الشخصي',
      profileDesc: 'قم بتحديث تفاصيل حسابك وتفضيلاتك.',
      updateAccountDetails: 'تفاصيل حسابك',
      fullName: 'الاسم الكامل',
      email: 'البريد الإلكتروني',
      role: 'الدور',
      timezone: 'المنطقة الزمنية',
      dateFormat: 'تنسيق التاريخ',
      defaultView: 'العرض الافتراضي',
      // Nested profile
      'profile.title': 'الملف الشخصي',
      'profile.subtitle': 'إدارة معلومات حسابك وتفضيلاتك',
      'profile.accountInfo': 'معلومات الحساب',
      'profile.accountInfoDesc': 'تفاصيل حسابك الأساسية',
      'profile.displayName': 'الاسم المعروض',
      'profile.email': 'البريد الإلكتروني',
      'profile.role': 'الدور',
      'profile.organization': 'المؤسسة',
      'profile.memberSince': 'عضو منذ',
      'profile.lastLogin': 'آخر تسجيل دخول',
      'profile.preferences': 'التفضيلات',
      'profile.preferencesDesc': 'خصص تجربتك',
      'profile.language': 'اللغة',
      'profile.timezone': 'المنطقة الزمنية',
      'profile.dateFormat': 'تنسيق التاريخ',
      'profile.currency': 'العملة',
      'profile.defaultView': 'العرض الافتراضي',
      'profile.fetchError': 'فشل في تحميل الملف الشخصي',
      'profile.saveSuccess': 'تم حفظ الملف الشخصي بنجاح',
      'profile.saveError': 'فشل في حفظ الملف الشخصي',
      // Notification preferences
      notificationPreferences: 'تفضيلات الإشعارات',
      notificationPrefs: 'تفضيلات الإشعارات',
      notificationDesc: 'اختر كيف تريد أن يتم إعلامك بالأحداث المهمة.',
      chooseNotifications: 'اختر كيف تريد أن يتم إعلامك',
      emailNotifications: 'إشعارات البريد الإلكتروني',
      emailNotificationsDesc: 'تلقي الإشعارات عبر البريد الإلكتروني',
      inAppNotifications: 'إشعارات داخل التطبيق',
      inAppNotificationsDesc: 'عرض الإشعارات في التطبيق',
      renewalReminders: 'تذكيرات التجديد',
      renewalRemindersDesc: 'الحصول على إشعارات حول تجديدات العقود القادمة',
      discrepancyAlerts: 'تنبيهات التناقضات',
      discrepancyAlertsDesc: 'الحصول على إشعارات حول تناقضات الفواتير',
      weeklyDigest: 'الملخص الأسبوعي',
      weeklyDigestDesc: 'تلقي ملخص أسبوعي للنشاط',
      // Nested notifications
      'notifications': {
        title: 'الإشعارات',
        subtitle: 'قم بتكوين كيفية إعلامك',
        channels: 'قنوات الإشعارات',
        channelsDescription: 'اختر كيف تريد تلقي الإشعارات',
        types: 'أنواع الإشعارات',
        typesDescription: 'حدد الإشعارات التي تريد تلقيها',
        email: 'إشعارات البريد الإلكتروني',
        emailDescription: 'تلقي الإشعارات عبر البريد الإلكتروني',
        inApp: 'إشعارات داخل التطبيق',
        inAppDescription: 'عرض الإشعارات في التطبيق',
        renewalReminders: 'تذكيرات التجديد',
        renewalRemindersDescription: 'الحصول على إشعارات حول تجديدات العقود القادمة',
        discrepancyAlerts: 'تنبيهات التناقضات',
        discrepancyAlertsDescription: 'الحصول على إشعارات عند اكتشاف تناقضات في الفواتير',
        weeklyDigest: 'الملخص الأسبوعي',
        weeklyDigestDescription: 'تلقي ملخص أسبوعي للنشاط',
        alertDays: 'أيام التنبيه',
        alertDaysDescription: 'اختر عدد الأيام قبل التجديد لتلقي التنبيهات',
        fetchError: 'فشل في تحميل تفضيلات الإشعارات',
        saveSuccess: 'تم حفظ تفضيلات الإشعارات بنجاح',
        saveError: 'فشل في حفظ تفضيلات الإشعارات'
      },
      // Language and currency
      language: 'اللغة',
      languageDesc: 'اختر لغتك المفضلة',
      currency: 'العملة',
      currencyDesc: 'اختر العملة لعرض المبالغ',
      currencyOptions: {
        // العملات العالمية الرئيسية
        EUR: 'يورو (EUR)',
        USD: 'دولار أمريكي (USD)',
        GBP: 'جنيه إسترليني (GBP)',
        JPY: 'ين ياباني (JPY)',
        CHF: 'فرنك سويسري (CHF)',
        CAD: 'دولار كندي (CAD)',
        AUD: 'دولار أسترالي (AUD)',
        NZD: 'دولار نيوزيلندي (NZD)',
        // العملات الآسيوية
        CNY: 'يوان صيني (CNY)',
        INR: 'روبية هندية (INR)',
        SGD: 'دولار سنغافوري (SGD)',
        HKD: 'دولار هونغ كونغ (HKD)',
        KRW: 'وون كوري جنوبي (KRW)',
        THB: 'بات تايلاندي (THB)',
        MYR: 'رينغيت ماليزي (MYR)',
        IDR: 'روبية إندونيسية (IDR)',
        PHP: 'بيزو فلبيني (PHP)',
        VND: 'دونغ فيتنامي (VND)',
        TWD: 'دولار تايواني (TWD)',
        // الشرق الأوسط وشمال أفريقيا
        MAD: 'درهم مغربي (MAD)',
        SAR: 'ريال سعودي (SAR)',
        AED: 'درهم إماراتي (AED)',
        TND: 'دينار تونسي (TND)',
        EGP: 'جنيه مصري (EGP)',
        QAR: 'ريال قطري (QAR)',
        KWD: 'دينار كويتي (KWD)',
        BHD: 'دينار بحريني (BHD)',
        OMR: 'ريال عماني (OMR)',
        JOD: 'دينار أردني (JOD)',
        ILS: 'شيكل إسرائيلي (ILS)',
        // العملات الأوروبية
        SEK: 'كرونة سويدية (SEK)',
        NOK: 'كرونة نرويجية (NOK)',
        DKK: 'كرونة دنماركية (DKK)',
        PLN: 'زلوتي بولندي (PLN)',
        CZK: 'كرونة تشيكية (CZK)',
        HUF: 'فورنت مجري (HUF)',
        RON: 'ليو روماني (RON)',
        TRY: 'ليرة تركية (TRY)',
        RUB: 'روبل روسي (RUB)',
        UAH: 'هريفنيا أوكرانية (UAH)',
        // الأمريكتان
        MXN: 'بيزو مكسيكي (MXN)',
        BRL: 'ريال برازيلي (BRL)',
        ARS: 'بيزو أرجنتيني (ARS)',
        CLP: 'بيزو تشيلي (CLP)',
        COP: 'بيزو كولومبي (COP)',
        PEN: 'سول بيروفي (PEN)',
        // أفريقيا
        ZAR: 'راند جنوب أفريقي (ZAR)',
        NGN: 'نايرا نيجيري (NGN)',
        KES: 'شلن كيني (KES)',
        GHS: 'سيدي غاني (GHS)',
        XOF: 'فرنك غرب أفريقي (XOF)',
        XAF: 'فرنك وسط أفريقي (XAF)'
      },
      // Roles
      procurementManager: 'مدير المشتريات',
      complianceOfficer: 'مسؤول الامتثال',
      admin: 'مدير المؤسسة',
      orgAdmin: 'مدير المؤسسة',
      viewer: 'مشاهد',
      roles: {
        admin: 'مدير المؤسسة',
        orgAdmin: 'مدير المؤسسة',
        procurementManager: 'مدير المشتريات',
        complianceOfficer: 'مسؤول الامتثال',
        viewer: 'مشاهد'
      },
      // Security section
      securityDesc: 'إدارة كلمة المرور وإعدادات الأمان',
      changePassword: 'تغيير كلمة المرور',
      currentPassword: 'كلمة المرور الحالية',
      newPassword: 'كلمة المرور الجديدة',
      confirmPassword: 'تأكيد كلمة المرور الجديدة',
      enterCurrentPassword: 'أدخل كلمة المرور الحالية',
      enterNewPassword: 'أدخل كلمة المرور الجديدة',
      confirmNewPassword: 'أكد كلمة المرور الجديدة',
      updatePassword: 'تحديث كلمة المرور',
      updating: 'جاري التحديث...',
      passwordMinLength: '8 أحرف على الأقل',
      passwordUppercase: 'حرف كبير واحد',
      passwordLowercase: 'حرف صغير واحد',
      passwordNumber: 'رقم واحد',
      passwordMismatch: 'كلمات المرور غير متطابقة',
      currentPasswordRequired: 'كلمة المرور الحالية مطلوبة',
      newPasswordRequired: 'كلمة المرور الجديدة مطلوبة',
      passwordTooShort: 'يجب أن تحتوي كلمة المرور على 8 أحرف على الأقل',
      passwordNeedsUppercase: 'يجب أن تحتوي كلمة المرور على حرف كبير واحد على الأقل',
      passwordNeedsLowercase: 'يجب أن تحتوي كلمة المرور على حرف صغير واحد على الأقل',
      passwordNeedsNumber: 'يجب أن تحتوي كلمة المرور على رقم واحد على الأقل',
      passwordRequirements: 'يجب أن تحتوي كلمة المرور على 8 أحرف على الأقل',
      passwordUpdated: 'تم تحديث كلمة المرور بنجاح',
      passwordUpdateFailed: 'فشل في تحديث كلمة المرور',
      accountSecurity: 'أمان الحساب',
      lastPasswordChange: 'آخر تغيير لكلمة المرور',
      twoFactorAuth: 'المصادقة الثنائية',
      notAvailable: 'المعلومات غير متوفرة',
      comingSoon: 'قريباً',
      // Administration section
      administration: 'الإدارة',
      users: {
        title: 'إدارة المستخدمين',
        subtitle: 'إدارة المستخدمين في مؤسستك',
        addUser: 'إضافة مستخدم',
        searchPlaceholder: 'البحث عن المستخدمين...',
        allStatuses: 'جميع الحالات',
        allRoles: 'جميع الأدوار',
        empty: 'لم يتم العثور على مستخدمين',
        emptyDesc: 'أضف مستخدمين إلى مؤسستك للبدء.',
        user: 'المستخدم',
        users: 'مستخدمين',
        role: 'الدور',
        created: 'تم الإنشاء',
        lastLogin: 'آخر تسجيل دخول',
        deactivate: 'إلغاء التنشيط',
        reactivate: 'إعادة التنشيط',
        changeRole: 'تغيير الدور',
        email: 'البريد الإلكتروني',
        displayName: 'الاسم المعروض',
        password: 'كلمة المرور',
        optionalPassword: 'اتركه فارغاً لطلب إعداد كلمة المرور',
        passwordHint: 'إذا تم توفيره، يجب أن يكون 8 أحرف على الأقل',
        resetPassword: 'إعادة تعيين كلمة المرور',
        resetPasswordSuccess: 'تم إرسال بريد إعادة تعيين كلمة المرور بنجاح',
        resetPasswordFailed: 'فشل في إرسال بريد إعادة تعيين كلمة المرور',
        deactivateReason: 'أدخل سبب إلغاء التنشيط:',
        confirmReactivate: 'هل أنت متأكد من إعادة تنشيط هذا المستخدم؟',
        confirmResetPassword: 'إرسال بريد إعادة تعيين كلمة المرور لهذا المستخدم؟'
      },
      invitations: {
        title: 'الدعوات',
        subtitle: 'دعوة المستخدمين للانضمام إلى مؤسستك',
        sendInvite: 'إرسال دعوة',
        sendInvitation: 'إرسال دعوة',
        pendingInvitations: 'الدعوات المعلقة',
        noInvitations: 'لا توجد دعوات',
        noInvitationsDesc: 'أرسل دعوات لإضافة مستخدمين إلى مؤسستك.',
        invitations: 'دعوات',
        resend: 'إعادة الإرسال',
        cancel: 'إلغاء',
        expired: 'منتهية الصلاحية',
        accepted: 'مقبولة',
        role: 'الدور',
        invitedBy: 'دعوة من',
        sentOn: 'أُرسلت في',
        expires: 'تنتهي',
        message: 'الرسالة',
        resentTimes: 'أُعيد إرسالها',
        times: 'مرة/مرات',
        email: 'عنوان البريد الإلكتروني',
        personalMessage: 'رسالة شخصية',
        messagePlaceholder: 'أضف رسالة شخصية (اختياري)',
        messageHint: 'رسالة اختيارية لتضمينها في بريد الدعوة',
        confirmResend: 'إعادة إرسال هذه الدعوة؟',
        confirmCancel: 'إلغاء هذه الدعوة؟'
      },
      registrations: {
        title: 'طلبات التسجيل',
        subtitle: 'الموافقة على طلبات تسجيل المستخدمين أو رفضها',
        pendingApprovals: 'الموافقات المعلقة',
        pendingRequests: 'طلبات معلقة',
        requests: 'طلبات',
        noRequests: 'لا توجد طلبات تسجيل',
        noRequestsDesc: 'ستظهر طلبات التسجيل هنا عندما يقوم المستخدمون بالتسجيل.',
        approve: 'موافقة',
        reject: 'رفض',
        rejectionReason: 'سبب الرفض',
        reason: 'السبب',
        confirmApprove: 'الموافقة على طلب التسجيل هذا؟',
        rejectReason: 'أدخل سبب الرفض:'
      },
      organization: {
        title: 'إعدادات المنظمة',
        subtitle: 'إدارة تفضيلات مؤسستك',
        general: 'عام',
        generalSettings: 'الإعدادات العامة',
        generalDesc: 'المعلومات الأساسية للمنظمة',
        name: 'اسم المنظمة',
        defaultCurrency: 'العملة الافتراضية',
        timezone: 'المنطقة الزمنية',
        onboarding: 'تسجيل المستخدمين',
        onboardingSettings: 'إعدادات تسجيل المستخدمين',
        onboardingDesc: 'تكوين كيفية انضمام المستخدمين إلى مؤسستك',
        subscription: 'الاشتراك',
        allowSelfRegistration: 'السماح بالتسجيل الذاتي',
        allowSelfRegistrationDesc: 'السماح للمستخدمين بطلب الانضمام إلى مؤسستك',
        requireApproval: 'طلب موافقة المسؤول',
        requireApprovalDesc: 'التسجيلات الجديدة تتطلب موافقة المسؤول قبل التنشيط',
        allowedDomains: 'نطاقات البريد الإلكتروني المسموح بها',
        allowedDomainsHint: 'قائمة مفصولة بفواصل. اتركها فارغة للسماح بجميع النطاقات.',
        defaultRole: 'الدور الافتراضي للمستخدمين الجدد',
        defaultRoleHint: 'الدور المعين للمستخدمين الجدد الذين ينضمون عبر الدعوة أو التسجيل الذاتي',
        invitationExpiry: 'انتهاء صلاحية الدعوة (أيام)',
        invitationExpiryHint: 'عدد الأيام قبل انتهاء صلاحية الدعوات (1-30)',
        subscriptionDetails: 'تفاصيل الاشتراك',
        subscriptionDesc: 'خطتك الحالية واستخدامك',
        currentPlan: 'الخطة الحالية',
        maxContracts: 'الحد الأقصى للعقود',
        maxUsers: 'الحد الأقصى للمستخدمين',
        maxStorage: 'الحد الأقصى للتخزين',
        upgradeMessage: 'تحتاج إلى مزيد من السعة؟ اتصل بنا لترقية خطتك.',
        contactSales: 'اتصل بالمبيعات',
        saveSuccess: 'تم حفظ إعدادات المنظمة بنجاح',
        onboardingSaveSuccess: 'تم حفظ إعدادات التسجيل بنجاح'
      },
      usage: {
        title: 'الاستخدام والفوترة',
        subtitle: 'راقب استهلاك موارد منظمتك',
        overview: 'نظرة عامة',
        history: 'السجل',
        byUser: 'حسب المستخدم',
        daysRemaining: 'يوم متبقي',
        overageEnabled: 'فوترة التجاوز مفعلة لمنظمتك. سيتم فرض رسوم إضافية على الاستخدام الذي يتجاوز الحدود.',
        aiTokens: 'رموز الذكاء الاصطناعي',
        storage: 'التخزين',
        documents: 'المستندات',
        extractions: 'الاستخراجات',
        tokens: 'الرموز',
        remaining: 'متبقي',
        overage: 'تجاوز',
        expectedOverageCharges: 'رسوم التجاوز المتوقعة',
        totalOverage: 'إجمالي التجاوز المتوقع',
        period: 'الفترة',
        overageCost: 'التجاوز',
        user: 'المستخدم',
        share: 'الحصة',
        noHistory: 'لا يوجد سجل استخدام متاح',
        noUserData: 'لا توجد بيانات استخدام للمستخدمين',
        status: {
          ok: 'جيد',
          warning: 'تحذير',
          blocked: 'محظور',
          overage: 'تجاوز'
        }
      },
      billing: {
        title: 'الفوترة والاشتراك',
        subtitle: 'إدارة اشتراكك وتفاصيل الدفع',
        currentPlan: 'الخطة الحالية',
        planDetails: 'تفاصيل الخطة',
        planIncludes: 'تشمل الخطة',
        billingPeriod: 'فترة الفوترة',
        nextBilling: 'تاريخ الفاتورة التالية',
        paymentMethod: 'طريقة الدفع',
        usageSummary: 'ملخص الاستخدام',
        usageThisPeriod: 'الاستخدام هذه الفترة',
        changePlan: 'تغيير الخطة',
        manageBilling: 'إدارة الفوترة',
        managePayments: 'إدارة المدفوعات',
        managePaymentsDesc: 'تحديث طرق الدفع، عرض الفواتير، وإدارة اشتراكك',
        plans: 'الخطط المتاحة',
        selectPlan: 'اختر الخطة',
        monthly: 'شهري',
        annual: 'سنوي',
        annualSavings: 'وفر مع الفوترة السنوية',
        currentPlanBadge: 'الحالية',
        recommended: 'موصى به',
        popular: 'الأكثر شعبية',
        upgrade: 'ترقية',
        downgrade: 'تخفيض',
        perMonth: '/شهر',
        perYear: '/سنة',
        billedAnnually: 'يُفوتر سنوياً',
        billedMonthly: 'يُفوتر شهرياً',
        confirmUpgrade: 'تأكيد الترقية',
        confirmDowngrade: 'تأكيد التخفيض',
        upgradeSuccess: 'تم ترقية خطتك بنجاح',
        upgradeError: 'فشل في ترقية الخطة. يرجى المحاولة مرة أخرى.',
        portalError: 'فشل في فتح بوابة الفوترة. يرجى المحاولة مرة أخرى.',
        loadError: 'فشل في تحميل تفاصيل الاشتراك',
        free: 'مجاني',
        unlimited: 'غير محدود',
        cancelSubscription: 'إلغاء الاشتراك',
        reactivateSubscription: 'إعادة تفعيل الاشتراك',
        subscriptionActive: 'نشط',
        subscriptionCanceled: 'ملغى',
        subscriptionPastDue: 'متأخر',
        cancelsOn: 'ينتهي في',
        viewDetails: 'عرض التفاصيل',
        managePayment: 'إدارة الدفع',
        choosePlan: 'اختر خطة',
        save20: 'وفر 20%',
        contactSales: 'اتصل بالمبيعات',
        select: 'اختر',
        upgradeCanceled: 'تم إلغاء ترقية الخطة.',
        cancelPending: 'سيتم إلغاء اشتراكك في نهاية فترة الفوترة الحالية.',
        noSubscription: 'لا يوجد اشتراك نشط',
        features: {
          contracts: 'العقود',
          invoices: 'الفواتير',
          users: 'المستخدمين',
          storage: 'التخزين',
          aiExtractions: 'استخراجات الذكاء الاصطناعي',
          support: 'الدعم',
          apiAccess: 'الوصول لـ API',
          customIntegrations: 'التكاملات المخصصة'
        }
      }
    },

    // Admin (Super Admin)
    admin: {
      superAdmin: 'المسؤول الأعلى',
      dashboard: {
        title: 'إدارة المنصة'
      },
      stats: {
        totalOrganizations: 'إجمالي المنظمات',
        totalUsers: 'إجمالي المستخدمين',
        activeOrgs: 'المنظمات النشطة',
        suspendedOrgs: 'معلقة',
        pendingApprovals: 'الموافقات المعلقة',
        acrossAllPlans: 'عبر جميع الخطط',
        platformWide: 'على مستوى المنصة',
        currentlyActive: 'نشطة حالياً',
        awaitingReview: 'في انتظار المراجعة',
        requiresAttention: 'تتطلب الاهتمام'
      },
      analytics: {
        title: 'تحليلات المنصة',
        subtitle: 'نظرة عامة على استخدام المنصة والمقاييس',
        orgsByPlan: 'المنظمات حسب الخطة',
        usersByRole: 'المستخدمون حسب الدور'
      },
      recentOrganizations: 'المنظمات الحديثة',
      noOrganizations: 'لا توجد منظمات بعد',
      created: 'تم الإنشاء',
      quickActions: 'الإجراءات السريعة',
      actions: {
        createOrg: 'إنشاء منظمة',
        createOrgDesc: 'إعداد منظمة جديدة',
        manageUsers: 'إدارة المستخدمين',
        manageUsersDesc: 'عرض جميع مستخدمي المنصة',
        reviewPending: 'مراجعة المعلقة',
        reviewPendingDesc: 'الموافقة على الطلبات المعلقة',
        billing: 'الفوترة والاستخدام',
        billingDesc: 'عرض فوترة المنصة'
      },
      organizations: {
        title: 'المنظمات',
        create: 'منظمة جديدة',
        searchPlaceholder: 'البحث عن المنظمات...',
        allStatuses: 'جميع الحالات',
        empty: 'لم يتم العثور على منظمات',
        name: 'المنظمة',
        plan: 'الخطة',
        status: 'الحالة',
        users: 'المستخدمون',
        created: 'تم الإنشاء',
        activate: 'تنشيط',
        suspend: 'تعليق',
        createTitle: 'إنشاء منظمة',
        details: 'تفاصيل المنظمة',
        changePlan: 'تغيير الخطة',
        changePlanTitle: 'تغيير خطة الاشتراك',
        changingPlanFor: 'تغيير الخطة لـ',
        editTitle: 'تعديل المنظمة',
        basicInfo: 'المعلومات الأساسية',
        domain: 'النطاق',
        website: 'الموقع الإلكتروني',
        phone: 'الهاتف',
        industry: 'القطاع',
        taxId: 'الرقم الضريبي',
        description: 'الوصف',
        descriptionPlaceholder: 'وصف موجز للمنظمة...',
        address: 'العنوان',
        street: 'الشارع',
        city: 'المدينة',
        state: 'الولاية / المقاطعة',
        postalCode: 'الرمز البريدي',
        country: 'الدولة',
        primaryContact: 'جهة الاتصال الرئيسية',
        contactName: 'الاسم',
        contactRole: 'المنصب',
        contactEmail: 'البريد الإلكتروني',
        contactPhone: 'الهاتف',
        companyPhone: 'هاتف الشركة',
        noContact: 'لا توجد معلومات اتصال',
        subscription: 'الاشتراك',
        meta: 'البيانات الوصفية',
        updated: 'تم التحديث',
        logo: 'الشعار',
        uploadLogo: 'رفع الشعار',
        removeLogo: 'إزالة',
        logoHint: 'JPG، PNG، SVG أو WebP. الحد الأقصى 2 ميجابايت.',
        invalidFileType: 'نوع ملف غير صالح. يرجى رفع صورة بصيغة JPG أو PNG أو SVG أو WebP.',
        fileTooLarge: 'الملف كبير جداً. الحد الأقصى للحجم 2 ميجابايت.',
        selectIndustry: 'اختر القطاع...'
      },
      users: {
        title: 'جميع المستخدمين',
        searchPlaceholder: 'البحث بالاسم أو البريد الإلكتروني...',
        allStatuses: 'جميع الحالات',
        allRoles: 'جميع الأدوار',
        empty: 'لم يتم العثور على مستخدمين',
        user: 'المستخدم',
        organization: 'المنظمة',
        role: 'الدور',
        status: 'الحالة',
        lastLogin: 'آخر تسجيل دخول',
        userDetails: 'تفاصيل المستخدم',
        profile: 'الملف الشخصي',
        displayName: 'الاسم المعروض',
        email: 'البريد الإلكتروني',
        orgName: 'الاسم',
        orgId: 'المعرف',
        activity: 'النشاط',
        created: 'تم الإنشاء',
        activate: 'تنشيط',
        suspend: 'تعليق',
        confirmActivate: 'هل أنت متأكد من رغبتك في تنشيط هذا المستخدم؟',
        activateFailed: 'فشل في تنشيط المستخدم',
        suspendReason: 'يرجى تقديم سبب لتعليق هذا المستخدم:',
        suspendFailed: 'فشل في تعليق المستخدم',
        resetPassword: 'إعادة تعيين كلمة المرور',
        confirmResetPassword: 'إرسال بريد إلكتروني لإعادة تعيين كلمة المرور لهذا المستخدم؟',
        resetPasswordSuccess: 'تم إرسال بريد إلكتروني لإعادة تعيين كلمة المرور بنجاح',
        resetPasswordFailed: 'فشل في إرسال بريد إلكتروني لإعادة تعيين كلمة المرور',
        editUser: 'تعديل المستخدم',
        noOrganization: '-- لا توجد منظمة (قيد الانتظار) --',
        createUser: 'إنشاء مستخدم',
        password: 'كلمة المرور',
        passwordPlaceholder: 'اتركها فارغة لإرسال بريد ترحيبي',
        requiredFields: 'البريد الإلكتروني والاسم المعروض مطلوبان',
        createFailed: 'فشل في إنشاء المستخدم',
        superAdminNoOrg: 'المسؤولون الأعلى لديهم وصول على مستوى المنصة ولا ينتمون إلى منظمة محددة.'
      },
      confirmSuspend: 'هل أنت متأكد من رغبتك في تعليق هذه المنظمة؟',
      plans: {
        title: 'الخطط ومزودي الذكاء الاصطناعي',
        free: 'مجاني',
        starter: 'مبتدئ',
        professional: 'احترافي',
        enterprise: 'مؤسسي',
        contracts: 'عقود',
        users: 'مستخدمين',
        new: 'خطة جديدة',
        create: 'إنشاء خطة جديدة',
        edit: 'تعديل الخطة',
        empty: 'لم يتم إنشاء خطط بعد',
        createFirst: 'أنشئ خطتك الأولى',
        confirmDelete: 'هل أنت متأكد من رغبتك في حذف هذه الخطة؟'
      },
      providers: {
        title: 'مزودي الذكاء الاصطناعي',
        new: 'إضافة مزود',
        create: 'إضافة مزود ذكاء اصطناعي',
        edit: 'تعديل المزود',
        empty: 'لم يتم تكوين مزودي ذكاء اصطناعي بعد',
        createFirst: 'أضف مزودك الأول',
        confirmDelete: 'هل أنت متأكد من رغبتك في حذف هذا المزود؟'
      },
      billing: {
        title: 'الفوترة والاستخدام',
        period: 'الفترة',
        overview: 'نظرة عامة',
        organizations: 'المنظمات',
        subscriptionRevenue: 'إيرادات الاشتراك',
        thisPeriod: 'هذه الفترة',
        estimatedCosts: 'التكاليف المقدرة للذكاء الاصطناعي',
        aiProvider: 'تكاليف مزود الذكاء الاصطناعي',
        grossMargin: 'الهامش الإجمالي',
        margin: 'هامش',
        totalTokens: 'إجمالي الرموز المستخدمة',
        acrossAllOrgs: 'عبر جميع المنظمات',
        usageSummary: 'ملخص استخدام المنصة',
        totalDocuments: 'المستندات المعالجة',
        totalExtractions: 'استخراجات الذكاء الاصطناعي',
        totalStorage: 'التخزين المستخدم',
        activeOrgs: 'المنظمات النشطة',
        overageOrgs: 'المنظمات التي تجاوزت الحد',
        noOverage: 'لا توجد منظمات تجاوزت الحد',
        noOverageDesc: 'جميع المنظمات ضمن حدود خططها',
        organization: 'المنظمة',
        overageTokens: 'الرموز المتجاوزة',
        overageCost: 'تكلفة التجاوز',
        noOrgs: 'لم يتم العثور على منظمات',
        noOrgsDesc: 'لا توجد بيانات استخدام لهذه الفترة',
        allOrgs: 'جميع المنظمات',
        export: 'تصدير',
        tokenUsage: 'استخدام الرموز',
        documents: 'المستندات',
        storage: 'التخزين',
        overage: 'التجاوز',
        cost: 'التكلفة التقديرية',
        manage: 'إدارة',
        billingSettings: 'إعدادات الفوترة',
        enableOverage: 'تفعيل التجاوز',
        enableOverageDesc: 'السماح للمنظمة بتجاوز حدود الخطة مع رسوم إضافية',
        customPricing: 'تسعير مخصص',
        optional: 'اختياري',
        customPricingHint: 'اتركه فارغاً لاستخدام الأسعار الافتراضية',
        pricePerToken: 'السعر لكل 1000 رمز',
        pricePerGb: 'السعر لكل جيجابايت تخزين',
        pricePerDoc: 'السعر لكل مستند',
        pricePerExtraction: 'السعر لكل استخراج',
        billingNotes: 'ملاحظات الفوترة',
        billingNotesPlaceholder: 'ملاحظات داخلية حول هذه المنظمة...'
      },
      suspendReason: 'يرجى إدخال سبب تعليق هذه المنظمة:'
    },

    // Upload
    upload: {
      title: 'رفع المستندات',
      subtitle: 'رفع الفواتير والعقود للاستخراج والتحليل بالذكاء الاصطناعي',
      invoice: 'فاتورة',
      contract: 'عقد',
      dragDrop: 'اسحب وأفلت الملفات هنا أو',
      browse: 'تصفح',
      supportedFormats: 'PDF, PNG, JPG, CSV, XLSX (حد أقصى 25 ميجابايت)',
      processing: 'جاري المعالجة...',
      processDocuments: 'معالجة المستندات',
      // Batch upload
      batchTitle: 'رفع دفعة',
      selectedFiles: 'الملفات المحددة',
      uploadAll: 'رفع الكل',
      clearAll: 'مسح الكل',
      uploading: 'جاري الرفع...',
      uploadComplete: 'اكتمل الرفع',
      uploadFailed: 'فشل الرفع',
      removeFile: 'إزالة الملف',
      cancelUpload: 'إلغاء الرفع',
      // Recent uploads
      recentUploads: 'الرفع الأخير',
      noRecentUploads: 'لا يوجد رفع حديث',
      // Tips
      tips: {
        title: 'نصائح الرفع',
        formats: 'الصيغ المدعومة: PDF, PNG, JPG, CSV, XLSX',
        quality: 'للحصول على أفضل النتائج، استخدم مستندات واضحة وعالية الدقة',
        batch: 'يمكنك رفع عدة ملفات في وقت واحد للمعالجة الجماعية',
        review: 'راجع البيانات المستخرجة وقم بإجراء التصحيحات قبل الحفظ'
      },
      // Modal
      modal: {
        processBtn: 'معالجة المستندات'
      },
      // Status
      status: {
        uploading: 'جاري الرفع...',
        extracting: 'جاري استخراج البيانات...',
        complete: 'اكتمل',
        error: 'خطأ'
      },
      // Extraction preview
      extractionPreview: 'معاينة الاستخراج',
      extractedFields: 'الحقول المستخرجة',
      needsReview: 'يحتاج مراجعة',
      highConfidence: 'ثقة عالية',
      lowConfidence: 'ثقة منخفضة',
      mediumConfidence: 'ثقة متوسطة',
      overallConfidence: 'الثقة الإجمالية',
      editField: 'تعديل الحقل',
      saveCorrections: 'حفظ التصحيحات',
      noCorrections: 'لا توجد تصحيحات للحفظ',
      correctionsSaved: 'تم حفظ التصحيحات بنجاح',
      // Confidence
      confidence: 'الثقة',
      confidenceHigh: 'عالية',
      confidenceMedium: 'متوسطة',
      confidenceLow: 'منخفضة',
      // Review flow
      finishReview: 'إنهاء المراجعة',
      reviewingDocument: 'مراجعة المستند',
      // Success
      success: {
        title: 'اكتمل الرفع!',
        message: 'تمت معالجة مستنداتك بنجاح.',
        uploadMore: 'رفع المزيد',
        viewDocuments: 'عرض المستندات'
      },
      // Errors
      errors: {
        invalidFormat: 'صيغة ملف غير صالحة',
        fileTooLarge: 'الملف يتجاوز الحجم الأقصى (25 ميجابايت)',
        uploadFailed: 'فشل الرفع. يرجى المحاولة مرة أخرى.',
        extractionFailed: 'فشل الاستخراج لهذا المستند'
      },
      // Status messages for modal
      status: {
        uploading: 'جاري الرفع...',
        extracting: 'جاري استخراج البيانات...',
        complete: 'مكتمل',
        error: 'خطأ'
      },
      modal: {
        processBtn: 'معالجة المستندات'
      }
    },

    // Activity
    activity: {
      invoiceUploaded: 'تم رفع الفاتورة',
      analysisCompleted: 'اكتمل التحليل',
      discrepancyResolved: 'تم حل التناقض',
      contractRenewed: 'تم تجديد العقد',
      contractUploaded: 'تم رفع العقد',
      discrepanciesFound: 'تناقضات في الدفعة'
    },
    
    // Status
    status: {
      active: 'نشط',
      expired: 'منتهي',
      pending: 'قيد الانتظار',
      review: 'مراجعة',
      approved: 'موافق عليه',
      draft: 'مسودة',
      pendingReview: 'قيد المراجعة',
      pendingApproval: 'قيد الموافقة',
      renewed: 'مجدد',
      terminated: 'منتهي'
    },

    // Analysis (Procurement Analysis)
    analysis: {
      // Dashboard
      dashboardTitle: 'تحليلات المشتريات',
      dashboardSubtitle: 'رؤى فورية حول أداء المشتريات الخاصة بك',
      totalSpendAnalyzed: 'إجمالي الإنفاق المحلل',
      discrepanciesDetected: 'التناقضات المكتشفة',
      potentialOvercharges: 'الرسوم الزائدة المحتملة',
      savingsRecovered: 'الوفورات المستردة',
      vsLastMonth: 'مقارنة بالشهر الماضي',
      ofSpend: 'من الإنفاق',
      recoveryRate: 'معدل الاسترداد',
      spendByCategory: 'الإنفاق حسب الفئة',
      discrepancyInsights: 'رؤى التناقضات',
      total: 'الإجمالي',
      topIssueTypes: 'أهم أنواع المشاكل',
      vendorPerformance: 'أداء الموردين',
      noVendorData: 'لا تتوفر بيانات الموردين حتى الآن',
      accuracy: 'الدقة',
      resolutionProgress: 'تقدم الحل',
      resolved: 'تم الحل',
      reviewPending: 'مراجعة',
      pendingItems: 'عناصر معلقة',
      recentActivity: 'النشاط الأخير',
      noRecentActivity: 'لا يوجد نشاط حديث',
      poweredByAI: 'مدعوم بالذكاء الاصطناعي',
      runAnalysis: 'بدء التحليل',
      category: {
        software: 'البرمجيات والتراخيص',
        services: 'الخدمات المهنية',
        infrastructure: 'البنية التحتية',
        supplies: 'اللوازم المكتبية',
        other: 'أخرى',
      },
      type: {
        pricing: 'رسوم زائدة',
        quantity: 'عدم تطابق الكمية',
        terms: 'مخالفة العقد',
        unauthorized: 'رسوم غير مصرح بها',
      },
      status: {
        pending: 'في انتظار المراجعة',
        underReview: 'قيد المراجعة',
        resolved: 'تم الحل',
        dismissed: 'مرفوض',
        clean: 'متوافق',
        hasDiscrepancies: 'يوجد تناقضات',
        needsReview: 'يحتاج مراجعة',
        noContract: 'بدون عقد',
      },
      insight: {
        criticalAlert: 'مشاكل حرجة تتطلب الانتباه',
        criticalDesc: 'تناقضات حرجة تحتاج مراجعة فورية لتجنب الخسائر المالية.',
        savingsOpportunity: 'فرصة توفير محددة',
        savingsDesc: 'من الرسوم الزائدة المحتملة المكتشفة. اتخذ إجراءً لاسترداد هذه الأموال.',
        improveRecovery: 'تحسين معدل الاسترداد',
        recoveryDesc: 'معدل الاسترداد الحالي أقل من المتوسط. فكر في إعطاء الأولوية للتناقضات ذات التأثير العالي.',
        goodStanding: 'كل شيء على ما يرام!',
        goodDesc: 'لم يتم اكتشاف مشاكل حرجة. استمر في مراقبة إنفاق المشتريات للحصول على نتائج مثلى.',
        reviewNow: 'مراجعة الآن',
        viewDetails: 'عرض التفاصيل',
        viewStrategy: 'عرض الاستراتيجية',
      },
      invoiceAnalysis: 'تحليل الفاتورة',
      contractAnalysis: 'تحليل العقد',
      overview: 'نظرة عامة',
      discrepancies: 'التناقضات',
      optimizations: 'التحسينات',
      aiInsights: 'رؤى الذكاء الاصطناعي',
      variance: 'الفرق',
      impact: 'التأثير',
      savings: 'التوفير',
      overcharge: 'مبالغة في السعر',
      undercharge: 'أقل من السعر',
      noVariance: 'لا يوجد فرق',
      criticalHigh: 'حرج/مرتفع',
      allLowMedium: 'كل منخفض/متوسط',
      clean: 'كل شيء صحيح',
      potentialOvercharge: 'مبالغة محتملة',
      noImpact: 'لا تأثير',
      opportunities: 'فرص',
      noOptimizations: 'لا توجد تحسينات',
      invoice: 'الفاتورة',
      contract: 'العقد',
      lineItems: 'البنود',
      matched: 'مرتبط',
      reanalyze: 'إعادة التحليل',
      noContractLinked: 'لا يوجد عقد مرتبط',
      noContractMessage: 'اربط عقداً لتفعيل التحليل الكامل سطراً بسطر.',
      selectContract: 'اختر العقد',
      discrepancy: {
        priceOvercharge: 'مبالغة في السعر',
        quantityExcess: 'كمية زائدة',
        unauthorizedItem: 'بند غير مصرح',
        missingDiscount: 'خصم ناقص',
        duplicateCharge: 'رسوم مكررة',
        incorrectTax: 'ضريبة خاطئة',
        contractExpired: 'عقد منتهي'
      },
      aiPowered: 'مدعوم بالذكاء الاصطناعي',
      summary: 'الملخص',
      keyFindings: 'النتائج الرئيسية',
      priorityActions: 'الإجراءات ذات الأولوية',
      procurementInsights: 'رؤى المشتريات',
      negotiationTips: 'نصائح التفاوض',
      suggestedScript: 'النص المقترح',
      confidenceLabel: 'الثقة',
      confidence: {
        high: 'ثقة عالية',
        medium: 'ثقة متوسطة',
        low: 'ثقة منخفضة',
      },
      invoiced: 'المفوتر',
      expected: 'المتوقع',
      accept: 'قبول',
      dispute: 'اعتراض',
      reject: 'رفض',
      acceptSuccess: 'تم قبول التناقض بنجاح',
      disputeSuccess: 'تم الاعتراض على التناقض بنجاح',
      rejectSuccess: 'تم رفض التناقض بنجاح',
      optimization: {
        volumeDiscount: 'خصم الكمية',
        renegotiation: 'إعادة التفاوض',
        consolidation: 'الدمج',
        timing: 'التوقيت',
        alternative: 'البديل'
      },
      optimizationOpportunities: 'فرص التحسين',
      potentialSavings: 'التوفير المحتمل',
      totalPotentialSavings: 'إجمالي التوفير المحتمل',
      perInvoice: 'لكل فاتورة',
      monthly: 'شهري',
      annual: 'سنوي',
      oneTime: 'مرة واحدة',
      affectedItems: 'العناصر المتأثرة',
      linkingInvoice: 'ربط الفاتورة',
      matchScore: 'نقاط التطابق',
      vendorMatch: 'تطابق المورد',
      skuOverlap: 'تداخل الرمز',
      dateValidity: 'صلاحية التاريخ',
      valueAlignment: 'توافق القيمة',
      bestMatch: 'أفضل تطابق',
      noSuggestions: 'لا توجد اقتراحات',
      noSuggestionsMessage: 'لم يتم العثور على عقود مطابقة لهذه الفاتورة.',
      analyzeWithoutContract: 'تحليل بدون عقد',
      linkAndAnalyze: 'ربط وتحليل',
      noDiscrepancies: 'لا توجد تناقضات',
      noDiscrepanciesMessage: 'هذه الفاتورة تتوافق مع شروط العقد.',
      noOptimizationsMessage: 'لم يتم تحديد فرص تحسين.',
      noInsights: 'لا توجد رؤى إضافية.',
      discrepanciesFound: 'التناقضات الموجودة',
      issues: 'مشاكل',
      totalImpact: 'التأثير الإجمالي',
      topDiscrepancies: 'أهم التناقضات',
      topOptimizations: 'أهم التحسينات',
      viewAll: 'عرض الكل'
    },

    // Contract (for ContractList and ContractDetail components)
    contract: {
      vendor: 'المورد',
      value: 'القيمة',
      period: 'الفترة',
      title: 'العنوان',
      status: 'الحالة',
      expires: 'ينتهي',
      expired: 'منتهي',
      expiresTomorrow: 'ينتهي غداً',
      daysLeft: 'أيام متبقية',
      autoRenewal: 'تجديد تلقائي',
      manualRenewal: 'تجديد يدوي',
      oneTime: 'مرة واحدة',
      renew: 'تجديد',
      expiringWithin: 'ينتهي خلال',
      noContracts: 'لا توجد عقود',
      noContractsDescription: 'قم برفع عقد للبدء',
      uploadContract: 'رفع عقد',
      uploadNew: 'رفع عقد',
      // Detail page translations
      number: 'رقم العقد',
      type: 'النوع',
      renewalType: 'نوع التجديد',
      untitled: 'عقد بدون عنوان',
      contractInfo: 'معلومات العقد',
      vendorInfo: 'معلومات المورد',
      vendorName: 'اسم المورد',
      vendorId: 'معرف المورد',
      vendorContact: 'جهة الاتصال',
      dates: 'التواريخ المهمة',
      effectiveDate: 'تاريخ السريان',
      expirationDate: 'تاريخ الانتهاء',
      daysRemaining: 'الأيام المتبقية',
      financial: 'التفاصيل المالية',
      totalValue: 'القيمة الإجمالية',
      currency: 'العملة',
      paymentTerms: 'شروط الدفع',
      pricingSchedule: 'جدول الأسعار',
      noPricingItems: 'لم يتم استخراج عناصر تسعير',
      itemCode: 'رمز الصنف',
      description: 'الوصف',
      unit: 'الوحدة',
      unitPrice: 'سعر الوحدة',
      minQty: 'الحد الأدنى',
      maxQty: 'الحد الأقصى',
      keyTerms: 'البنود الرئيسية',
      linkedInvoices: 'الفواتير المرتبطة',
      noLinkedInvoices: 'لا توجد فواتير مرتبطة بهذا العقد',
      totalInvoiced: 'إجمالي الفواتير',
      discrepancies: 'المشاكل والتناقضات',
      noDiscrepancies: 'لا توجد مشاكل في هذا العقد',
      recentDiscrepancies: 'التناقضات الأخيرة',
      recentIssues: 'المشاكل الأخيرة',
      contractIssues: 'مشاكل العقد',
      invoiceDiscrepancies: 'تناقضات الفواتير',
      compareInvoices: 'مقارنة الفواتير',
      expiredAlert: 'العقد منتهي',
      expiredMessage: 'انتهى هذا العقد ويجب تجديده أو إنهاؤه.',
      expiringCritical: 'ينتهي قريباً',
      expiringNotice: 'إشعار الانتهاء',
      daysUntilExpiry: 'أيام حتى الانتهاء',
      initiateRenewal: 'بدء التجديد',
      notFound: 'العقد غير موجود',
      loadError: 'فشل تحميل العقد',
      // AI Summary translations
      aiSummary: 'ملخص العقد بالذكاء الاصطناعي',
      generatingSummary: 'جارٍ إنشاء الملخص بالذكاء الاصطناعي...',
      aiGenerated: 'تم التوليد بالذكاء الاصطناعي',
      // Parties
      parties: 'الأطراف',
      buyer: 'المشتري',
      seller: 'البائع',
      saveSuccess: 'تم حفظ العقد بنجاح!',
      saveError: 'فشل في حفظ العقد',
      // Additional detail fields
      signedDate: 'تاريخ التوقيع',
      billingFrequency: 'تكرار الفوترة',
      // Renewal terms
      renewalTerms: 'شروط التجديد',
      noticePeriod: 'فترة الإشعار',
      renewalTerm: 'مدة التجديد',
      optOutDeadline: 'آخر موعد للإلغاء',
      // Legal terms
      legalTerms: 'الشروط القانونية',
      governingLaw: 'القانون الحاكم',
      disputeResolution: 'حل النزاعات',
      liabilityLimit: 'حد المسؤولية',
      terminationClause: 'شرط الإنهاء',
      terminationNotice: 'إشعار الإنهاء',
      insuranceRequirements: 'متطلبات التأمين',
      confidentiality: 'السرية',
      nonCompete: 'عدم المنافسة',
      customTerms: 'شروط إضافية',
    },

    // Vendors
    vendors: {
      name: 'الاسم',
      category: 'الفئة',
      aliases: 'معروف أيضاً باسم',
      totalValue: 'القيمة الإجمالية',
      activeContracts: 'العقود النشطة',
      contracts: 'عقود',
      noContracts: 'لا توجد عقود نشطة',
      email: 'البريد الإلكتروني',
      phone: 'الهاتف',
      address: 'العنوان',
      search: 'بحث',
      searchPlaceholder: 'البحث عن الموردين...',
      noVendors: 'لم يتم العثور على موردين',
      noVendorsDescription: 'يتم إنشاء الموردين تلقائياً عند رفع العقود أو الفواتير',
      addVendor: 'إضافة مورد',
      viewContracts: 'عرض العقود',
      viewInvoices: 'عرض الفواتير',
      deleteConfirm: 'هل أنت متأكد من حذف هذا المورد؟ لن يؤدي هذا إلى حذف العقود أو الفواتير المرتبطة.',
      deleteSuccess: 'تم حذف المورد بنجاح',
      deleteFailed: 'فشل في حذف المورد. يرجى المحاولة مرة أخرى.',
      createHint: 'يتم إنشاء الموردين تلقائياً عند رفع العقود أو الفواتير',
      namePlaceholder: 'أدخل اسم المورد',
      emailPlaceholder: 'supplier@example.com',
      phonePlaceholder: '+966 12 345 6789',
      nameRequired: 'اسم المورد مطلوب',
      createSuccess: 'تم إنشاء المورد بنجاح',
      createFailed: 'فشل في إنشاء المورد',
      editVendor: 'تعديل المورد',
      vendorInfo: 'معلومات المورد',
      contactInfo: 'معلومات الاتصال',
      updateSuccess: 'تم تحديث المورد بنجاح',
      updateFailed: 'فشل في تحديث المورد',
      overview: 'نظرة عامة',
      totalInvoices: 'إجمالي الفواتير',
      vendorId: 'رقم المورد',
      primaryContact: 'جهة الاتصال الرئيسية',
      noContractsLinked: 'لا توجد عقود مرتبطة بهذا المورد',
      noInvoicesLinked: 'لا توجد فواتير مرتبطة بهذا المورد',
      notFound: 'لم يتم العثور على المورد',
      loadError: 'فشل في تحميل تفاصيل المورد',
      categories: {
        technology: 'التكنولوجيا',
        services: 'الخدمات',
        supplies: 'المستلزمات',
        consulting: 'الاستشارات',
        other: 'أخرى',
      },
    },

    // Login
    login: {
      title: 'تسجيل الدخول',
      subtitle: 'الوصول إلى منصة ذكاء المشتريات الخاصة بك',
      continueWithGoogle: 'المتابعة مع Google',
      continueWithMicrosoft: 'المتابعة مع Microsoft',
      enterpriseSSO: 'تسجيل دخول المؤسسة',
      enterpriseDomain: 'نطاق الشركة',
      enterpriseDomainPlaceholder: 'شركتك.com',
      continueWithSSO: 'المتابعة مع SSO',
      signInWithEmail: 'تسجيل الدخول بالبريد الإلكتروني',
      or: 'أو',
      noAccount: 'ليس لديك حساب؟',
      createAccount: 'إنشاء حساب',
      forgotPassword: 'نسيت كلمة المرور؟',
      error: {
        authFailed: 'فشل المصادقة. يرجى المحاولة مرة أخرى.',
        enterpriseDomainRequired: 'يرجى إدخال نطاق شركتك'
      }
    },

    // Auth
    auth: {
      // Login & Register
      signIn: 'تسجيل الدخول',
      register: 'إنشاء حساب',
      email: 'البريد الإلكتروني',
      password: 'كلمة المرور',
      confirmPassword: 'تأكيد كلمة المرور',
      newPassword: 'كلمة المرور الجديدة',
      fullName: 'الاسم الكامل',
      companyName: 'اسم الشركة',
      companyNamePlaceholder: 'أدخل اسم شركتك أو مؤسستك',
      forgotPassword: 'نسيت كلمة المرور؟',
      noAccount: 'ليس لديك حساب؟',
      hasAccount: 'لديك حساب بالفعل؟',
      signUp: 'التسجيل',
      orContinueWith: 'أو المتابعة مع',
      continueWithGoogle: 'المتابعة مع Google',
      continueWithMicrosoft: 'المتابعة مع Microsoft',
      createAccountSubtitle: 'أنشئ حسابك',
      // Email Verification
      verifyEmail: 'تحقق من بريدك الإلكتروني',
      verificationCodeSent: 'أرسلنا رمزاً مكوناً من 6 أرقام إلى',
      enterCode: 'أدخل رمز التحقق',
      verify: 'تحقق',
      resendCode: 'إعادة إرسال الرمز',
      didntReceiveCode: 'لم تستلم الرمز؟',
      codeSent: 'تم إرسال الرمز بنجاح',
      // Password Reset
      resetPassword: 'إعادة تعيين كلمة المرور',
      sendResetCode: 'إرسال الرمز',
      enterResetCode: 'أدخل الرمز',
      backToSignIn: 'العودة لتسجيل الدخول',
      passwordResetSuccess: 'تم تحديث كلمة المرور بنجاح',
      // Processing
      callback: 'جاري تسجيل الدخول...',
      processing: 'جاري معالجة المصادقة...',
      // Errors
      invalidCredentials: 'البريد الإلكتروني أو كلمة المرور غير صحيحة',
      emailExists: 'البريد الإلكتروني مسجل بالفعل',
      weakPassword: 'يجب أن تتكون كلمة المرور من 8 أحرف على الأقل',
      passwordMismatch: 'كلمات المرور غير متطابقة',
      invalidEmail: 'يرجى إدخال بريد إلكتروني صالح',
      invalidOtp: 'رمز غير صالح أو منتهي الصلاحية',
      otpExpired: 'انتهت صلاحية الرمز. يرجى طلب رمز جديد.',
      emailNotFound: 'لم يتم العثور على حساب بهذا البريد الإلكتروني',
      tooManyAttempts: 'محاولات كثيرة جداً. يرجى المحاولة لاحقاً.',
      sessionExpired: 'انتهت صلاحية جلستك. يرجى تسجيل الدخول مرة أخرى.'
    },

    // Checkout
    checkout: {
      verifyingPayment: 'جاري التحقق من الدفع...',
      paymentSuccess: 'تم الدفع بنجاح!',
      completeRegistration: 'أكمل التسجيل للبدء',
      subscriptionReady: 'اشتراكك جاهز للتفعيل',
      activateAccount: 'تفعيل الحساب',
    },

    // Errors
    errors: {
      pageNotFound: 'الصفحة غير موجودة',
      somethingWentWrong: 'حدث خطأ ما'
    },

    // Optimization
    optimization: {
      title: 'تحسين التكاليف',
      description: 'اكتشف وطبق فرص توفير التكاليف عبر عمليات المشتريات الخاصة بك.',
      potentialSavings: 'التوفير المحتمل',
      implementedSavings: 'التوفير المحقق',
      totalOpportunities: 'إجمالي الفرص',
      implemented: 'مطبقة',
      runAnalysis: 'بدء التحليل',
      analyzing: 'جاري التحليل...',
      analysisComplete: 'اكتمل التحليل',
      analysisError: 'فشل التحليل',
      fetchError: 'فشل تحميل فرص التحسين',
      noOpportunities: 'لا توجد فرص',
      noOpportunitiesDesc: 'قم بتشغيل التحليل لاكتشاف فرص التوفير.',
      affectedVendors: 'الموردون المتأثرون',
      affectedContracts: 'العقود المتأثرة',
      perYear: '/ سنة',
      reduction: 'تخفيض',
      accept: 'قبول',
      reject: 'رفض',
      acceptSuccess: 'تم قبول الفرصة بنجاح',
      acceptError: 'فشل قبول الفرصة',
      rejectSuccess: 'تم رفض الفرصة',
      rejectError: 'فشل رفض الفرصة',
      summary: 'الملخص',
      recommendation: 'التوصية',
      currentState: 'الحالة الحالية',
      proposedState: 'الحالة المقترحة',
      totalSavings: 'إجمالي التوفير المقدر',
      monthlySpend: 'شهرياً',
      annualSpend: 'سنوياً',
      confidence: 'الثقة',
      type: {
        volume_discount: 'خصم الكمية',
        consolidation: 'توحيد الموردين',
        renegotiation: 'إعادة التفاوض',
        elimination: 'إلغاء الخدمة',
        timing: 'توقيت الدفع',
        alternative: 'مورد بديل'
      },
      status: {
        identified: 'محددة',
        evaluating: 'قيد التقييم',
        under_review: 'قيد المراجعة',
        approved: 'موافق عليها',
        in_progress: 'قيد التنفيذ',
        implemented: 'مطبقة',
        rejected: 'مرفوضة',
        deferred: 'مؤجلة'
      },
      priority: {
        critical: 'حرجة',
        high: 'عالية',
        medium: 'متوسطة',
        low: 'منخفضة'
      }
    },

    // Reports
    reports: {
      title: 'التقارير',
      subtitle: 'إنشاء وإدارة تقارير الامتثال',
      generate: 'إنشاء تقرير',
      generateNew: 'إنشاء تقرير جديد',
      availableTypes: 'أنواع التقارير المتاحة',
      generatedReports: 'التقارير المُنشأة',
      reportName: 'اسم التقرير',
      reportType: 'نوع التقرير',
      reportFormat: 'التنسيق',
      reportTitle: 'العنوان (اختياري)',
      titlePlaceholder: 'أدخل عنواناً مخصصاً للتقرير',
      description: 'الوصف',
      descriptionPlaceholder: 'أضف وصفاً لهذا التقرير',
      startDate: 'تاريخ البداية',
      endDate: 'تاريخ النهاية',
      format: 'التنسيق',
      status: 'الحالة',
      createdAt: 'تاريخ الإنشاء',
      size: 'الحجم',
      download: 'تحميل',
      noReports: 'لم يتم إنشاء تقارير بعد',
      noTypesAvailable: 'لا توجد أنواع تقارير متاحة',
      selectType: 'اختر نوع التقرير',
      selectTypeError: 'يرجى اختيار نوع التقرير',
      generating: 'جاري الإنشاء...',
      generateError: 'فشل إنشاء التقرير',
      downloadError: 'فشل تحميل التقرير',
      deleteError: 'فشل حذف التقرير',
      confirmDelete: 'هل أنت متأكد من حذف هذا التقرير؟'
    },

    // Audit
    audit: {
      title: 'سجل المراجعة',
      subtitle: 'تتبع جميع التغييرات والإجراءات في النظام',
      noEntries: 'لم يتم العثور على إدخالات مراجعة',
      filterByAction: 'تصفية حسب الإجراء',
      filterByResource: 'تصفية حسب نوع المورد',
      filterByActor: 'تصفية حسب المستخدم',
      filterByDate: 'تصفية حسب التاريخ',
      allActions: 'جميع الإجراءات',
      allResources: 'جميع الموارد',
      success: 'نجاح',
      failed: 'فشل',
      actor: 'المستخدم',
      action: 'الإجراء',
      resource: 'المورد',
      timestamp: 'الوقت',
      details: 'التفاصيل',
      viewChanges: 'عرض التغييرات',
      status: 'الحالة'
    },

    // Individual discrepancy translations
    discrepancy: {
      addComment: 'إضافة تعليق',
      aiAnalysis: 'تحليل الذكاء الاصطناعي',
      clause: 'بند',
      commentPlaceholder: 'أدخل تعليقك...',
      comparison: 'مقارنة',
      confidence: 'الثقة',
      contract: 'العقد',
      contractValue: 'قيمة العقد',
      detected: 'تم الكشف',
      detectedAt: 'تم الكشف في',
      difference: 'الفرق',
      dismiss: 'رفض',
      dismissed: 'مرفوض',
      escalate: 'تصعيد',
      id: 'المعرف',
      impact: 'التأثير',
      invoice: 'الفاتورة',
      invoiceValue: 'قيمة الفاتورة',
      lineItem: 'بند',
      linkedDocuments: 'المستندات المرتبطة',
      loadError: 'فشل في التحميل',
      markResolved: 'تحديد كمحلول',
      metadata: 'البيانات الوصفية',
      noDiscrepancies: 'لا توجد تناقضات',
      noDiscrepanciesDescription: 'لم يتم اكتشاف أي تناقضات بعد',
      notFound: 'التناقض غير موجود',
      overcharge: 'زيادة في السعر',
      overchargeDetected: 'تم اكتشاف زيادة في السعر',
      overchargeExplanation: 'مبلغ الفاتورة يتجاوز سعر العقد',
      pending: 'قيد الانتظار',
      reopen: 'إعادة فتح',
      resolutionHistory: 'سجل الحل',
      resolve: 'حل',
      resolved: 'تم الحل',
      resolvedAt: 'تم الحل في',
      severity: 'الخطورة',
      startReview: 'بدء المراجعة',
      status: 'الحالة',
      suggestedAction: 'الإجراء المقترح',
      totalImpact: 'إجمالي التأثير',
      type: {
        pricing: 'التسعير',
        quantity: 'الكمية',
        terms: 'الشروط',
        dates: 'التواريخ',
        missingItem: 'عنصر مفقود',
        unauthorized: 'غير مصرح',
        duplicate: 'مكرر',
        other: 'أخرى',
        // CamelCase variants (for UI components)
        missingDiscount: 'خصم مفقود',
        incorrectTax: 'ضريبة غير صحيحة',
        priceOvercharge: 'زيادة في السعر',
        quantityExcess: 'زيادة في الكمية',
        duplicateCharge: 'رسوم مكررة',
        unauthorizedItem: 'عنصر غير مصرح',
        contractExpired: 'عقد منتهي',
        // Snake case variants (from API)
        missing_required_field: 'حقل مطلوب مفقود',
        price_mismatch: 'عدم تطابق السعر',
        quantity_mismatch: 'عدم تطابق الكمية',
        date_mismatch: 'عدم تطابق التاريخ',
        unauthorized_item: 'عنصر غير مصرح',
        duplicate_charge: 'رسوم مكررة',
        missing_discount: 'خصم مفقود',
        incorrect_tax: 'ضريبة غير صحيحة',
        contract_expired: 'عقد منتهي',
        invalid_format: 'تنسيق غير صالح',
        missing_signature: 'توقيع مفقود',
        terms_violation: 'مخالفة الشروط',
        compliance_issue: 'مشكلة امتثال',
        overcharge: 'زيادة في السعر',
        undercharge: 'نقص في السعر',
        // Descriptions
        pricingDesc: 'السعر يختلف عن شروط العقد',
        quantityDesc: 'الكمية لا تتطابق مع الطلب',
        termsDesc: 'ينتهك شروط العقد',
        datesDesc: 'التاريخ خارج الفترة الصالحة',
        missingItemDesc: 'عنصر مفقود من الفاتورة',
        unauthorizedDesc: 'عنصر غير موجود في العقد',
        duplicateDesc: 'تم اكتشاف رسوم مكررة',
        otherDesc: 'نوع آخر من التناقض'
      },
      underReview: 'قيد المراجعة',
      undercharge: 'نقص في السعر',
      underchargeDetected: 'تم اكتشاف نقص في السعر',
      underchargeExplanation: 'مبلغ الفاتورة أقل من سعر العقد'
    },

    // Individual invoice translations
    invoice: {
      amount: 'المبلغ',
      analyzeAgainstContract: 'تحليل مقابل العقد',
      confirmUnlink: 'هل أنت متأكد من إلغاء ربط هذا العقد؟',
      contractLinked: 'تم ربط العقد بنجاح',
      contractUnlinked: 'تم إلغاء ربط العقد',
      date: 'التاريخ',
      description: 'الوصف',
      discrepancies: 'التناقضات',
      dueDate: 'تاريخ الاستحقاق',
      financialInfo: 'المعلومات المالية',
      invoiceInfo: 'معلومات الفاتورة',
      lineItems: 'بنود الفاتورة',
      linkAnother: 'ربط آخر',
      linkContract: 'ربط عقد',
      linkError: 'فشل في ربط العقد',
      linkedContracts: 'العقود المرتبطة',
      loadError: 'فشل في تحميل الفاتورة',
      noDiscrepancies: 'لا توجد تناقضات',
      noLineItems: 'لا توجد بنود',
      noLinkedContracts: 'لا توجد عقود مرتبطة',
      notFound: 'الفاتورة غير موجودة',
      number: 'رقم الفاتورة',
      poNumber: 'رقم أمر الشراء',
      quantity: 'الكمية',
      reanalyze: 'إعادة التحليل',
      subtotal: 'المجموع الفرعي',
      tax: 'الضريبة',
      total: 'الإجمالي',
      unitPrice: 'سعر الوحدة',
      unlinkError: 'فشل في إلغاء ربط العقد',
      untitled: 'فاتورة بدون عنوان',
      vendorAddress: 'عنوان المورد',
      vendorId: 'معرف المورد',
      vendorInfo: 'معلومات المورد',
      vendorName: 'اسم المورد',
      suggestedContracts: 'العقود المقترحة',
      suggestionsIntro: 'بناءً على المورد ومطابقة SKU وتواريخ العقد:',
      recommended: 'موصى به',
      linkAndAnalyze: 'ربط وتحليل',
      edit: 'تعديل الفاتورة',
      basicInfo: 'المعلومات الأساسية',
      dates: 'التواريخ',
      sku: 'رمز المنتج',
      currency: 'العملة',
      addLineItem: 'إضافة بند',
      saveSuccess: 'تم حفظ الفاتورة بنجاح',
      saveError: 'فشل في حفظ الفاتورة',
      recalculate: 'إعادة حساب من البنود',
      receivedDate: 'تاريخ الاستلام',
      status: 'الحالة',
    },

    // Status translations
    status: {
      active: 'نشط',
      analyzed: 'تم التحليل',
      approved: 'موافق عليه',
      disputed: 'متنازع عليه',
      draft: 'مسودة',
      expired: 'منتهي الصلاحية',
      extracted: 'مستخرج',
      paid: 'مدفوع',
      pending: 'قيد الانتظار',
      pendingApproval: 'في انتظار الموافقة',
      pendingReview: 'في انتظار المراجعة',
      rejected: 'مرفوض',
      renewed: 'مجدد',
      terminated: 'منتهي'
    },

    // Time translations
    time: {
      daysAgo: 'أيام',
      hoursAgo: 'ساعات',
      justNow: 'الآن',
      minutesAgo: 'دقائق'
    },

    // Copilot AI Assistant
    copilot: {
      title: 'Kashf AI',
      placeholder: 'اسأل عن فواتيرك وعقودك...',
      send: 'إرسال',
      newChat: 'محادثة جديدة',
      history: 'السجل',
      typing: 'جاري التفكير...',
      error: 'حدث خطأ. يرجى المحاولة مرة أخرى.',
      noConversations: 'لا توجد محادثات',
      deleteConfirm: 'حذف هذه المحادثة؟',
      backToChat: 'العودة إلى المحادثة',
      suggestions: {
        title: 'جرب أن تسأل:',
        invoice: 'ما هي الفواتير المتأخرة؟',
        contract: 'ما هي العقود التي تنتهي هذا الشهر؟',
        vendor: 'من هم أكبر الموردين من حيث الإنفاق؟',
        discrepancy: 'أظهر لي التناقضات غير المحلولة'
      }
    },

  }
};

// Language configuration
export const languages = [
  { code: 'en', name: 'English', flag: '🇬🇧', dir: 'ltr' },
  { code: 'fr', name: 'Français', flag: '🇫🇷', dir: 'ltr' },
  { code: 'ar', name: 'العربية', flag: '🇹🇳', dir: 'rtl' }
];

// Current language state - default to French
let currentLang = localStorage.getItem('invoaice-lang') || 'fr';

/**
 * Get current language
 */
export function getCurrentLang() {
  return currentLang;
}

/**
 * Set language
 */
export function setLang(lang) {
  if (translations[lang]) {
    currentLang = lang;
    localStorage.setItem('invoaice-lang', lang);
    
    // Update document direction for RTL support
    const langConfig = languages.find(l => l.code === lang);
    document.documentElement.dir = langConfig?.dir || 'ltr';
    document.documentElement.lang = lang;
    
    return true;
  }
  return false;
}

/**
 * Get translation by key path (e.g., 'nav.dashboard')
 * @param {string} keyPath - Dot-separated path to the translation key
 * @param {string} defaultValue - Optional fallback if key not found
 */
export function t(keyPath, defaultValue = null) {
  const keys = keyPath.split('.');
  let value = translations[currentLang];

  for (const key of keys) {
    if (value && typeof value === 'object' && key in value) {
      value = value[key];
    } else {
      // Fallback to English
      value = translations.en;
      for (const k of keys) {
        if (value && typeof value === 'object' && k in value) {
          value = value[k];
        } else {
          // Return default value if provided, otherwise return key
          return defaultValue !== null ? defaultValue : keyPath;
        }
      }
      break;
    }
  }

  return value;
}

/**
 * Initialize language from storage or browser
 */
export function initLang() {
  const stored = localStorage.getItem('invoaice-lang');
  if (stored && translations[stored]) {
    setLang(stored);
  } else {
    // Try to detect from browser
    const browserLang = navigator.language.split('-')[0];
    if (translations[browserLang]) {
      setLang(browserLang);
    } else {
      setLang('en');
    }
  }
}

/**
 * Alias functions for compatibility
 */
export const getCurrentLanguage = getCurrentLang;
export const setLanguage = setLang;

/**
 * Apply translations to elements with data-i18n attribute
 * Also sets document direction for RTL languages
 */
export function applyTranslations() {
  // Set document direction based on current language
  const langConfig = languages.find(l => l.code === currentLang);
  document.documentElement.dir = langConfig?.dir || 'ltr';
  document.documentElement.lang = currentLang;
  
  // Translate elements with data-i18n attribute
  document.querySelectorAll('[data-i18n]').forEach(el => {
    const key = el.dataset.i18n;
    el.textContent = t(key);
  });
}

/**
 * Currency configuration
 */
let currentCurrency = localStorage.getItem('invoaice-currency') || 'EUR';

/**
 * Fallback exchange rates from USD (used if API fails)
 * Last updated: 2025-01
 */
const FALLBACK_FX_RATES = {
  // Major World Currencies
  USD: 1.0,
  EUR: 0.92,
  GBP: 0.79,
  JPY: 149.5,
  CHF: 0.88,
  CAD: 1.36,
  AUD: 1.53,
  NZD: 1.64,
  // Asian Currencies
  CNY: 7.24,
  INR: 83.2,
  SGD: 1.34,
  HKD: 7.82,
  KRW: 1320.0,
  THB: 35.5,
  MYR: 4.47,
  IDR: 15700.0,
  PHP: 56.0,
  VND: 24500.0,
  TWD: 31.5,
  // Middle East & North Africa
  MAD: 10.0,
  SAR: 3.75,      // Saudi Riyal - pegged to USD
  AED: 3.67,      // UAE Dirham - pegged to USD
  TND: 3.1,
  EGP: 30.9,
  QAR: 3.64,
  KWD: 0.31,
  BHD: 0.38,
  OMR: 0.38,
  JOD: 0.71,
  ILS: 3.65,
  // European Currencies
  SEK: 10.5,
  NOK: 10.8,
  DKK: 6.87,
  PLN: 4.0,
  CZK: 23.0,
  HUF: 360.0,
  RON: 4.6,
  TRY: 32.0,
  RUB: 92.0,
  UAH: 37.0,
  // Americas
  MXN: 17.2,
  BRL: 4.97,
  ARS: 870.0,
  CLP: 950.0,
  COP: 4000.0,
  PEN: 3.7,
  // Africa
  ZAR: 18.5,
  NGN: 1550.0,
  KES: 155.0,
  GHS: 12.5,
  XOF: 605.0,
  XAF: 605.0,
};

/**
 * Live exchange rates from USD - fetched from ExchangeRate-API (open.er-api.com)
 * Free, no API key required, updates daily
 */
let FX_RATES_FROM_USD = { ...FALLBACK_FX_RATES };
let fxRatesLastUpdated = null;
const FX_CACHE_DURATION_MS = 60 * 60 * 1000; // 1 hour cache

/**
 * Fetch live exchange rates from ExchangeRate-API (free, no key required)
 * @returns {Promise<boolean>} True if rates were updated successfully
 */
export async function fetchLiveFXRates() {
  try {
    const response = await fetch('https://open.er-api.com/v6/latest/USD');
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    const data = await response.json();

    if (data.result === 'success' && data.rates) {
      // Update rates for supported currencies
      const supportedCurrencies = Object.keys(FALLBACK_FX_RATES);
      supportedCurrencies.forEach(currency => {
        if (data.rates[currency] !== undefined) {
          FX_RATES_FROM_USD[currency] = data.rates[currency];
        }
      });

      fxRatesLastUpdated = Date.now();

      // Cache in localStorage
      localStorage.setItem('invoaice-fx-rates', JSON.stringify({
        rates: FX_RATES_FROM_USD,
        timestamp: fxRatesLastUpdated,
      }));

      console.log('[FX] Live rates updated from ExchangeRate-API:', FX_RATES_FROM_USD);
      return true;
    }
    return false;
  } catch (error) {
    console.warn('[FX] Failed to fetch live rates, using cached/fallback:', error.message);
    return false;
  }
}

/**
 * Initialize FX rates - load from cache or fetch fresh
 */
export async function initFXRates() {
  // Try to load from localStorage cache first
  try {
    const cached = localStorage.getItem('invoaice-fx-rates');
    if (cached) {
      const { rates, timestamp } = JSON.parse(cached);
      const age = Date.now() - timestamp;

      if (age < FX_CACHE_DURATION_MS) {
        // Cache is fresh, use it
        FX_RATES_FROM_USD = { ...FALLBACK_FX_RATES, ...rates };
        fxRatesLastUpdated = timestamp;
        console.log('[FX] Using cached rates (age:', Math.round(age / 60000), 'min)');
        return;
      }
    }
  } catch (e) {
    // Ignore cache errors
  }

  // Fetch fresh rates
  await fetchLiveFXRates();
}

/**
 * Get FX rates last update info
 */
export function getFXRatesInfo() {
  return {
    rates: { ...FX_RATES_FROM_USD },
    lastUpdated: fxRatesLastUpdated ? new Date(fxRatesLastUpdated).toISOString() : null,
    source: 'ExchangeRate-API (open.er-api.com)',
  };
}

/**
 * Get current currency
 */
export function getCurrency() {
  return currentCurrency;
}

/**
 * Set currency
 */
export function setCurrency(currency) {
  const validCurrencies = Object.keys(FX_RATES_FROM_USD);
  if (validCurrencies.includes(currency)) {
    currentCurrency = currency;
    localStorage.setItem('invoaice-currency', currency);
    // Dispatch event for components to update
    window.dispatchEvent(new CustomEvent('currencyChanged', { detail: { currency } }));
  }
}

/**
 * Convert amount from USD to target currency
 * @param {number} amountUSD - Amount in USD
 * @param {string} targetCurrency - Target currency code (default: user's preferred currency)
 * @returns {number} Converted amount
 */
export function convertFromUSD(amountUSD, targetCurrency = null) {
  if (amountUSD === null || amountUSD === undefined || isNaN(amountUSD)) {
    return 0;
  }
  const currency = targetCurrency || getCurrency();
  const rate = FX_RATES_FROM_USD[currency] || 1.0;
  return amountUSD * rate;
}

/**
 * Convert amount from a currency to USD
 * @param {number} amount - Amount in source currency
 * @param {string} sourceCurrency - Source currency code
 * @returns {number} Amount in USD
 */
export function convertToUSD(amount, sourceCurrency) {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return 0;
  }
  const rate = FX_RATES_FROM_USD[sourceCurrency] || 1.0;
  return amount / rate;
}

/**
 * Get the FX rate from USD to a target currency
 * @param {string} targetCurrency - Target currency code
 * @returns {number} Exchange rate
 */
export function getFXRate(targetCurrency = null) {
  const currency = targetCurrency || getCurrency();
  return FX_RATES_FROM_USD[currency] || 1.0;
}

// Make currency functions available globally
if (typeof window !== 'undefined') {
  window.getCurrency = getCurrency;
  window.setCurrency = setCurrency;
  window.convertFromUSD = convertFromUSD;
  window.getFXRate = getFXRate;
  window.getFXRatesInfo = getFXRatesInfo;
  window.fetchLiveFXRates = fetchLiveFXRates;
  window.initFXRates = initFXRates;

  // Initialize FX rates on load (non-blocking)
  initFXRates().catch(err => console.warn('[FX] Init failed:', err));
}

/**
 * Format a USD amount in the user's preferred currency
 * This is the main function to use for displaying prices/costs from the backend (which are in USD)
 * @param {number} amountUSD - The amount in USD
 * @param {object} options - Options: { showOriginal: false, targetCurrency: null }
 * @returns {string} Formatted currency string in user's preferred currency
 */
export function formatUSDAmount(amountUSD, options = {}) {
  if (amountUSD === null || amountUSD === undefined || isNaN(amountUSD)) {
    return '-';
  }
  const { showOriginal = false, targetCurrency = null } = options;
  const currency = targetCurrency || getCurrency();

  // If user's currency is USD, no conversion needed
  if (currency === 'USD') {
    return formatCurrency(amountUSD, 'USD');
  }

  // Convert from USD to user's currency
  const convertedAmount = convertFromUSD(amountUSD, currency);
  const formatted = formatCurrency(convertedAmount, currency);

  // Optionally show original USD amount
  if (showOriginal && amountUSD > 0) {
    return `${formatted} (${formatCurrency(amountUSD, 'USD')})`;
  }

  return formatted;
}

// Map non-standard currency codes to ISO 4217 codes
const CURRENCY_CODE_MAP = {
  'DT': 'TND',   // Tunisian Dinar
  'DA': 'DZD',   // Algerian Dinar
  'DH': 'MAD',   // Moroccan Dirham
  'LE': 'EGP',   // Egyptian Pound
  'SR': 'SAR',   // Saudi Riyal
  'AED': 'AED', // UAE Dirham (already valid, but ensure consistency)
};

/**
 * Normalize currency code to ISO 4217 standard
 * @param {string} code - Currency code (may be non-standard)
 * @returns {string} ISO 4217 currency code
 */
export function normalizeCurrencyCode(code) {
  if (!code) return null;
  return CURRENCY_CODE_MAP[code.toUpperCase()] || code;
}

/**
 * Format currency amount (for amounts already in the specified currency)
 * @param {number} amount - The amount to format
 * @param {string} currency - Currency code (default: user's preferred currency)
 * @returns {string} Formatted currency string
 */
export function formatCurrency(amount, currency = null) {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return '-';
  }
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  const lang = getCurrentLang();
  const locale = lang === 'ar' ? 'ar-SA' : lang === 'fr' ? 'fr-FR' : 'en-US';
  // Use provided currency, or fall back to user's preferred currency
  // Normalize to ISO 4217 code
  const rawCode = currency || getCurrency();
  const currencyCode = normalizeCurrencyCode(rawCode);

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numAmount);
  } catch (e) {
    // Fallback for unknown currency codes
    return `${rawCode} ${numAmount.toFixed(2)}`;
  }
}

/**
 * Format date
 * @param {string|Date} date - The date to format
 * @param {string} format - Format style: 'short', 'medium', 'long' (default: 'medium')
 * @returns {string} Formatted date string
 */
export function formatDate(date, format = 'medium') {
  if (!date) return '-';

  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(dateObj.getTime())) return '-';

  const lang = getCurrentLang();
  const locale = lang === 'ar' ? 'ar-SA' : lang === 'fr' ? 'fr-FR' : 'en-US';

  const options = {
    short: { year: 'numeric', month: 'numeric', day: 'numeric' },
    medium: { year: 'numeric', month: 'short', day: 'numeric' },
    long: { year: 'numeric', month: 'long', day: 'numeric' },
  };

  try {
    return new Intl.DateTimeFormat(locale, options[format] || options.medium).format(dateObj);
  } catch (e) {
    return dateObj.toLocaleDateString();
  }
}
